import { useState, useRef, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from "react";
import { jsPDF } from "jspdf";
import { supabase } from "./supabaseClient";
import { useIsNarrow, useIsPortrait } from "./useResponsive";
import { NNHC_LOGO_BASE64 } from "./assets/nnhcLogo";
import { theme, stageChip } from "./styles/theme";
import PhotoUpload from "./components/PhotoUpload";
import Icd10Search from "./components/Icd10Search";
import DocumentUpload from "./components/DocumentUpload";
import {
  WoundCareForm,
  SeizureVNSForm,
  IVInfusionForm,
  VentTrachForm,
  SkillNursingAssessment,
  NurseShiftNoteForm,
  MicHint,
  SaveContinueButton,
} from "./ClinicalForms_Part2";
import {
  signAddendum,
  proposeAmendment,
  approveAmendment,
  rejectAmendment,
  promptPasswordReauth,
  userIsSupervisor,
} from "./lib/amendmentSigning";

// Pull the actual base64 PNG data URL out of a signature pad payload.
// SignaturePad emits { dataUrl, timestamp, label }, SigPad emits
// { url, ts, label } — both are wrapper objects. The clinical_docs
// signature columns are TEXT and need the raw data URL string.
// Already-string inputs and null/undefined pass through.
function extractSignatureDataUrl(sig) {
  if (!sig) return null;
  if (typeof sig === "string") return sig.startsWith("data:") ? sig : null;
  if (typeof sig === "object") return sig.dataUrl || sig.url || null;
  return null;
}

// ─── Document integrity hash (ESIGN Act / NC UETA compliant) ─────────────────
// Deterministic canonical JSON: recursively sorts object keys at every level.
// Previous bug: JSON.stringify(obj, Object.keys(obj).sort()) treats the array
// as a whitelist FILTER at every nesting level, silently dropping nested fields
// like form_data.vitals — which is why two different submissions produced
// identical SHA-256 hashes.
function canonicalize(value) {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(canonicalize);
  return Object.keys(value).sort().reduce((acc, k) => {
    acc[k] = canonicalize(value[k]);
    return acc;
  }, {});
}

async function computeDocHash(payload) {
  const canonical = JSON.stringify(canonicalize(payload));
  if (typeof window !== "undefined" && window.console) {
    console.log("HASH INPUT:", canonical.substring(0, 200), "length:", canonical.length);
  }
  const bytes = new TextEncoder().encode(canonical);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return "SHA256:" + Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, "0")).join("").toUpperCase();
}

// ─── NNHC Brand Constants — re-exported from theme.js for legacy code paths ──
// New code should import from ./styles/theme. This alias is kept so the
// thousands of existing inline-style references keep working after the
// Sprint 2.5 design-consistency pass.
const BRAND = {
  purple: theme.colors.purple,
  purpleLight: "#6B2FB5",
  purpleDark: theme.colors.purpleDark,
  gold: theme.colors.gold,
  goldLight: theme.colors.goldLight,
  goldPale: "#FDF3E0",
  white: theme.colors.white,
  offWhite: theme.colors.offWhite,
  gray50: theme.colors.gray[50],
  gray100: theme.colors.gray[100],
  gray200: "#E5E7EB",
  gray400: "#9CA3AF",
  gray600: theme.colors.gray[500],
  gray800: theme.colors.gray[900],
  success: theme.colors.success,
  warning: theme.colors.warning,
  error: theme.colors.error,
};

// ─── Shared utility components ────────────────────────────────────────────────

// Card layout for PHI-bearing tables on phones (< 768px): each table row
// becomes a card of stacked label:value pairs. Per the HIPAA rules we never
// hide columns or rely on horizontal scroll for PHI — every value shown in the
// wide table is shown here too.
const phiCardStyle = {
  border: `1px solid ${BRAND.gray200}`, borderRadius: 10, padding: "12px 14px",
  background: BRAND.white, display: "flex", flexDirection: "column", gap: 5,
};
function FieldPair({ label, value, mono }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "1px 0", alignItems: "baseline" }}>
      <span style={{ color: BRAND.gray400, fontWeight: 700, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em", flexShrink: 0 }}>{label}</span>
      <span style={{ color: BRAND.gray800, fontSize: 13, textAlign: "right", fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-word" }}>{value}</span>
    </div>
  );
}

// Unified modal shell — used by every Sprint 2.5 drawer/modal so they all
// close on ESC / click-outside / ✕ and share the Add New Patient design.
// Modal layout: full-screen sheet on phones (< 768px), centered card on
// tablet/desktop. Shared by ModalShell and the hand-rolled <form> modals.
function modalOverlayStyle(isNarrow, scrim = "rgba(15, 8, 30, 0.55)") {
  return {
    position: "fixed", inset: 0, background: scrim,
    display: "flex", alignItems: isNarrow ? "stretch" : "flex-start",
    justifyContent: "center", padding: isNarrow ? 0 : "40px 16px",
    zIndex: 1000, overflowY: "auto",
  };
}
function modalCardStyle(isNarrow, maxWidth) {
  return {
    background: BRAND.white, width: "100%", maxWidth: isNarrow ? "none" : maxWidth,
    borderRadius: isNarrow ? 0 : 16, padding: isNarrow ? 16 : 28,
    boxShadow: isNarrow ? "none" : "0 24px 80px rgba(0,0,0,0.35)",
    fontFamily: "DM Sans, sans-serif",
  };
}

function ModalShell({ title, onClose, children, maxWidth = 680 }) {
  const isNarrow = useIsNarrow();
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={modalOverlayStyle(isNarrow)}
    >
      <div className={"form-rgrid-scope" + (isNarrow ? " min-h-screen-dvh" : "")} style={modalCardStyle(isNarrow, maxWidth)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontFamily: "Comfortaa, sans-serif", color: BRAND.purple, fontWeight: 700 }}>
            {title}
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: BRAND.gray600,
            width: 44, height: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
          }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// PHI watermark overlay (Fix 10). Render at top-level of PHI-bearing screens
// as a sibling to main content. pointerEvents: none so it never swallows clicks.
function PHIWatermark({ user }) {
  if (!user) return null;
  const stamp = new Date().toLocaleString();
  return (
    <div aria-hidden="true" style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      pointerEvents: "none", zIndex: 1, opacity: 0.04,
      fontSize: 14, color: BRAND.purple,
      fontFamily: "JetBrains Mono, monospace",
      overflow: "hidden", userSelect: "none",
    }}>
      {Array.from({ length: 30 }).map((_, i) => (
        <div key={i} style={{
          transform: "rotate(-28deg)", whiteSpace: "nowrap", marginTop: 60,
        }}>
          {Array.from({ length: 8 }).map(() =>
            `${user.name || ""} · ${user.username || ""} · ${stamp} · NNHC CONFIDENTIAL     `
          ).join("")}
        </div>
      ))}
    </div>
  );
}

// ─── NPI Luhn check (Fix 5) ──────────────────────────────────────────────────
// NPPES uses the standard Luhn algorithm applied to the NPI prefixed with
// "80840" (the ISO issuer prefix for US healthcare providers) per CMS spec.
function isValidNPI(npi) {
  const s = (npi || "").replace(/\D/g, "");
  if (s.length !== 10) return false;
  const withPrefix = "80840" + s;
  let sum = 0;
  for (let i = 0; i < withPrefix.length; i++) {
    let d = parseInt(withPrefix[withPrefix.length - 1 - i], 10);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
}

// ─── Vitals range validation (Fix 4) ─────────────────────────────────────────
// Returns { ok, warn, msg } for a vital key and string value.
// warn=true signals an abnormal-but-possible value — never a hard block.
const VITAL_RANGES = {
  temp:      { hard: [90, 110],  warn: [96, 102],  unit: "°F",  label: "Temperature" },
  pulse:     { hard: [30, 250],  warn: [40, 120],  unit: "bpm", label: "Pulse" },
  resp:      { hard: [5, 60],    warn: [8, 30],    unit: "/min", label: "Respiration" },
  o2sat:     { hard: [50, 100],  warn: [85, 100],  unit: "%",   label: "O2 Sat" },
  bpSystolic:  { hard: [50, 250], warn: [80, 180], unit: "",    label: "BP systolic" },
  bpDiastolic: { hard: [30, 150], warn: [40, 120], unit: "",    label: "BP diastolic" },
};

function validateVital(key, raw) {
  if (raw === "" || raw == null) return { ok: true, warn: false };
  const n = Number(raw);
  if (!Number.isFinite(n)) return { ok: false, warn: true, msg: "Numeric value required." };
  const r = VITAL_RANGES[key];
  if (!r) return { ok: true, warn: false };
  if (n < r.hard[0] || n > r.hard[1]) {
    return { ok: false, warn: true, msg: `${r.label} ${n}${r.unit} is physiologically implausible (${r.hard[0]}–${r.hard[1]}${r.unit}).` };
  }
  if (n < r.warn[0] || n > r.warn[1]) {
    return { ok: true, warn: true, msg: "Value outside typical range. Click to confirm or re-enter." };
  }
  return { ok: true, warn: false };
}

// Small icon used inline on abnormal vitals — title attribute = tooltip.
function WarnIcon({ msg }) {
  return (
    <span
      title={msg}
      style={{
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        width: 18, height: 18, borderRadius: "50%", background: "#FEF3C7",
        color: "#B45309", fontSize: 12, fontWeight: 700, cursor: "help",
        marginLeft: 6, flexShrink: 0,
      }}
    >!</span>
  );
}

// ─── N/A Omission wrapper (Fix 6) ────────────────────────────────────────────
// Wrap a required field so clinicians can explicitly omit it with a reason.
// The parent form owns `omissions` (array) and passes down get/set helpers.
function NAOmit({ fieldKey, label, omissions, setOmissions, children }) {
  const existing = (omissions || []).find(o => o.field === fieldKey);
  const checked = !!existing;
  const setChecked = (val) => {
    if (val) {
      setOmissions([...(omissions || []).filter(o => o.field !== fieldKey), { field: fieldKey, label, reason: "" }]);
    } else {
      setOmissions((omissions || []).filter(o => o.field !== fieldKey));
    }
  };
  const setReason = (reason) => {
    setOmissions((omissions || []).map(o => o.field === fieldKey ? { ...o, reason } : o));
  };
  const reasonOk = !checked || (existing?.reason || "").trim().length >= 5;
  return (
    <div style={{ position: "relative" }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        <label style={{
          display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
          fontSize: 11, fontWeight: 600, color: BRAND.gray600,
          padding: "2px 8px", borderRadius: 12,
          background: checked ? "#FEF3C7" : "transparent",
          border: `1px solid ${checked ? BRAND.warning : "transparent"}`,
        }}>
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ accentColor: BRAND.warning }}
          />
          N/A
        </label>
      </div>
      <div style={{ opacity: checked ? 0.4 : 1, pointerEvents: checked ? "none" : "auto" }}>
        {children}
      </div>
      {checked && (
        <div style={{ marginTop: 6 }}>
          <textarea
            placeholder="Reason for omission (required, 5+ characters)"
            value={existing?.reason || ""}
            onChange={(e) => setReason(e.target.value)}
            style={{
              ...textInput,
              minHeight: 42,
              borderColor: reasonOk ? BRAND.warning : BRAND.error,
              background: "#FFFBEB",
              fontSize: 12,
            }}
          />
          {!reasonOk && (
            <div style={{ fontSize: 11, color: BRAND.error, marginTop: 2 }}>
              A reason of 5+ characters is required.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Draft autosave hook (Fix 8) ─────────────────────────────────────────────
// Persists form state to localStorage keyed by doc type + patient id. Loads
// existing draft on mount. Caller should call clearDraft() on successful save.
// When `skipLoad` is true (e.g. resuming a DB draft), the localStorage hydrate
// step is skipped so the in-memory DB draft state isn't clobbered.
function useDraftAutosave(docType, patientId, data, setData, skipLoad = false) {
  const keyRef = useRef(null);
  const loadedRef = useRef(false);
  const key = docType && patientId ? `nnhc_draft_${docType}_${patientId}` : null;
  keyRef.current = key;

  // Load once per key
  useEffect(() => {
    if (!key) return;
    loadedRef.current = false;
    if (!skipLoad) {
      try {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object") setData(parsed);
        }
      } catch { /* ignore malformed drafts */ }
    }
    loadedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persist on change
  useEffect(() => {
    if (!key || !loadedRef.current) return;
    try { localStorage.setItem(key, JSON.stringify(data)); } catch { /* quota */ }
  }, [key, data]);

  const clearDraft = useCallback(() => {
    if (keyRef.current) localStorage.removeItem(keyRef.current);
  }, []);

  return { clearDraft };
}

// ─── Signature Pad Component ─────────────────────────────────────────────────
// forwardRef + useImperativeHandle exposes:
//   - getCurrentDataUrl(): returns the canvas as a base64 PNG if the user has
//     drawn on it, regardless of whether they clicked "Accept Signature".
//     Used by submit handlers as a defensive fallback so a drawn-but-unaccepted
//     signature still persists to clinical_docs (FIX 1: MD signature save bug).
//   - hasStroke(): true if any stroke is currently on the canvas. Used by the
//     Signatures-step Save Draft handler to block saves once any pad has been
//     touched (FIX 2).
const SignaturePad = forwardRef(function SignaturePad({ label, onSigned, signed }, ref) {
  const isNarrow = useIsNarrow();
  const isPortrait = useIsPortrait();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const lastPos = useRef(null);

  useImperativeHandle(ref, () => ({
    getCurrentDataUrl: () => {
      if (signed) return signed.dataUrl || signed.url || null;
      if (hasSignature && canvasRef.current) {
        return canvasRef.current.toDataURL("image/png");
      }
      return null;
    },
    hasStroke: () => !!signed || hasSignature,
  }), [signed, hasSignature]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: ((clientX - rect.left) * canvas.width) / rect.width,
      y: ((clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getPos(e, canvas);
    setIsDrawing(true);
    lastPos.current = pos;
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = BRAND.purpleDark;
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    lastPos.current = pos;
    setHasSignature(true);
  };

  const stopDraw = () => setIsDrawing(false);

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSigned(null);
  };

  const accept = () => {
    const canvas = canvasRef.current;
    const dataUrl = canvas.toDataURL("image/png");
    onSigned({ dataUrl, timestamp: new Date().toISOString(), label });
  };

  return (
    <div style={{
      border: `2px solid ${signed ? BRAND.success : BRAND.gray200}`,
      borderRadius: 12,
      padding: 16,
      background: signed ? "#F0FDF4" : BRAND.white,
      transition: "all 0.2s"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: BRAND.gray600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          {label}
        </span>
        {signed && (
          <span style={{ fontSize: 12, color: BRAND.success, fontWeight: 600 }}>
            ✓ SIGNED {new Date(signed.timestamp).toLocaleTimeString()}
          </span>
        )}
      </div>
      {!signed ? (
        <>
          {isNarrow && isPortrait && (
            <div style={{
              background: "#FEF3C7", border: `1.5px solid ${BRAND.gold}`, borderRadius: 8,
              padding: "10px 12px", fontSize: 13, color: BRAND.gray800, marginBottom: 10, lineHeight: 1.5,
            }}>
              📱 Please rotate your device to landscape to sign more comfortably.
            </div>
          )}
          <canvas
            ref={canvasRef}
            width={520}
            height={100}
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={stopDraw}
            onMouseLeave={stopDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={stopDraw}
            style={{
              width: "100%",
              height: 100,
              background: "#FAFAFA",
              border: `1.5px dashed ${BRAND.gray200}`,
              borderRadius: 8,
              cursor: "crosshair",
              display: "block",
              touchAction: "none"
            }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button onClick={clear} style={btnSecondary}>Clear</button>
            <button onClick={accept} disabled={!hasSignature} style={{
              ...btnPrimary,
              opacity: hasSignature ? 1 : 0.45,
              cursor: hasSignature ? "pointer" : "not-allowed"
            }}>
              Accept Signature
            </button>
          </div>
        </>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <img src={signed.dataUrl} alt="signature" style={{ height: 60, maxWidth: 280, background: "#FAFAFA", border: `1px solid ${BRAND.gray200}`, borderRadius: 6 }} />
          <button onClick={() => onSigned(null)} style={btnSecondary}>Re-sign</button>
        </div>
      )}
    </div>
  );
});

// ─── Button Styles ────────────────────────────────────────────────────────────
const btnPrimary = {
  background: `linear-gradient(135deg, ${BRAND.purple}, ${BRAND.purpleLight})`,
  color: BRAND.white,
  border: "none",
  borderRadius: 8,
  padding: "12px 22px",
  minHeight: 44,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "Comfortaa, sans-serif",
  letterSpacing: "0.02em",
  boxShadow: `0 2px 8px ${BRAND.purple}30`,
  transition: "all 0.15s",
};

const btnSecondary = {
  background: BRAND.white,
  color: BRAND.purple,
  border: `1.5px solid ${BRAND.purple}`,
  borderRadius: 8,
  padding: "11px 20px",
  minHeight: 44,
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "Comfortaa, sans-serif",
  transition: "all 0.15s",
};

const btnGold = {
  background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`,
  color: BRAND.white,
  border: "none",
  borderRadius: 8,
  padding: "12px 22px",
  minHeight: 44,
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "Comfortaa, sans-serif",
  boxShadow: `0 2px 8px ${BRAND.gold}40`,
};

// ─── Form Field Components ────────────────────────────────────────────────────
const FieldRow = ({ label, children, required, mic }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      fontSize: 12,
      fontWeight: 700,
      color: BRAND.gray600,
      marginBottom: 6,
      textTransform: "uppercase",
      letterSpacing: "0.06em"
    }}>
      <span>{label}{required && <span style={{ color: BRAND.error, marginLeft: 3 }}>*</span>}</span>
      {mic && <MicHint targetId={mic} />}
    </label>
    {children}
  </div>
);

const textInput = {
  width: "100%",
  padding: "11px 14px",
  minHeight: 44,
  border: `1.5px solid ${BRAND.gray200}`,
  borderRadius: 8,
  fontSize: 16,
  color: BRAND.gray800,
  fontFamily: "DM Sans, sans-serif",
  background: BRAND.white,
  boxSizing: "border-box",
  transition: "border-color 0.15s",
  outline: "none",
};

const CheckBox = ({ label, checked, onChange }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: 8 }}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      style={{ width: 18, height: 18, accentColor: BRAND.purple, cursor: "pointer" }} />
    <span style={{ fontSize: 14, color: BRAND.gray800 }}>{label}</span>
  </label>
);

const YesNoRow = ({ label, value, onChange, showComment, comment, onComment }) => (
  <div style={{
    display: "grid",
    gridTemplateColumns: "2fr 80px 80px 2fr",
    gap: 12,
    alignItems: "center",
    padding: "10px 14px",
    borderBottom: `1px solid ${BRAND.gray100}`,
    background: BRAND.white
  }}>
    <span style={{ fontSize: 13, color: BRAND.gray800, lineHeight: 1.4 }}>{label}</span>
    {["YES", "NO"].map(opt => (
      <label key={opt} style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        padding: "4px 10px",
        borderRadius: 6,
        background: value === opt ? (opt === "YES" ? "#F0FDF4" : "#FFF1F0") : BRAND.gray50,
        border: `1.5px solid ${value === opt ? (opt === "YES" ? BRAND.success : BRAND.error) : BRAND.gray200}`,
        justifyContent: "center",
        transition: "all 0.15s"
      }}>
        <input type="radio" name={label} value={opt} checked={value === opt}
          onChange={() => onChange(opt)}
          style={{ accentColor: opt === "YES" ? BRAND.success : BRAND.error }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: opt === "YES" ? BRAND.success : BRAND.error }}>{opt}</span>
      </label>
    ))}
    <input
      type="text"
      placeholder={showComment || "Comments"}
      value={comment || ""}
      onChange={e => onComment && onComment(e.target.value)}
      style={{ ...textInput, padding: "7px 10px", fontSize: 12 }}
    />
  </div>
);

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ title, icon, mic }) => (
  <div style={{
    background: `linear-gradient(90deg, ${BRAND.purple}15, transparent)`,
    borderLeft: `4px solid ${BRAND.purple}`,
    padding: "10px 16px",
    borderRadius: "0 8px 8px 0",
    marginBottom: 16,
    marginTop: 24,
    display: "flex",
    alignItems: "center",
    gap: 10
  }}>
    {icon && <span style={{ fontSize: 18 }}>{icon}</span>}
    <span style={{ fontWeight: 700, color: BRAND.purple, fontSize: 14, letterSpacing: "0.05em", textTransform: "uppercase", flex: 1 }}>
      {title}
    </span>
    {mic && <MicHint targetId={mic} />}
  </div>
);

// ─── FORM: RN Supervisory Visit ───────────────────────────────────────────────
function RNSupervisoryForm({ patient, onComplete, onBack, onSaveDraft, initialDraft, docType = "RN_SUPERVISORY", showToast }) {
  const [data, setData] = useState(() => ({
    visitDate: new Date().toISOString().split("T")[0],
    nextVisitDate: "",
    purposeOfVisit: "ROUTINE",
    // Sprint 2.5: BP is now stored as separate systolic/diastolic numeric
    // fields per position so the PDF can render a true vitals table and
    // range validation can flag abnormal values.
    vitals: {
      temp: "", pulse: "", resp: "", o2sat: "",
      bp_lying_systolic: "", bp_lying_diastolic: "",
      bp_sitting_systolic: "", bp_sitting_diastolic: "",
      bp_standing_systolic: "", bp_standing_diastolic: "",
    },
    _omissions: [],
    respiratoryStatus: { trach: false, vent: false, cpap: false, alarmSet: false, na: false },
    oxygenStatus: "N/A",
    communicationNeeds: { interpreterNeeded: false, languageBarrier: false, devices: false, other: false },
    infectiousControl: "STANDARD_PRECAUTIONS",
    checklist: {},
    checklistComments: {},
    aidePresent: "NO",
    aideName: "",
    aideBadge: null,
    aideSkillsMatch: null,
    aidePerformingTasks: null,
    aideServiceLog: null,
    aidePPE: null,
    aideInteraction: null,
    clientSatisfied: null,
    aideSafetyCompliance: null,
    employerConcerns: "",
    trainingProvided: "",
    narrative: "",
    ...(initialDraft?.form_data || {}),
  }));

  const CHECKLIST = [
    ["medChange", "Changes to medical condition?", 'If "Yes", comments:'],
    ["pocReviewed", "POC Reviewed?", 'If "NO", comments:'],
    ["personalCareMet", "Personal Care needs being met?", 'If "NO", comments:'],
    ["pocRevision", "POC Revision required?", 'If "Yes", comments:'],
    ["hospitalization", "Client hospitalization / MD Visits?", 'If "Yes", comments:'],
    ["newServices", "Other new services / medication change?", 'If "Yes", comments:'],
    ["clientSatisfied", "Client satisfied with services?", 'If "NO", comments:'],
    ["continueService", "Client continues to require PCS?", 'If "NO", comments:'],
    ["caregiver", "Able/willing available caregiver?", 'If "Yes", comments:'],
    ["covidExposure", "Exposed to COVID-19 or traveled outside US in last 21 days?", 'If "Yes", comments:'],
  ];

  const [rnSig, setRnSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  const [step, setStep] = useState(() => parseInt(String(initialDraft?.form_data?.lastStep ?? 1), 10) || 1);
  const draftIdRef = useRef(initialDraft?.id || null);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { dataUrl, timestamp: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    const anyStroke = rnSigPadRef.current?.hasStroke?.() || clientSigPadRef.current?.hasStroke?.();
    if (anyStroke) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type: "RN_SUPERVISORY", data, lastStep: step, draftIdRef });
  };

  // Draft autosave (Fix 8) — persist form state per (docType, patient id).
  // When resuming a DB draft, skip the localStorage load so we don't clobber it.
  const { clearDraft } = useDraftAutosave(docType, patient?.id, data, setData, !!initialDraft);

  const setOmissions = (next) =>
    setData(d => ({ ...d, _omissions: typeof next === "function" ? next(d._omissions || []) : next }));

  const u = (path, val) => setData(d => {
    const copy = { ...d };
    const parts = path.split(".");
    let ref = copy;
    for (let i = 0; i < parts.length - 1; i++) ref = ref[parts[i]];
    ref[parts[parts.length - 1]] = val;
    return copy;
  });

  const steps = ["Clinical Assessment", "Aide Evaluation", "Signatures", "Review & Submit"];

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif" }}>
      {onSaveDraft && (
        <SaveContinueButton onSave={step === 3 ? handleSaveDraftClick : () => onSaveDraft({ type: "RN_SUPERVISORY", data, lastStep: step, draftIdRef })} />
      )}
      {/* Step Nav */}
      <div style={{ display: "flex", gap: 4, marginBottom: 28 }}>
        {steps.map((s, i) => (
          <div key={i} onClick={() => i < step && setStep(i + 1)} style={{
            flex: 1, padding: "8px 4px", textAlign: "center", fontSize: 11,
            fontWeight: 700, borderRadius: 8, cursor: i < step ? "pointer" : "default",
            background: step === i + 1 ? BRAND.purple : step > i + 1 ? BRAND.success : BRAND.gray100,
            color: step === i + 1 ? BRAND.white : step > i + 1 ? BRAND.white : BRAND.gray400,
            transition: "all 0.2s", textTransform: "uppercase", letterSpacing: "0.05em"
          }}>
            {step > i + 1 ? "✓ " : `${i + 1}. `}{s}
          </div>
        ))}
      </div>

      {/* ── Step 1: Clinical Assessment ── */}
      {step === 1 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 24 }}>
            <FieldRow label="Visit Date" required>
              <NAOmit fieldKey="visitDate" label="Visit Date" omissions={data._omissions} setOmissions={setOmissions}>
                <input type="date" value={data.visitDate} onChange={e => u("visitDate", e.target.value)} style={textInput} />
              </NAOmit>
            </FieldRow>
            <FieldRow label="Next Visit Date">
              <input type="date" value={data.nextVisitDate} onChange={e => u("nextVisitDate", e.target.value)} style={textInput} />
            </FieldRow>
            <FieldRow label="Purpose of Visit" required>
              <NAOmit fieldKey="purposeOfVisit" label="Purpose of Visit" omissions={data._omissions} setOmissions={setOmissions}>
                <select value={data.purposeOfVisit} onChange={e => u("purposeOfVisit", e.target.value)} style={textInput}>
                  <option value="ROUTINE">Routine</option>
                  <option value="DISCHARGE">Discharge</option>
                  <option value="RESUME_SERVICE">Resume Service</option>
                  <option value="OTHER">Other</option>
                </select>
              </NAOmit>
            </FieldRow>
          </div>

          <SectionHeader title="Vital Signs" icon="🩺" />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
            {[
              ["temp", "Temperature (°F)", "temp"],
              ["pulse", "Pulse (bpm)", "pulse"],
              ["resp", "Respirations", "resp"],
              ["o2sat", "O2 Saturation (%)", "o2sat"],
            ].map(([key, lbl, rangeKey]) => {
              const check = validateVital(rangeKey, data.vitals[key]);
              return (
                <FieldRow key={key} label={lbl}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <input
                      type="number"
                      step="0.1"
                      inputMode="decimal"
                      value={data.vitals[key]}
                      onChange={e => u(`vitals.${key}`, e.target.value)}
                      placeholder="—"
                      style={{
                        ...textInput,
                        borderColor: check.warn ? BRAND.warning : BRAND.gray200,
                        background: check.warn ? "#FFFBEB" : BRAND.white,
                      }}
                    />
                    {check.warn && <WarnIcon msg={check.msg} />}
                  </div>
                </FieldRow>
              );
            })}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
            {[
              ["lying", "B/P Lying"],
              ["sitting", "B/P Sitting"],
              ["standing", "B/P Standing"],
            ].map(([pos, l]) => {
              const sysKey = `bp_${pos}_systolic`;
              const diaKey = `bp_${pos}_diastolic`;
              const sCheck = validateVital("bpSystolic", data.vitals[sysKey]);
              const dCheck = validateVital("bpDiastolic", data.vitals[diaKey]);
              return (
                <FieldRow key={pos} label={l}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input
                      type="number"
                      inputMode="numeric"
                      placeholder="Systolic"
                      value={data.vitals[sysKey]}
                      onChange={e => u(`vitals.${sysKey}`, e.target.value)}
                      style={{
                        ...textInput, flex: 1,
                        borderColor: sCheck.warn ? BRAND.warning : BRAND.gray200,
                        background: sCheck.warn ? "#FFFBEB" : BRAND.white,
                      }}
                    />
                    <span style={{ fontWeight: 700, color: BRAND.gray400 }}>/</span>
                    <input
                      type="number"
                      inputMode="numeric"
                      placeholder="Diastolic"
                      value={data.vitals[diaKey]}
                      onChange={e => u(`vitals.${diaKey}`, e.target.value)}
                      style={{
                        ...textInput, flex: 1,
                        borderColor: dCheck.warn ? BRAND.warning : BRAND.gray200,
                        background: dCheck.warn ? "#FFFBEB" : BRAND.white,
                      }}
                    />
                    {(sCheck.warn || dCheck.warn) && (
                      <WarnIcon msg={sCheck.warn ? sCheck.msg : dCheck.msg} />
                    )}
                  </div>
                </FieldRow>
              );
            })}
          </div>

          <SectionHeader title="Respiratory Status" icon="🫁" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, padding: "12px 0", marginBottom: 8 }}>
            {[["trach", "Trach"], ["vent", "Vent"], ["cpap", "CPAP"], ["alarmSet", "Alarm Set"], ["na", "N/A"]].map(([k, l]) => (
              <CheckBox key={k} label={l} checked={data.respiratoryStatus[k]}
                onChange={v => u(`respiratoryStatus.${k}`, v)} />
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
            <div>
              <SectionHeader title="Oxygen" icon="💨" />
              {["CONTINUOUS", "PRN", "N/A"].map(o => (
                <label key={o} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="radio" name="oxygen" value={o} checked={data.oxygenStatus === o} onChange={() => u("oxygenStatus", o)}
                    style={{ accentColor: BRAND.purple }} />
                  <span style={{ fontSize: 14 }}>{o}</span>
                </label>
              ))}
            </div>
            <div>
              <SectionHeader title="Communication Needs" icon="💬" />
              {[["interpreterNeeded", "Interpreter/ADA Needed"], ["languageBarrier", "Language Barrier"], ["devices", "Devices"], ["other", "Other"]].map(([k, l]) => (
                <CheckBox key={k} label={l} checked={data.communicationNeeds[k]} onChange={v => u(`communicationNeeds.${k}`, v)} />
              ))}
            </div>
            <div>
              <SectionHeader title="Infectious Control" icon="🦠" />
              {[["STANDARD_PRECAUTIONS", "Standard Precautions"], ["CONTACT", "Contact"], ["DROPLET", "Droplet"], ["AIRBORNE", "Airborne"], ["OTHER", "Other"]].map(([v, l]) => (
                <label key={v} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, cursor: "pointer" }}>
                  <input type="radio" name="infControl" value={v} checked={data.infectiousControl === v} onChange={() => u("infectiousControl", v)}
                    style={{ accentColor: BRAND.purple }} />
                  <span style={{ fontSize: 14 }}>{l}</span>
                </label>
              ))}
            </div>
          </div>

          <SectionHeader title="Clinical Checklist" icon="✅" />
          <div style={{
            border: `1.5px solid ${BRAND.gray200}`,
            borderRadius: 10,
            overflow: "hidden",
            marginBottom: 20
          }}>
            <div style={{
              display: "grid", gridTemplateColumns: "2fr 80px 80px 2fr",
              background: BRAND.purple, color: BRAND.white, padding: "8px 14px",
              fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em"
            }}>
              <span>Question</span><span style={{ textAlign: "center" }}>YES</span>
              <span style={{ textAlign: "center" }}>NO</span><span>Comments</span>
            </div>
            {CHECKLIST.map(([key, lbl, hint]) => (
              <YesNoRow key={key} label={lbl}
                value={data.checklist[key] || ""}
                onChange={v => setData(d => ({ ...d, checklist: { ...d.checklist, [key]: v } }))}
                showComment={hint}
                comment={data.checklistComments[key] || ""}
                onComment={v => setData(d => ({ ...d, checklistComments: { ...d.checklistComments, [key]: v } }))}
              />
            ))}
          </div>

          <SectionHeader title="Clinical Narrative / Notes" icon="📝" mic="rnsv-narrative" />
          <textarea
            id="rnsv-narrative"
            value={data.narrative}
            onChange={e => u("narrative", e.target.value)}
            placeholder="Document clinical findings, patient/caregiver education, changes in condition, and nursing interventions..."
            style={{ ...textInput, minHeight: 100, resize: "vertical" }}
          />

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}>
            <button style={btnPrimary} onClick={() => setStep(2)}>Continue to Aide Evaluation →</button>
          </div>
        </div>
      )}

      {/* ── Step 2: Aide Evaluation ── */}
      {step === 2 && (
        <div>
          <SectionHeader title="Caregiver / Aide Evaluation" icon="👤" />

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
            <FieldRow label="Aide Present?">
              <div style={{ display: "flex", gap: 12 }}>
                {["YES", "NO"].map(v => (
                  <label key={v} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                    <input type="radio" name="aidePresent" value={v} checked={data.aidePresent === v}
                      onChange={() => u("aidePresent", v)} style={{ accentColor: BRAND.purple }} />
                    <span style={{ fontSize: 14 }}>{v}</span>
                  </label>
                ))}
              </div>
            </FieldRow>
            <FieldRow label="If Yes — Aide Name">
              <input type="text" value={data.aideName} onChange={e => u("aideName", e.target.value)}
                disabled={data.aidePresent !== "YES"} style={{ ...textInput, opacity: data.aidePresent !== "YES" ? 0.5 : 1 }} />
            </FieldRow>
          </div>

          {data.aidePresent === "YES" && (
            <div style={{ border: `1.5px solid ${BRAND.gray200}`, borderRadius: 10, overflow: "hidden", marginBottom: 20 }}>
              <div style={{
                display: "grid", gridTemplateColumns: "2fr 80px 80px 2fr",
                background: BRAND.purpleLight, color: BRAND.white, padding: "8px 14px",
                fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em"
              }}>
                <span>Evaluation Criteria</span><span style={{ textAlign: "center" }}>YES</span>
                <span style={{ textAlign: "center" }}>NO</span><span>Comments</span>
              </div>
              {[
                ["aideBadge", "Aide wearing name badge?"],
                ["aideSkillsMatch", "Aide skills match POC?"],
                ["aidePerformingTasks", "Aide performing tasks / hours per POC?"],
                ["aideServiceLog", "Aide service log accurate?"],
                ["aidePPE", "Aide using PPE?"],
                ["aideSafetyCompliance", "Demonstrates standard safety precautions?"],
              ].map(([key, lbl]) => (
                <YesNoRow key={key} label={lbl}
                  value={data[key] || ""}
                  onChange={v => u(key, v)}
                  comment={data.checklistComments[key] || ""}
                  onComment={v => setData(d => ({ ...d, checklistComments: { ...d.checklistComments, [key]: v } }))}
                />
              ))}
            </div>
          )}

          {data.aidePresent === "YES" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <FieldRow label="Aide–Client Interaction">
                <select value={data.aideInteraction || ""} onChange={e => u("aideInteraction", e.target.value)} style={textInput}>
                  <option value="">Select...</option>
                  <option value="SATISFACTORY">Satisfactory</option>
                  <option value="UNSATISFACTORY">Unsatisfactory</option>
                </select>
              </FieldRow>
              <FieldRow label="Client / Family Satisfied with Aide?">
                <select value={data.clientSatisfied || ""} onChange={e => u("clientSatisfied", e.target.value)} style={textInput}>
                  <option value="">Select...</option>
                  <option value="YES">Yes</option>
                  <option value="NO">No</option>
                </select>
              </FieldRow>
            </div>
          )}

          <SectionHeader title="Employer Concerns / Training Provided" icon="🏢" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <FieldRow label="Employer Concerns">
              <textarea value={data.employerConcerns} onChange={e => u("employerConcerns", e.target.value)}
                style={{ ...textInput, minHeight: 80, resize: "vertical" }} placeholder="Document any concerns..." />
            </FieldRow>
            <FieldRow label="Training Provided (if applicable)">
              <textarea value={data.trainingProvided} onChange={e => u("trainingProvided", e.target.value)}
                style={{ ...textInput, minHeight: 80, resize: "vertical" }} placeholder="Describe training provided..." />
            </FieldRow>
          </div>

          <PhotoUpload
            docId={null}
            formType="RN_SUPERVISORY"
            onUploaded={(paths) =>
              setData(prev => ({ ...prev, photoPaths: paths }))}
          />

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
            <button style={btnSecondary} onClick={() => setStep(1)}>← Back</button>
            <button style={btnPrimary} onClick={() => setStep(3)}>Continue to Signatures →</button>
          </div>
        </div>
      )}

      {/* ── Step 3: Signatures ── */}
      {step === 3 && (
        <div>
          <SectionHeader title="Electronic Signatures" icon="✍️" />
          <div style={{
            background: BRAND.goldPale,
            border: `1.5px solid ${BRAND.gold}`,
            borderRadius: 10,
            padding: 16,
            marginBottom: 24,
            fontSize: 13,
            color: BRAND.gray800,
            lineHeight: 1.6
          }}>
            <strong>Legal Notice:</strong> By signing below, you confirm that this supervisory visit was completed as documented.
            Signatures are timestamp-sealed and SHA-256 hashed per ESIGN Act (15 U.S.C. §7001) and NC UETA (G.S. 66-317).
            This document is tamper-evident and constitutes a legal clinical record.
          </div>

          <div style={{ marginBottom: 20 }}>
            <p style={{ fontSize: 13, color: BRAND.gray600, marginBottom: 4, fontWeight: 600 }}>
              I acknowledge and confirm the NNHC RN performed this supervisory note and evaluation as part of my care.
              This process is done every 90 days. I recognize and agree that I have the right to refuse treatment
              or terminate services at any time by notifying Nurturing Nurses HomeCare, Inc.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <SignaturePad
              ref={rnSigPadRef}
              label="RN Supervisor / Director of Nursing Signature"
              onSigned={setRnSig}
              signed={rnSig}
            />
            <SignaturePad
              ref={clientSigPadRef}
              label="Client / Client Representative Signature (optional)"
              onSigned={setClientSig}
              signed={clientSig}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 24 }}>
            <button style={btnSecondary} onClick={() => setStep(2)}>← Back</button>
            <button style={btnPrimary} onClick={() => setStep(4)}
              disabled={!rnSig}
              className={!rnSig ? "disabled" : ""}
            >
              Review & Submit →
            </button>
          </div>
        </div>
      )}

      {/* ── Step 4: Review & Submit ── */}
      {step === 4 && (
        <div>
          <SectionHeader title="Document Review" icon="📋" />

          <div style={{
            background: BRAND.offWhite,
            border: `1.5px solid ${BRAND.gray200}`,
            borderRadius: 12,
            padding: 20,
            marginBottom: 24
          }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div><span style={{ fontSize: 11, fontWeight: 700, color: BRAND.gray400, textTransform: "uppercase" }}>Patient</span>
                <p style={{ margin: "4px 0", fontWeight: 600, color: BRAND.gray800 }}>{patient.name}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 700, color: BRAND.gray400, textTransform: "uppercase" }}>DOB</span>
                <p style={{ margin: "4px 0", fontWeight: 600, color: BRAND.gray800 }}>{patient.dob}</p></div>
              <div><span style={{ fontSize: 11, fontWeight: 700, color: BRAND.gray400, textTransform: "uppercase" }}>Visit Date</span>
                <p style={{ margin: "4px 0", fontWeight: 600, color: BRAND.gray800 }}>{data.visitDate}</p></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, padding: "12px 0",
              borderTop: `1px solid ${BRAND.gray200}`, borderBottom: `1px solid ${BRAND.gray200}`, marginBottom: 12 }}>
              {(() => {
                const bp = (p) => {
                  const s = data.vitals[`bp_${p}_systolic`];
                  const d = data.vitals[`bp_${p}_diastolic`];
                  return s || d ? `${s || "—"}/${d || "—"}` : "";
                };
                return [
                  ["Temp", data.vitals.temp], ["Pulse", data.vitals.pulse],
                  ["Resp", data.vitals.resp], ["O2 Sat", data.vitals.o2sat],
                  ["B/P Lying", bp("lying")], ["B/P Sitting", bp("sitting")],
                  ["B/P Standing", bp("standing")], ["Purpose", data.purposeOfVisit],
                ];
              })().map(([k, v]) => (
                <div key={k}><span style={{ fontSize: 11, color: BRAND.gray400, fontWeight: 600, textTransform: "uppercase" }}>{k}</span>
                  <p style={{ margin: "2px 0", fontSize: 13, fontWeight: 600, color: v ? BRAND.gray800 : BRAND.gray400 }}>{v || "—"}</p>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: BRAND.gray600 }}>
              <strong>Checklist Summary:</strong>{" "}
              {Object.entries(data.checklist).map(([k, v]) => `${k}: ${v}`).join(" | ") || "No responses recorded"}
            </div>
          </div>

          <div style={{
            background: "#F0FDF4",
            border: `1.5px solid ${BRAND.success}`,
            borderRadius: 10,
            padding: 16,
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 16
          }}>
            <span style={{ fontSize: 32 }}>✅</span>
            <div>
              <p style={{ margin: 0, fontWeight: 700, color: BRAND.success }}>Both signatures captured</p>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: BRAND.gray600 }}>
                RN signed at {rnSig && new Date(rnSig.timestamp).toLocaleString()} •
                Client signed at {clientSig && new Date(clientSig.timestamp).toLocaleString()}
              </p>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 8 }}>
            <button style={btnSecondary} onClick={() => setStep(3)}>← Back</button>
            <div style={{ display: "flex", gap: 12 }}>
              <button style={btnSecondary} onClick={() => window.print()}>🖨 Print</button>
              <button style={btnGold} onClick={() => onComplete({
                type: "RN_SUPERVISORY", data, patient, clearDraft, draftIdRef,
                rnSig: sigFromPad(rnSig, rnSigPadRef, "RN Supervisor / Director of Nursing Signature"),
                clientSig: sigFromPad(clientSig, clientSigPadRef, "Client / Client Representative Signature"),
              })}>
                ✓ Submit & Generate PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FORM: Plan of Care ───────────────────────────────────────────────────────
// PlanOfCareForm step map (1-indexed):
//   1 — Patient & Cert          (cert period, allergies, DNR, ICD-10 diagnoses)
//   2 — Clinical Status         (DME, safety, services, function, mental status)
//   3 — Orders & Goals          (orders, goals, physician info)
//   4 — Supporting Documents    (DocumentUpload — physician orders, AVS, labs)
//   5 — Signatures              (RN required; client + MD optional; submit)
function PlanOfCareForm({ patient, onComplete, onBack, onSaveDraft, initialDraft, docType = "PLAN_OF_CARE", showToast }) {
  const [data, setData] = useState(() => ({
    certPeriodFrom: "",
    certPeriodTo: "",
    levelOfCare: "Skilled",
    visitType: "Initial",
    allergies: "",
    dnr: "No",
    diagnoses: [{ code: "", description: "" }],
    nutritionalReq: "",
    dme: "",
    safetyMeasures: "",
    servicesAuth: "",
    hoursWeekly: "",
    daysPerWeek: "",
    schedule: "",
    functionalLimitations: {},
    activitiesPermitted: {},
    mentalStatus: {},
    prognosis: "",
    orders: "",
    goals: "",
    physicianName: "",
    physicianNPI: "",
    _omissions: [],
    ...(initialDraft?.form_data || {}),
  }));

  const [rnSig, setRnSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const [mdSig, setMdSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  const mdSigPadRef = useRef(null);
  const [step, setStep] = useState(() => parseInt(String(initialDraft?.form_data?.lastStep ?? 1), 10) || 1);
  const draftIdRef = useRef(initialDraft?.id || null);

  // Wrap the SignaturePad onSigned setter into a payload that handleComplete
  // expects ({ dataUrl, timestamp, label }) — used when falling back to
  // ref.current.getCurrentDataUrl() at submit time.
  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { dataUrl, timestamp: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    const anyStroke =
      rnSigPadRef.current?.hasStroke?.() ||
      clientSigPadRef.current?.hasStroke?.() ||
      mdSigPadRef.current?.hasStroke?.();
    if (anyStroke) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type: "PLAN_OF_CARE", data, lastStep: step, draftIdRef });
  };

  const { clearDraft } = useDraftAutosave(docType, patient?.id, data, setData, !!initialDraft);
  const setOmissions = (next) =>
    setData(d => ({ ...d, _omissions: typeof next === "function" ? next(d._omissions || []) : next }));

  const u = (k, v) => setData(d => ({ ...d, [k]: v }));

  const FUNC_LIMITS = ["Amputation", "Bowel/Bladder (Incontinence)", "Contracture", "Hearing",
    "Paralysis", "Endurance", "Ambulation", "Speech", "Legally Blind", "Dyspnea w/ Min Exertion"];
  const ACTIVITIES = ["Complete Bedrest", "Bedrest BRP", "Up As Tolerated", "Transfer Bed/Chair",
    "Exercises Prescribed", "Partial Weight Bearing", "Independent At Home", "Crutches", "Cane", "Walker", "Wheelchair", "No Restrictions"];
  const MENTAL = ["Oriented", "Comatose", "Forgetful", "Depressed", "Disoriented", "Lethargic", "Agitated", "Other"];

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif" }}>
      {onSaveDraft && (
        <SaveContinueButton onSave={step === 5 ? handleSaveDraftClick : () => onSaveDraft({ type: "PLAN_OF_CARE", data, lastStep: step, draftIdRef })} />
      )}
      {/* Step nav */}
      <div style={{ display: "flex", gap: 4, marginBottom: 28 }}>
        {["Patient & Cert", "Clinical Status", "Orders & Goals", "Supporting Documents", "Signatures"].map((s, i) => (
          <div key={i} style={{
            flex: 1, padding: "8px 4px", textAlign: "center", fontSize: 11, fontWeight: 700,
            borderRadius: 8, background: step === i + 1 ? BRAND.purple : step > i + 1 ? BRAND.success : BRAND.gray100,
            color: step === i + 1 || step > i + 1 ? BRAND.white : BRAND.gray400,
            textTransform: "uppercase", letterSpacing: "0.05em"
          }}>
            {step > i + 1 ? "✓ " : `${i + 1}. `}{s}
          </div>
        ))}
      </div>

      {step === 1 && (
        <div>
          <SectionHeader title="Certification Period & Patient Info" icon="📅" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
            <FieldRow label="Cert Period From" required>
              <NAOmit fieldKey="certPeriodFrom" label="Cert Period From" omissions={data._omissions} setOmissions={setOmissions}>
                <input type="date" value={data.certPeriodFrom} onChange={e => u("certPeriodFrom", e.target.value)} style={textInput} />
              </NAOmit>
            </FieldRow>
            <FieldRow label="Cert Period To" required>
              <NAOmit fieldKey="certPeriodTo" label="Cert Period To" omissions={data._omissions} setOmissions={setOmissions}>
                <input type="date" value={data.certPeriodTo} onChange={e => u("certPeriodTo", e.target.value)} style={textInput} />
              </NAOmit>
            </FieldRow>
            <FieldRow label="Level of Care" required>
              <NAOmit fieldKey="levelOfCare" label="Level of Care" omissions={data._omissions} setOmissions={setOmissions}>
                <select value={data.levelOfCare} onChange={e => u("levelOfCare", e.target.value)} style={textInput}>
                  {["Extensive", "Moderate", "Skilled"].map(v => <option key={v}>{v}</option>)}
                </select>
              </NAOmit>
            </FieldRow>
            <FieldRow label="Visit Type" required>
              <NAOmit fieldKey="visitType" label="Visit Type" omissions={data._omissions} setOmissions={setOmissions}>
                <select value={data.visitType} onChange={e => u("visitType", e.target.value)} style={textInput}>
                  {["Initial", "Reassessment", "Annual Evaluation"].map(v => <option key={v}>{v}</option>)}
                </select>
              </NAOmit>
            </FieldRow>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
            <FieldRow label="Allergies">
              <input type="text" value={data.allergies} onChange={e => u("allergies", e.target.value)} style={textInput} placeholder="NKDA or list allergies" />
            </FieldRow>
            <FieldRow label="DNR Status" required>
              <NAOmit fieldKey="dnr" label="DNR Status" omissions={data._omissions} setOmissions={setOmissions}>
                <select value={data.dnr} onChange={e => u("dnr", e.target.value)} style={textInput}>
                  <option>No — Full Code</option>
                  <option>Yes — DNR</option>
                </select>
              </NAOmit>
            </FieldRow>
            <FieldRow label="Nutritional Requirements">
              <input type="text" value={data.nutritionalReq} onChange={e => u("nutritionalReq", e.target.value)} style={textInput} placeholder="e.g. Diabetic diet" />
            </FieldRow>
          </div>

          <SectionHeader title="ICD-10 Diagnosis Codes" icon="🏥" />
          <div style={{ marginBottom: 20 }}>
            <Icd10Search
              value={(data.diagnoses || []).filter(dx => dx && dx.code)}
              onChange={(v) => u("diagnoses", v)}
              placeholder="Search ICD-10 code or description..."
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
            <button style={btnPrimary} onClick={() => setStep(step + 1)}>Continue →</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>
            <FieldRow label="DME & Supplies">
              <textarea value={data.dme} onChange={e => u("dme", e.target.value)}
                style={{ ...textInput, minHeight: 80 }} placeholder="e.g. Wheelchair, Hospital Bed..." />
            </FieldRow>
            <FieldRow label="Safety Measures">
              <textarea value={data.safetyMeasures} onChange={e => u("safetyMeasures", e.target.value)}
                style={{ ...textInput, minHeight: 80 }} placeholder="e.g. Fall precautions, rails..." />
            </FieldRow>
            <FieldRow label="Services Authorized">
              <textarea value={data.servicesAuth} onChange={e => u("servicesAuth", e.target.value)}
                style={{ ...textInput, minHeight: 80 }} placeholder="e.g. Personal Care Services, HHA..." />
            </FieldRow>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr", gap: 16, marginBottom: 20 }}>
            <FieldRow label="Hours Authorized Weekly">
              <input type="text" value={data.hoursWeekly} onChange={e => u("hoursWeekly", e.target.value)} style={textInput} />
            </FieldRow>
            <FieldRow label="Days Per Week">
              <input type="text" value={data.daysPerWeek} onChange={e => u("daysPerWeek", e.target.value)} style={textInput} />
            </FieldRow>
            <FieldRow label="Current Schedule">
              <input type="text" value={data.schedule} onChange={e => u("schedule", e.target.value)} style={textInput} placeholder="e.g. Mon-Fri 2p-5pm, Sat 2p-4p" />
            </FieldRow>
          </div>

          <SectionHeader title="Functional Limitations" icon="⚕️" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {FUNC_LIMITS.map(f => (
              <label key={f} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                borderRadius: 20, cursor: "pointer",
                background: data.functionalLimitations[f] ? BRAND.purple : BRAND.gray100,
                color: data.functionalLimitations[f] ? BRAND.white : BRAND.gray600,
                fontSize: 13, fontWeight: 500, transition: "all 0.15s",
                border: `1.5px solid ${data.functionalLimitations[f] ? BRAND.purple : BRAND.gray200}`
              }}>
                <input type="checkbox" checked={!!data.functionalLimitations[f]}
                  onChange={e => u("functionalLimitations", { ...data.functionalLimitations, [f]: e.target.checked })}
                  style={{ display: "none" }} />
                {data.functionalLimitations[f] ? "✓ " : ""}{f}
              </label>
            ))}
          </div>

          <SectionHeader title="Activities Permitted" icon="🏃" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {ACTIVITIES.map(a => (
              <label key={a} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                borderRadius: 20, cursor: "pointer",
                background: data.activitiesPermitted[a] ? BRAND.success : BRAND.gray100,
                color: data.activitiesPermitted[a] ? BRAND.white : BRAND.gray600,
                fontSize: 13, fontWeight: 500, transition: "all 0.15s",
                border: `1.5px solid ${data.activitiesPermitted[a] ? BRAND.success : BRAND.gray200}`
              }}>
                <input type="checkbox" checked={!!data.activitiesPermitted[a]}
                  onChange={e => u("activitiesPermitted", { ...data.activitiesPermitted, [a]: e.target.checked })}
                  style={{ display: "none" }} />
                {data.activitiesPermitted[a] ? "✓ " : ""}{a}
              </label>
            ))}
          </div>

          <SectionHeader title="Mental Status" icon="🧠" />
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {MENTAL.map(m => (
              <label key={m} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20, cursor: "pointer",
                background: data.mentalStatus[m] ? BRAND.gold : BRAND.gray100,
                color: data.mentalStatus[m] ? BRAND.white : BRAND.gray600,
                fontSize: 13, fontWeight: 500, transition: "all 0.15s",
                border: `1.5px solid ${data.mentalStatus[m] ? BRAND.gold : BRAND.gray200}`
              }}>
                <input type="checkbox" checked={!!data.mentalStatus[m]}
                  onChange={e => u("mentalStatus", { ...data.mentalStatus, [m]: e.target.checked })}
                  style={{ display: "none" }} />
                {data.mentalStatus[m] ? "✓ " : ""}{m}
              </label>
            ))}
          </div>

          <SectionHeader title="Prognosis" icon="📊" />
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            {["Poor", "Guarded", "Fair", "Good", "Excellent"].map(p => (
              <label key={p} style={{
                flex: 1, textAlign: "center", padding: "10px", borderRadius: 8, cursor: "pointer",
                background: data.prognosis === p ? BRAND.purple : BRAND.gray100,
                color: data.prognosis === p ? BRAND.white : BRAND.gray600,
                fontWeight: 600, fontSize: 13, border: `1.5px solid ${data.prognosis === p ? BRAND.purple : BRAND.gray200}`,
                transition: "all 0.15s"
              }}>
                <input type="radio" name="prognosis" value={p} checked={data.prognosis === p}
                  onChange={() => u("prognosis", p)} style={{ display: "none" }} />
                {p}
              </label>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button style={btnSecondary} onClick={() => setStep(step - 1)}>← Back</button>
            <button style={btnPrimary} onClick={() => setStep(step + 1)}>Continue →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <SectionHeader title="Orders & Treatment Plan" icon="📋" />
          <FieldRow label="Orders for Discipline and Treatments (Amount / Frequency / Duration)" required mic="poc-orders">
            <NAOmit fieldKey="orders" label="Orders for Discipline and Treatments" omissions={data._omissions} setOmissions={setOmissions}>
              <textarea id="poc-orders" value={data.orders} onChange={e => u("orders", e.target.value)}
                style={{ ...textInput, minHeight: 120, resize: "vertical" }}
                placeholder="List all ordered disciplines, treatments, frequency, and duration..." />
            </NAOmit>
          </FieldRow>

          <SectionHeader title="Goals / Rehabilitation Potential / Discharge Plans" icon="🎯" />
          <FieldRow label="Goals & Discharge Plan" required mic="poc-goals">
            <NAOmit fieldKey="goals" label="Goals & Discharge Plan" omissions={data._omissions} setOmissions={setOmissions}>
              <textarea id="poc-goals" value={data.goals} onChange={e => u("goals", e.target.value)}
                style={{ ...textInput, minHeight: 100, resize: "vertical" }}
                placeholder="Document short/long-term goals, rehabilitation potential, and anticipated discharge plan..." />
            </NAOmit>
          </FieldRow>

          <SectionHeader title="Physician Information" icon="👨‍⚕️" />
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
            <FieldRow label="Ordering / Certifying Physician Name">
              <input type="text" value={data.physicianName} onChange={e => u("physicianName", e.target.value)} style={textInput} />
            </FieldRow>
            <FieldRow label="Physician NPI">
              {(() => {
                const raw = data.physicianNPI || "";
                const digits = raw.replace(/\D/g, "").slice(0, 10);
                const complete = digits.length === 10;
                const valid = complete && isValidNPI(digits);
                const showError = complete && !valid;
                return (
                  <>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={digits}
                      onChange={e => u("physicianNPI", e.target.value.replace(/\D/g, "").slice(0, 10))}
                      placeholder="1234567890"
                      style={{
                        ...textInput,
                        borderColor: showError ? BRAND.error : (valid ? BRAND.success : BRAND.gray200),
                        background: showError ? "#FEF2F2" : BRAND.white,
                      }}
                    />
                    {showError && (
                      <div style={{ fontSize: 11, color: BRAND.error, marginTop: 4 }}>
                        NPI must be 10 digits with a valid Luhn check. Verify at{" "}
                        <a href="https://npiregistry.cms.hhs.gov/" target="_blank" rel="noopener noreferrer"
                           style={{ color: BRAND.error }}>npiregistry.cms.hhs.gov</a>.
                      </div>
                    )}
                    {valid && (
                      <div style={{ fontSize: 11, color: BRAND.success, marginTop: 4 }}>
                        ✓ NPI format valid
                      </div>
                    )}
                  </>
                );
              })()}
            </FieldRow>
          </div>

          <div style={{
            background: BRAND.goldPale, border: `1.5px solid ${BRAND.gold}`, borderRadius: 10,
            padding: 14, marginTop: 20, marginBottom: 20, fontSize: 12, color: BRAND.gray800
          }}>
            <strong>Physician Certification Statement:</strong> "I certify/recertify that this patient is confined to his/her home
            and needs intermittent skilled nursing care, physical therapy and/or speech therapy or continues to need occupational therapy.
            The patient is under my care, and I have authorized services on this plan of care and will periodically review the plan."
          </div>

          <PhotoUpload
            docId={null}
            formType="PLAN_OF_CARE"
            onUploaded={(paths) =>
              setData(prev => ({ ...prev, photoPaths: paths }))}
          />

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button style={btnSecondary} onClick={() => setStep(step - 1)}>← Back</button>
            <button style={btnPrimary} onClick={() => setStep(step + 1)}>Continue to Documents →</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div>
          <SectionHeader title="Supporting Documents" icon="📎" />
          <div style={{
            background: BRAND.goldPale, border: `1.5px solid ${BRAND.gold}`,
            borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 12
          }}>
            Attach the certifying physician's signed orders, hospital discharge
            summaries, current medication lists, lab/imaging reports, voice
            recordings, or any other supporting documents. Each file requires
            a category.
          </div>
          <DocumentUpload
            docId={null}
            formType="PLAN_OF_CARE"
            onUploaded={(docs) =>
              setData(prev => ({ ...prev, attachedDocuments: docs }))}
          />
          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 24 }}>
            <button style={btnSecondary} onClick={() => setStep(step - 1)}>← Back</button>
            <button style={btnPrimary} onClick={() => setStep(step + 1)}>Continue to Signatures →</button>
          </div>
        </div>
      )}

      {step === 5 && (
        <div>
          <SectionHeader title="Required Signatures" icon="✍️" />
          <div style={{
            background: BRAND.goldPale, border: `1.5px solid ${BRAND.gold}`, borderRadius: 10, padding: 14, marginBottom: 24, fontSize: 12
          }}>
            <strong>⚠️ NC BON &amp; ACHC Requirement:</strong> Plan of Care requires three signatures before implementation:
            (1) Clinical Supervisor/RN, (2) Client/Representative, and (3) Ordering Physician.
            Physician signature must be obtained <em>prior to care delivery</em> per 42 CFR §484.60.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <SignaturePad ref={rnSigPadRef} label="Clinical Supervisor / RN Signature" onSigned={setRnSig} signed={rnSig} />
            <SignaturePad ref={clientSigPadRef} label="Client / Client's Representative Signature (optional)" onSigned={setClientSig} signed={clientSig} />
            <SignaturePad ref={mdSigPadRef} label="Ordering Physician Signature" onSigned={setMdSig} signed={mdSig} />
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 24 }}>
            <button style={btnSecondary} onClick={() => setStep(step - 1)}>← Back</button>
            <button style={btnGold} onClick={() => onComplete({
              type: "PLAN_OF_CARE", data, patient, clearDraft, draftIdRef,
              rnSig: sigFromPad(rnSig, rnSigPadRef, "Clinical Supervisor / RN Signature"),
              clientSig: sigFromPad(clientSig, clientSigPadRef, "Client / Client's Representative Signature"),
              mdSig: sigFromPad(mdSig, mdSigPadRef, "Ordering Physician Signature"),
            })}
              disabled={!rnSig && !rnSigPadRef.current?.hasStroke?.()}>
              ✓ Submit Plan of Care
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FORM: Tube Feeding ───────────────────────────────────────────────────────
function TubeFeedingForm({ patient, onComplete, onBack, onSaveDraft, initialDraft, docType = "TUBE_FEEDING", showToast }) {
  const [data, setData] = useState(() => ({
    tubeType: "G-Tube", tubeFr: "", placement: "", lastChanged: "",
    formulaName: "", calDensity: "", dailyVolume: "", medNecessity: "",
    feedMethod: "Pump-Controlled", pumpRate: "", pumpHours: "", pumpType: "",
    bolusVol: "", bolusFreq: "",
    flushRoutine: "", flushBefore: false, flushAfter: false, flushBeforeMeds: false, flushAfterMeds: false,
    flushBeforeMedsAmt: "", flushBetweenMeds: "", flushAfterMedsAmt: "", doNotCrush: "",
    residualFreq: "", holdResidual: "",
    holdVomit: false, holdDistention: false, holdAspiration: false, holdDiarrhea: false, holdResidualParam: "",
    holdDiarrheaCount: "", notifyProvider: "",
    hobElevation: "30-45", maintainMinutes: "",
    siteCare: "", rotateTube: "Yes",
    tubeDislogeProtocol: "Replace immediately",
    pumpManuf: "", pumpModel: "", primaryRate: "", maxRate: "", titration: "",
    alarmOcclusion: false, alarmAir: false, alarmBattery: false,
    failGravity: false, failBolus: false, failHold: false,
    stomFreq: "", granulation: "", bumperInstructions: "",
    aspRisk: false, aspNotify: "",
    pediatricWeight: "", pediatricCal: "", growthMonitoring: "",
    _omissions: [],
    ...(initialDraft?.form_data || {}),
  }));

  const setOmissions = (next) =>
    setData(d => ({ ...d, _omissions: typeof next === "function" ? next(d._omissions || []) : next }));

  const [rnSig, setRnSig] = useState(null);
  const [mdSig, setMdSig] = useState(null);
  const [clientSig, setClientSig] = useState(null);
  const rnSigPadRef = useRef(null);
  const mdSigPadRef = useRef(null);
  const clientSigPadRef = useRef(null);
  // TubeFeeding uses string section keys ("main"|"pump"|"peg"|"sig").
  const [section, setSection] = useState(() => {
    const saved = initialDraft?.form_data?.lastStep;
    return typeof saved === "string" && ["main","pump","peg","sig"].includes(saved) ? saved : "main";
  });
  const draftIdRef = useRef(initialDraft?.id || null);
  const { clearDraft } = useDraftAutosave(docType, patient?.id, data, setData, !!initialDraft);

  const sigFromPad = (state, padRef, label) => {
    if (state) return state;
    const dataUrl = padRef.current?.getCurrentDataUrl?.();
    return dataUrl ? { dataUrl, timestamp: new Date().toISOString(), label } : null;
  };

  const handleSaveDraftClick = () => {
    const anyStroke =
      rnSigPadRef.current?.hasStroke?.() ||
      mdSigPadRef.current?.hasStroke?.() ||
      clientSigPadRef.current?.hasStroke?.();
    if (anyStroke) {
      const msg = "Cannot save as draft once any signature is captured. Use 'Clear' on each signature pad to remove signatures, then Save Draft. Or click Submit to finalize.";
      if (showToast) showToast(msg, "error");
      else window.alert(msg);
      return;
    }
    onSaveDraft({ type: "TUBE_FEEDING", data, lastStep: section, draftIdRef });
  };
  const u = (k, v) => setData(d => ({ ...d, [k]: v }));

  const SECTIONS = [
    { key: "main", label: "Tube Feeding Orders", icon: "🍶" },
    { key: "pump", label: "Pump Addendum", icon: "⚙️" },
    { key: "peg", label: "PEG / Site Care", icon: "🔬" },
    { key: "sig", label: "Signatures", icon: "✍️" },
  ];

  return (
    <div style={{ fontFamily: "DM Sans, sans-serif" }}>
      {onSaveDraft && (
        <SaveContinueButton onSave={section === "sig" ? handleSaveDraftClick : () => onSaveDraft({ type: "TUBE_FEEDING", data, lastStep: section, draftIdRef })} />
      )}
      {/* Tab nav */}
      <div style={{ display: "flex", gap: 6, marginBottom: 24, borderBottom: `2px solid ${BRAND.gray200}`, paddingBottom: 0 }}>
        {SECTIONS.map(s => (
          <button key={s.key} onClick={() => setSection(s.key)} style={{
            padding: "10px 16px", fontSize: 13, fontWeight: 600, fontFamily: "Comfortaa, sans-serif",
            border: "none", borderBottom: section === s.key ? `3px solid ${BRAND.purple}` : "3px solid transparent",
            background: "none", color: section === s.key ? BRAND.purple : BRAND.gray400,
            cursor: "pointer", marginBottom: -2, transition: "all 0.15s"
          }}>
            {s.icon} {s.label}
          </button>
        ))}
      </div>

      {section === "main" && (
        <div>
          <SectionHeader title="Section I — Tube Feeding Order Clarification" icon="🍶" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <FieldRow label="Feeding Tube Type" required>
              <NAOmit fieldKey="tubeType" label="Feeding Tube Type" omissions={data._omissions} setOmissions={setOmissions}>
                <select value={data.tubeType} onChange={e => u("tubeType", e.target.value)} style={textInput}>
                  {["PEG", "G-Tube", "J-Tube", "NG", "Other"].map(v => <option key={v}>{v}</option>)}
                </select>
              </NAOmit>
            </FieldRow>
            <FieldRow label="Tube Size (Fr)">
              <input type="text" value={data.tubeFr} onChange={e => u("tubeFr", e.target.value)} style={textInput} />
            </FieldRow>
            <FieldRow label="Date of Placement">
              <input type="date" value={data.placement} onChange={e => u("placement", e.target.value)} style={textInput} />
            </FieldRow>
          </div>

          <SectionHeader title="Enteral Formula" icon="💊" />
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <FieldRow label="Formula Name" required>
              <NAOmit fieldKey="formulaName" label="Formula Name" omissions={data._omissions} setOmissions={setOmissions}>
                <input type="text" value={data.formulaName} onChange={e => u("formulaName", e.target.value)} style={textInput} />
              </NAOmit>
            </FieldRow>
            <FieldRow label="Caloric Density">
              <input type="text" value={data.calDensity} onChange={e => u("calDensity", e.target.value)} style={textInput} placeholder="kcal/mL" />
            </FieldRow>
            <FieldRow label="Total Daily Volume (mL/day)">
              <input type="text" value={data.dailyVolume} onChange={e => u("dailyVolume", e.target.value)} style={textInput} />
            </FieldRow>
          </div>
          <FieldRow label="Medical Necessity Rationale" mic="enteral-medNecessity">
            <textarea id="enteral-medNecessity" value={data.medNecessity} onChange={e => u("medNecessity", e.target.value)}
              style={{ ...textInput, minHeight: 60 }} />
          </FieldRow>

          <SectionHeader title="Feeding Method" icon="🔄" />
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
            {["Continuous", "Pump-Controlled", "Gravity", "Bolus"].map(m => (
              <label key={m} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "8px 16px", borderRadius: 8, cursor: "pointer",
                background: data.feedMethod === m ? BRAND.purple : BRAND.gray100,
                color: data.feedMethod === m ? BRAND.white : BRAND.gray600,
                border: `1.5px solid ${data.feedMethod === m ? BRAND.purple : BRAND.gray200}`,
                fontWeight: 600, fontSize: 13, transition: "all 0.15s"
              }}>
                <input type="radio" name="feedMethod" checked={data.feedMethod === m} onChange={() => u("feedMethod", m)} style={{ display: "none" }} />
                {m}
              </label>
            ))}
          </div>

          {["Continuous", "Pump-Controlled"].includes(data.feedMethod) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
              <FieldRow label="Rate (mL/hr)"><input type="text" value={data.pumpRate} onChange={e => u("pumpRate", e.target.value)} style={textInput} /></FieldRow>
              <FieldRow label="Hours/Day"><input type="text" value={data.pumpHours} onChange={e => u("pumpHours", e.target.value)} style={textInput} /></FieldRow>
              <FieldRow label="Pump Type"><input type="text" value={data.pumpType} onChange={e => u("pumpType", e.target.value)} style={textInput} /></FieldRow>
            </div>
          )}
          {data.feedMethod === "Bolus" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <FieldRow label="Volume per Feeding (mL)"><input type="text" value={data.bolusVol} onChange={e => u("bolusVol", e.target.value)} style={textInput} /></FieldRow>
              <FieldRow label="Feedings per Day"><input type="text" value={data.bolusFreq} onChange={e => u("bolusFreq", e.target.value)} style={textInput} /></FieldRow>
            </div>
          )}

          <SectionHeader title="Water Flush Orders" icon="💧" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <FieldRow label="Routine Flush (mL)"><input type="text" value={data.flushRoutine} onChange={e => u("flushRoutine", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Before Meds (mL)"><input type="text" value={data.flushBeforeMedsAmt} onChange={e => u("flushBeforeMedsAmt", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Between Meds (mL)"><input type="text" value={data.flushBetweenMeds} onChange={e => u("flushBetweenMeds", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="After Meds (mL)"><input type="text" value={data.flushAfterMedsAmt} onChange={e => u("flushAfterMedsAmt", e.target.value)} style={textInput} /></FieldRow>
          </div>
          <FieldRow label="Do NOT Crush (list medications)">
            <input type="text" value={data.doNotCrush} onChange={e => u("doNotCrush", e.target.value)} style={textInput} placeholder="List medications that must not be crushed" />
          </FieldRow>

          <SectionHeader title="Hold Parameters & Emergency Protocol" icon="⚠️" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, color: BRAND.gray600, marginBottom: 8 }}>Hold feeding for:</p>
              {[["holdVomit", "Vomiting"], ["holdDistention", "Distension"], ["holdAspiration", "Aspiration signs"], ["holdDiarrhea", "Diarrhea"]].map(([k, l]) => (
                <CheckBox key={k} label={l} checked={data[k]} onChange={v => u(k, v)} />
              ))}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                <FieldRow label="Residual > (mL)"><input type="text" value={data.holdResidualParam} onChange={e => u("holdResidualParam", e.target.value)} style={textInput} /></FieldRow>
                <FieldRow label="Diarrhea > (episodes)"><input type="text" value={data.holdDiarrheaCount} onChange={e => u("holdDiarrheaCount", e.target.value)} style={textInput} /></FieldRow>
              </div>
            </div>
            <div>
              <FieldRow label="Notify Provider If">
                <textarea value={data.notifyProvider} onChange={e => u("notifyProvider", e.target.value)} style={{ ...textInput, minHeight: 80 }} />
              </FieldRow>
              <FieldRow label="If Tube Dislodged">
                <select value={data.tubeDislogeProtocol} onChange={e => u("tubeDislogeProtocol", e.target.value)} style={textInput}>
                  <option>Replace immediately</option>
                  <option>Send to ER</option>
                  <option>Other (specify in notes)</option>
                </select>
              </FieldRow>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button style={btnPrimary} onClick={() => setSection("pump")}>Continue to Pump Addendum →</button>
          </div>
        </div>
      )}

      {section === "pump" && (
        <div>
          <SectionHeader title="Section II — Enteral Pump Addendum" icon="⚙️" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <FieldRow label="Pump Manufacturer"><input type="text" value={data.pumpManuf} onChange={e => u("pumpManuf", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Pump Model"><input type="text" value={data.pumpModel} onChange={e => u("pumpModel", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Primary Rate (mL/hr)"><input type="text" value={data.primaryRate} onChange={e => u("primaryRate", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Maximum Rate (mL/hr)"><input type="text" value={data.maxRate} onChange={e => u("maxRate", e.target.value)} style={textInput} /></FieldRow>
          </div>
          <FieldRow label="Titration Instructions">
            <textarea value={data.titration} onChange={e => u("titration", e.target.value)} style={{ ...textInput, minHeight: 60 }} />
          </FieldRow>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 16 }}>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", marginBottom: 10 }}>Alarm Parameters</p>
              {[["alarmOcclusion", "Occlusion"], ["alarmAir", "Air in Line"], ["alarmBattery", "Low Battery"]].map(([k, l]) => (
                <CheckBox key={k} label={l} checked={data[k]} onChange={v => u(k, v)} />
              ))}
            </div>
            <div>
              <p style={{ fontSize: 12, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", marginBottom: 10 }}>If Pump Failure</p>
              {[["failGravity", "Switch to Gravity"], ["failBolus", "Switch to Bolus"], ["failHold", "Hold Feeding"]].map(([k, l]) => (
                <CheckBox key={k} label={l} checked={data[k]} onChange={v => u(k, v)} />
              ))}
            </div>
          </div>

          <SectionHeader title="Section V — Pediatric Addendum (if applicable)" icon="👶" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
            <FieldRow label="Weight (kg)"><input type="text" value={data.pediatricWeight} onChange={e => u("pediatricWeight", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Caloric Req per kg"><input type="text" value={data.pediatricCal} onChange={e => u("pediatricCal", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Growth Monitoring Frequency"><input type="text" value={data.growthMonitoring} onChange={e => u("growthMonitoring", e.target.value)} style={textInput} /></FieldRow>
          </div>

          <SectionHeader title="Section VI — High Aspiration Risk Protocol" icon="🚨" />
          <CheckBox label="High Aspiration Risk — activate protocol" checked={data.aspRisk} onChange={v => u("aspRisk", v)} />
          {data.aspRisk && (
            <div style={{ padding: "12px 16px", background: "#FEF2F2", border: `1px solid ${BRAND.error}`, borderRadius: 8, marginTop: 8, marginBottom: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: BRAND.error, marginBottom: 8 }}>Active Aspiration Precautions:</p>
              <ul style={{ margin: 0, padding: "0 0 0 20px", fontSize: 13, color: BRAND.gray800, lineHeight: 2 }}>
                <li>Strict HOB elevation at all times during feeding</li>
                <li>Hold feeding for cough, respiratory distress, or O2 desaturation</li>
              </ul>
              <FieldRow label="Immediate Notification Parameters">
                <input type="text" value={data.aspNotify} onChange={e => u("aspNotify", e.target.value)} style={textInput} />
              </FieldRow>
            </div>
          )}
          <div style={{ display: "flex", gap: 12, justifyContent: "space-between" }}>
            <button style={btnSecondary} onClick={() => setSection("main")}>← Back</button>
            <button style={btnPrimary} onClick={() => setSection("peg")}>Continue to Site Care →</button>
          </div>
        </div>
      )}

      {section === "peg" && (
        <div>
          <SectionHeader title="Section III — PEG Site Care" icon="🔬" />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <FieldRow label="Stoma Assessment Frequency"><input type="text" value={data.stomFreq} onChange={e => u("stomFreq", e.target.value)} style={textInput} /></FieldRow>
            <FieldRow label="Granulation Tissue Management"><input type="text" value={data.granulation} onChange={e => u("granulation", e.target.value)} style={textInput} /></FieldRow>
          </div>
          <FieldRow label="External Bumper Position Verification Instructions">
            <textarea value={data.bumperInstructions} onChange={e => u("bumperInstructions", e.target.value)} style={{ ...textInput, minHeight: 80 }} />
          </FieldRow>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <FieldRow label="Site Care — Clean Daily With">
              <input type="text" value={data.siteCare} onChange={e => u("siteCare", e.target.value)} style={textInput} placeholder="e.g. Soap and water, CHG..." />
            </FieldRow>
            <FieldRow label="Rotate Tube Daily">
              <select value={data.rotateTube} onChange={e => u("rotateTube", e.target.value)} style={textInput}>
                <option>Yes</option>
                <option>No</option>
              </select>
            </FieldRow>
          </div>

          <div style={{ marginTop: 20, padding: "14px 16px", background: BRAND.offWhite, borderRadius: 10, fontSize: 12, color: BRAND.gray600 }}>
            <strong style={{ color: BRAND.purple }}>Regulatory Alignment:</strong> This packet aligns with CMS CoP 42 CFR §484.55 (Comprehensive Assessment),
            42 CFR §484.60 (Plan of Care Requirements), 42 CFR §484.110 (Clinical Records),
            ACHC HH5-1A (Plan of Care), and ACHC HH7-3B (Medication and Treatment Orders).
          </div>

          <PhotoUpload
            docId={null}
            formType="TUBE_FEEDING"
            onUploaded={(paths) =>
              setData(prev => ({ ...prev, photoPaths: paths }))}
          />

          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 16 }}>
            <button style={btnSecondary} onClick={() => setSection("pump")}>← Back</button>
            <button style={btnPrimary} onClick={() => setSection("sig")}>Continue to Signatures →</button>
          </div>
        </div>
      )}

      {section === "sig" && (
        <div>
          <SectionHeader title="Ordering Provider Signature" icon="✍️" />
          <div style={{ marginBottom: 20 }}>
            <FieldRow label="Ordering Provider Name">
              <input type="text" style={textInput} placeholder="Provider name" />
            </FieldRow>
            <FieldRow label="Provider NPI">
              <input type="text" style={textInput} placeholder="10-digit NPI" />
            </FieldRow>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <SignaturePad ref={mdSigPadRef} label="Ordering Provider / Physician Signature" onSigned={setMdSig} signed={mdSig} />
            <SignaturePad ref={rnSigPadRef} label="RN / Clinical Nurse Completing Documentation" onSigned={setRnSig} signed={rnSig} />
            <SignaturePad ref={clientSigPadRef} label="Client / Caregiver Signature (optional)" onSigned={setClientSig} signed={clientSig} />
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "space-between", marginTop: 24 }}>
            <button style={btnSecondary} onClick={() => setSection("peg")}>← Back</button>
            <button style={btnGold} onClick={() => onComplete({
              type: "TUBE_FEEDING", data, patient, clearDraft, draftIdRef,
              rnSig: sigFromPad(rnSig, rnSigPadRef, "RN / Clinical Nurse Completing Documentation"),
              mdSig: sigFromPad(mdSig, mdSigPadRef, "Ordering Provider / Physician Signature"),
              clientSig: sigFromPad(clientSig, clientSigPadRef, "Client / Caregiver Signature"),
            })}
              disabled={!mdSig && !rnSig && !mdSigPadRef.current?.hasStroke?.() && !rnSigPadRef.current?.hasStroke?.()}>
              ✓ Submit Tube Feeding Packet
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Document Submitted Screen ────────────────────────────────────────────────
function generatePDF(doc, hash, downloadedBy) {
  const pdf = new jsPDF({ unit: "pt", format: "letter" });
  const W = pdf.internal.pageSize.getWidth();
  const H = pdf.internal.pageSize.getHeight();

  const PURPLE = [107, 45, 158];
  const GOLD = [201, 146, 42];
  const PURPLE_TINT = [245, 240, 250];
  const MED_GRAY = [102, 102, 102];

  // WATERMARK (diagonal, ~8% opacity). Fix 10 — include downloader identity
  // on the diagonal pattern so leaked PDFs can be traced back to a session.
  pdf.saveGraphicsState();
  pdf.setGState(pdf.GState({ opacity: 0.08 }));
  pdf.setTextColor(...PURPLE);
  pdf.setFontSize(60);
  pdf.setFont("helvetica", "bold");
  pdf.text("NNHC • ORIGINAL", W / 2, H / 2, { align: "center", angle: 28 });
  if (downloadedBy && downloadedBy.name) {
    pdf.setFontSize(10);
    pdf.text(
      `Downloaded by ${downloadedBy.name} · ${new Date().toLocaleString()}`,
      W / 2, H / 2 + 40, { align: "center", angle: 28 }
    );
  }
  pdf.restoreGraphicsState();

  // VOIDED watermark — overrides original watermark with a red diagonal stamp.
  if (doc.status === "voided") {
    pdf.saveGraphicsState();
    pdf.setGState(pdf.GState({ opacity: 0.25 }));
    pdf.setTextColor(220, 38, 38);
    pdf.setFontSize(140);
    pdf.setFont("helvetica", "bold");
    pdf.text("VOIDED", W / 2, H / 2, { align: "center", angle: 28 });
    pdf.restoreGraphicsState();
  }

  // HEADER: logo (left)
  try {
    pdf.addImage(NNHC_LOGO_BASE64, "PNG", 40, 30, 110, 80);
  } catch (e) { console.warn("Logo embed failed:", e); }

  // Contact info (middle)
  pdf.setFontSize(7);
  pdf.setTextColor(51, 51, 51);
  pdf.setFont("helvetica", "normal");
  let cy = 40;
  pdf.text("10915 Downs Road • Pineville, NC 28134", 175, cy); cy += 11;
  pdf.text("Tel  (888) 548-1568", 175, cy); cy += 11;
  pdf.text("Fax (980) 223-6033", 175, cy); cy += 11;
  pdf.setTextColor(...PURPLE);
  pdf.text("NurturingNursesHC.com", 175, cy); cy += 13;
  pdf.setTextColor(85, 85, 85);
  pdf.setFontSize(6.5);
  pdf.text("NC DHSR Licensed Home Care Agency Since 2013 • ACHC Accreditation Pending • NPI 1457693301", 175, cy);

  // Document title (right) - bold small-caps
  const DOC_TITLES = {
    RN_SUPERVISORY: ["90-DAY", "SUPERVISORY VISIT"],
    PLAN_OF_CARE: ["PLAN OF", "CARE"],
    TUBE_FEEDING: ["TUBE FEEDING", "ORDER PACKET"],
    WOUND_CARE: ["WOUND CARE", "NOTE"],
    SEIZURE_VNS: ["SEIZURE /", "VNS LOG"],
    IV_INFUSION: ["IV INFUSION", "RECORD"],
    VENT_TRACH: ["VENT / TRACH", "CARE NOTE"],
    SKILLED_NURSING: ["SKILLED NURSING", "ASSESSMENT"],
    SUPPLY_LOG: ["SUPPLY", "DELIVERY LOG"],
    SHIFT_NOTE: ["NURSE'S", "SHIFT NOTE"],
  };
  const [line1, line2] = DOC_TITLES[doc.doc_type] || ["CLINICAL", "DOCUMENT"];
  pdf.setFontSize(14);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(...PURPLE);
  pdf.setCharSpace(1.5);
  pdf.text(line1, W - 40, 48, { align: "right" });
  pdf.text(line2, W - 40, 66, { align: "right" });
  pdf.setCharSpace(0);

  // CONFIDENTIAL PHI badge
  pdf.setFillColor(...GOLD);
  pdf.roundedRect(W - 140, 76, 100, 14, 2, 2, "F");
  pdf.setTextColor(44, 26, 0);
  pdf.setFontSize(7);
  pdf.setFont("helvetica", "bold");
  pdf.text("CONFIDENTIAL PHI", W - 90, 86, { align: "center" });

  // Gold separator
  pdf.setDrawColor(...GOLD);
  pdf.setLineWidth(2);
  pdf.line(40, 120, W - 40, 120);

  // METADATA ROW (dynamic "Visiting RN / DON" vs "Visiting RN")
  let y = 138;
  const colW = (W - 80) / 3;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...MED_GRAY);
  pdf.text("VISITING RN", 40, y);
  pdf.text("VISIT DATE", 40 + colW, y);
  pdf.text("NEXT VISIT DUE", 40 + colW * 2, y);

  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text((doc.clinician_name || "") + ", " + (doc.clinician_creds || ""), 40, y + 13);
  pdf.text(new Date(doc.created_at || Date.now()).toLocaleDateString("en-US"), 40 + colW, y + 13);
  const NEXT_VISIT_DAYS = {
    RN_SUPERVISORY: 90,
    WOUND_CARE: 7,
    SKILLED_NURSING: 30,
    VENT_TRACH: 14,
    PLAN_OF_CARE: 60,
    TUBE_FEEDING: 30,
  };
  const baseDate = doc.created_at ? new Date(doc.created_at) : new Date();
  const days = NEXT_VISIT_DAYS[doc.doc_type];
  const nextVisitStr = days
    ? new Date(baseDate.getTime() + days * 86400000).toLocaleDateString("en-US")
    : "Per physician order";
  pdf.text(nextVisitStr, 40 + colW * 2, y + 13);

  // CLIENT INFO BOX
  y += 32;
  pdf.setFillColor(...PURPLE_TINT);
  pdf.rect(40, y, W - 80, 38, "F");
  pdf.setFillColor(...PURPLE);
  pdf.rect(40, y, 3, 38, "F");
  pdf.setFontSize(7);
  pdf.setTextColor(...MED_GRAY);
  pdf.setFont("helvetica", "normal");
  pdf.text("CLIENT", 50, y + 10);
  pdf.text("DOB", 50 + colW, y + 10);
  pdf.text("MID #", 50 + colW * 2, y + 10);
  pdf.setFontSize(9);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text(doc.patient_name || "", 50, y + 22);
  pdf.text(doc.patient_dob || "", 50 + colW, y + 22);
  pdf.text(doc.patient_mid || "", 50 + colW * 2, y + 22);
  pdf.setFontSize(7.5);
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(51, 51, 51);
  pdf.text("ICD-10: " + (doc.patient_icd10 || doc.form_data?.icd10 || "On file"), 50, y + 33);

  // FORM DATA — structured rendering (Fix 3)
  y += 55;
  pdf.setFontSize(9);
  pdf.setTextColor(...PURPLE);
  pdf.setFont("helvetica", "bold");
  pdf.text("FORM DATA", 40, y);
  y += 14;

  const isShiftNote = doc.doc_type === "SHIFT_NOTE";

  const formData = doc.form_data || doc.data || {};
  const omissionMap = {};
  (formData._omissions || []).forEach(o => { if (o && o.field) omissionMap[o.field] = o.reason || ""; });

  const needPage = (h) => { if (y > H - 180 - h) { pdf.addPage(); y = 50; } };

  const drawTable = (title, rows) => {
    needPage(40 + rows.length * 14);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text(title, 40, y);
    y += 6;
    const tableX = 40, tableW = W - 80, col1 = 150;
    pdf.setDrawColor(...PURPLE);
    pdf.setLineWidth(0.6);
    // header row
    pdf.setFillColor(...PURPLE_TINT);
    pdf.rect(tableX, y, tableW, 14, "FD");
    pdf.setFontSize(7.5);
    pdf.setTextColor(...PURPLE);
    pdf.setFont("helvetica", "bold");
    pdf.text("FIELD", tableX + 6, y + 9);
    pdf.text("VALUE", tableX + col1 + 6, y + 9);
    y += 14;
    // body rows
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    rows.forEach(([k, v]) => {
      needPage(16);
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.3);
      pdf.rect(tableX, y, tableW, 14, "S");
      pdf.line(tableX + col1, y, tableX + col1, y + 14);
      pdf.setFontSize(7.5);
      pdf.setTextColor(...MED_GRAY);
      pdf.text(String(k), tableX + 6, y + 9);
      pdf.setTextColor(0, 0, 0);
      pdf.text(String(v || "—"), tableX + col1 + 6, y + 9);
      y += 14;
    });
    y += 8;
  };

  const camelToTitle = (str) => String(str)
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
    .replace(/\s+/g, " ");

  // Plain-object → comma-joined truthy keys, or "None documented"
  const renderObjectAsList = (obj) => {
    const truthy = Object.entries(obj)
      .filter(([, v]) => v === true)
      .map(([k]) => camelToTitle(k));
    return truthy.length ? truthy.join(", ") : "None documented";
  };

  // SHIFT_NOTE — dedicated layout that mirrors NNHC's paper Nurse's Shift Note
  if (isShiftNote) {
    const fd = formData;

    // Visit Info table: DATE | DAY | START | END | TOTAL TIME
    const visitCols = [
      ["DATE", fd.visitDate ? new Date(fd.visitDate).toLocaleDateString("en-US")
        : new Date(doc.created_at || Date.now()).toLocaleDateString("en-US")],
      ["DAY", fd.day || "—"],
      ["START", fd.startTime || "—"],
      ["END", fd.endTime || "—"],
      ["TOTAL TIME", fd.totalTimeWorked || "—"],
    ];
    needPage(40);
    const vcellW = (W - 80) / visitCols.length;
    pdf.setDrawColor(...PURPLE);
    pdf.setLineWidth(0.6);
    pdf.setFillColor(...PURPLE_TINT);
    pdf.rect(40, y, W - 80, 14, "FD");
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    visitCols.forEach((c, i) => pdf.text(c[0], 40 + i * vcellW + 6, y + 9));
    y += 14;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.rect(40, y, W - 80, 16, "S");
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    visitCols.forEach((c, i) => {
      if (i > 0) pdf.line(40 + i * vcellW, y, 40 + i * vcellW, y + 16);
      pdf.text(String(c[1]), 40 + i * vcellW + 6, y + 11);
    });
    y += 24;

    // Vitals row: 7 columns
    const v = fd.vitals || {};
    needPage(50);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("VITAL SIGNS", 40, y);
    y += 10;
    const vitCols = [
      ["TEMP °F", v.temp || "—"],
      ["PULSE", v.pulse || "—"],
      ["RP/AP", fd.pulseType || "—"],
      ["RESP", v.resp || "—"],
      ["O2 SAT %", v.o2sat || "—"],
      ["B/P", fd.bp || "—"],
      ["POSITION", fd.bpPosition || "—"],
    ];
    const vw = (W - 80) / vitCols.length;
    pdf.setDrawColor(...PURPLE);
    pdf.setLineWidth(0.6);
    pdf.setFillColor(...PURPLE_TINT);
    pdf.rect(40, y, W - 80, 14, "FD");
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    vitCols.forEach((c, i) => pdf.text(c[0], 40 + i * vw + 4, y + 9));
    y += 14;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.3);
    pdf.rect(40, y, W - 80, 16, "S");
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    vitCols.forEach((c, i) => {
      if (i > 0) pdf.line(40 + i * vw, y, 40 + i * vw, y + 16);
      pdf.text(String(c[1]), 40 + i * vw + 4, y + 11);
    });
    y += 24;

    // Systems grid — two columns
    const respLabels = { trach: "Trach", vent: "Vent", cpap: "CPAP", alarm: "Alarm", na: "N/A" };
    const respList = Object.entries(fd.respiratoryStatus || {})
      .filter(([, val]) => val === true)
      .map(([k]) => respLabels[k] || k)
      .join(", ") || "—";
    const equipList = [
      fd.equipmentChecked && "Primary",
      fd.backupEquipmentChecked && "Backup",
      fd.emergencyEquipmentChecked && "Emergency",
      fd.goBagChecked && "Go Bag",
    ].filter(Boolean).join(", ") || "—";
    const mentalDisplay = fd.mentalStatus === "Other"
      ? `Other: ${fd.mentalStatusOther || "—"}` : (fd.mentalStatus || "—");
    const ictlDisplay = fd.infectionControl === "Other"
      ? `Other: ${fd.infectionControlOther || "—"}` : (fd.infectionControl || "—");
    const seizDisplay = fd.seizures
      ? `Yes — ${fd.seizureDetails || "documented"}` : "No";

    const leftRows = [
      ["Mental Status", mentalDisplay],
      ["Resp Status", respList],
      ["Oxygen", fd.oxygen || "—"],
      ["Cardiac Status", fd.cardiacStatus || "—"],
      ["Edema", fd.edema || "—"],
      ["Skin Integrity", fd.skinIntegrity || "—"],
      ["Neurological", fd.neurological || "—"],
      ["Seizures", seizDisplay],
      ["Equipment", equipList],
      ["Other Notes", fd.otherNotes || "—"],
    ];
    const rightRows = [
      ["Nutrition / Diet", fd.nutritionDiet || "—"],
      ["Bowel Status", fd.bowelStatus || "—"],
      ["Urinary Status", fd.urinaryStatus || "—"],
      ["Environment / Safety", fd.environmentSafety || "—"],
      ["Communication Needs", fd.communicationNeeds || "—"],
      ["Infection Control", ictlDisplay],
    ];

    needPage(30);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("SYSTEMS ASSESSMENT", 40, y);
    y += 10;

    const gridColW = (W - 80) / 2;
    const labelW = 100;
    const lineH = 11;
    const padX = 6;

    const measureRows = (rows) => rows.reduce((acc, r) => {
      const wrapped = pdf.splitTextToSize(String(r[1]), gridColW - labelW - padX * 3);
      return acc + Math.max(14, wrapped.length * lineH + 6);
    }, 0);
    const totalGridH = Math.max(measureRows(leftRows), measureRows(rightRows));
    needPage(totalGridH + 8);

    const renderCol = (rows, x0) => {
      let cy = y;
      rows.forEach((r, idx) => {
        const wrapped = pdf.splitTextToSize(String(r[1]), gridColW - labelW - padX * 3);
        const h = Math.max(14, wrapped.length * lineH + 6);
        if (idx % 2 === 0) {
          pdf.setFillColor(...PURPLE_TINT);
          pdf.rect(x0, cy, gridColW, h, "F");
        }
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.3);
        pdf.rect(x0, cy, gridColW, h, "S");
        pdf.line(x0 + labelW, cy, x0 + labelW, cy + h);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(7.5);
        pdf.setTextColor(...MED_GRAY);
        pdf.text(r[0], x0 + padX, cy + 9);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(0, 0, 0);
        let ty = cy + 9;
        wrapped.forEach((line) => {
          pdf.text(line, x0 + labelW + padX, ty);
          ty += lineH;
        });
        cy += h;
      });
      return cy;
    };

    const leftEnd = renderCol(leftRows, 40);
    const rightEnd = renderCol(rightRows, 40 + gridColW);
    y = Math.max(leftEnd, rightEnd) + 12;

    // Change in Status
    needPage(34);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("CHANGE IN STATUS", 40, y);
    y += 10;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.text(`Status: ${fd.changeInStatus || "No"}`, 40, y);
    if (fd.changeInStatus === "Yes") {
      const coord = (fd.coordinatedWith || []).join(", ") || "—";
      pdf.text(`Coordinated with: ${coord}`, 160, y);
      if (fd.coordinatedWithOther) {
        y += 12;
        pdf.text(`Other: ${fd.coordinatedWithOther}`, 40, y);
      }
      if (fd.addendumCompleted) {
        y += 12;
        pdf.text("Addendum completed.", 40, y);
      }
    }
    y += 18;

    // Pain Status box
    needPage(70);
    pdf.setFillColor(...PURPLE_TINT);
    pdf.setDrawColor(...PURPLE);
    pdf.setLineWidth(0.6);
    pdf.rect(40, y, W - 80, 64, "FD");
    pdf.setFillColor(...PURPLE);
    pdf.rect(40, y, 3, 64, "F");
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("PAIN STATUS", 50, y + 11);
    pdf.setFontSize(8);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    let py = y + 24;
    pdf.text(`Pain problem: ${fd.painProblem || "No"}`, 50, py);
    if (fd.painProblem === "Yes") {
      pdf.text(`Intensity: ${fd.painIntensity || "—"}/10`, 200, py);
      pdf.text(`Type: ${fd.painType || "—"}`, 320, py);
      if (fd.painLocation) pdf.text(`Location: ${fd.painLocation}`, 420, py);
      py += 12;
      pdf.text(`Exacerbation: ${fd.exacerbationFactors || "—"}`, 50, py);
      py += 12;
      const tx = (fd.painTreatment || []).join(", ") +
        (fd.painTreatmentOther ? ` (${fd.painTreatmentOther})` : "");
      pdf.text(`Treatment: ${tx || "—"}`, 50, py);
      pdf.text(`Effective: ${fd.treatmentEffective || "—"}`, 320, py);
      if (fd.treatmentEffective === "No" && fd.treatmentFollowUp) {
        py += 12;
        pdf.text(`Follow-up: ${fd.treatmentFollowUp}`, 50, py);
      }
    }
    y += 72;

    // Narrative boxes
    const narBox = (title, text) => {
      if (!text) return;
      const wrapped = pdf.splitTextToSize(String(text), W - 100);
      const h = Math.max(36, wrapped.length * 11 + 18);
      needPage(h + 16);
      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...PURPLE);
      pdf.text(title, 40, y);
      y += 8;
      pdf.setDrawColor(...PURPLE);
      pdf.setLineWidth(0.6);
      pdf.rect(40, y, W - 80, h, "S");
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(8.5);
      pdf.setTextColor(0, 0, 0);
      let ty = y + 12;
      wrapped.forEach((line) => { pdf.text(line, 50, ty); ty += 11; });
      y += h + 10;
    };
    narBox("SHIFT NOTES", fd.shiftNotes);
    narBox("PROGRESS TOWARD GOALS", fd.progressNotes);
    narBox("MULTIDISCIPLINARY NOTES", fd.mdNotes);
  }

  // 1. Vitals table
  if (!isShiftNote && formData.vitals && typeof formData.vitals === "object") {
    const v = formData.vitals;
    const rows = [];
    const bpCell = (s, d) => (s || d) ? `${s || "—"}/${d || "—"}` : "";
    const pushIf = (label, value) => { if (value !== "" && value != null) rows.push([label, value]); };
    pushIf("Temperature (°F)", v.temp);
    pushIf("Pulse (bpm)",      v.pulse);
    pushIf("Respirations",     v.resp);
    pushIf("O2 Sat (%)",       v.o2sat);
    pushIf("B/P Lying",        bpCell(v.bp_lying_systolic, v.bp_lying_diastolic) || v.bpLying);
    pushIf("B/P Sitting",      bpCell(v.bp_sitting_systolic, v.bp_sitting_diastolic) || v.bpSitting);
    pushIf("B/P Standing",     bpCell(v.bp_standing_systolic, v.bp_standing_diastolic) || v.bpStanding);
    if (rows.length) drawTable("VITAL SIGNS", rows);
  }

  // 2. Checklist — clean two-column YES/NO table with alternating rows
  if (!isShiftNote && formData.checklist && typeof formData.checklist === "object") {
    const entries = Object.entries(formData.checklist).filter(([, v]) => v != null && v !== "");
    if (entries.length) {
      needPage(30 + Math.ceil(entries.length / 2) * 14);
      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...PURPLE);
      pdf.text("CLINICAL CHECKLIST", 40, y);
      y += 10;

      const rowH = 14;
      const tableX = 40;
      const tableW = W - 80;
      const colW = tableW / 2;

      for (let i = 0; i < entries.length; i += 2) {
        needPage(rowH);
        const rowIdx = i / 2;
        if (rowIdx % 2 === 0) {
          pdf.setFillColor(245, 240, 250);
          pdf.rect(tableX, y, tableW, rowH, "F");
        }
        for (let j = 0; j < 2 && i + j < entries.length; j++) {
          const [k, v] = entries[i + j];
          const isYes = v === "YES" || v === true;
          const cellX = tableX + j * colW;

          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(0, 0, 0);
          pdf.text(camelToTitle(k), cellX + 8, y + 9);

          pdf.setFont("helvetica", "bold");
          if (isYes) pdf.setTextColor(0, 100, 0); else pdf.setTextColor(150, 0, 0);
          pdf.text(isYes ? "YES" : "NO", cellX + colW - 8, y + 9, { align: "right" });
        }
        y += rowH;
      }
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(0, 0, 0);
      y += 8;
    }
  }

  // 3. ICD-10 / diagnoses
  if (!isShiftNote && Array.isArray(formData.icd10) && formData.icd10.length) {
    needPage(24);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("DIAGNOSES / ICD-10 CODES", 40, y);
    y += 12;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    pdf.text(formData.icd10.join(", "), 40, y);
    y += 14;
  } else if (!isShiftNote) {
    // Format: "[CODE] Description" per Sprint 4 Tier 1 spec.
    const fmtOne = (d) => {
      if (!d) return "";
      if (typeof d === "string") return d;
      if (!d.code) return "";
      return d.description ? `[${d.code}] ${d.description}` : `[${d.code}]`;
    };

    // Group: a wrapping label (e.g. "Primary") plus a list of formatted entries.
    const groups = [];
    if (formData.primaryDx) {
      const s = fmtOne(formData.primaryDx);
      if (s) groups.push({ label: "Primary", entries: [s] });
    }
    if (Array.isArray(formData.secondaryDx) && formData.secondaryDx.length) {
      const entries = formData.secondaryDx.map(fmtOne).filter(Boolean);
      if (entries.length) groups.push({ label: "Secondary", entries });
    }
    if (Array.isArray(formData.diagnoses) && formData.diagnoses.length) {
      const entries = formData.diagnoses.map(fmtOne).filter(Boolean);
      if (entries.length) groups.push({ label: null, entries });
    }
    if (Array.isArray(formData.woundDiagnoses) && formData.woundDiagnoses.length) {
      const entries = formData.woundDiagnoses.map(fmtOne).filter(Boolean);
      if (entries.length) groups.push({ label: "Wound", entries });
    }

    if (groups.length) {
      needPage(28);
      pdf.setFontSize(8.5);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...PURPLE);
      pdf.text("DIAGNOSES / ICD-10 CODES", 40, y);
      y += 12;
      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      const lineH = 11;
      groups.forEach((g) => {
        if (g.label) {
          needPage(lineH + 2);
          pdf.setFont("helvetica", "bold");
          pdf.text(`${g.label}:`, 40, y);
          y += lineH;
        }
        pdf.setFont("helvetica", "normal");
        g.entries.forEach((entry) => {
          const wrapped = pdf.splitTextToSize(`• ${entry}`, W - 100);
          needPage(wrapped.length * lineH);
          wrapped.forEach((line) => { pdf.text(line, 50, y); y += lineH; });
        });
      });
      y += 4;
    }
  }

  // 3b. WOUND_CARE — per-wound Tissue Type & Drainage summary
  if (doc.doc_type === "WOUND_CARE") {
    const woundCount = parseInt(formData.woundCount) || 1;
    const woundRows = [];
    for (let i = 1; i <= woundCount; i++) {
      const tissueArr = formData[`woundTissue_${i}`];
      const drainAmt = formData[`woundDrainageAmt_${i}`];
      const drainType = formData[`woundDrainageType_${i}`];
      const tissueDisplay = Array.isArray(tissueArr) && tissueArr.length
        ? tissueArr.join(", ") : "—";
      const drainDisplay = (drainAmt || drainType)
        ? `${drainAmt || "—"} / ${drainType || "—"}` : "—";
      if (tissueArr || drainAmt || drainType) {
        woundRows.push([`Wound #${i} — Tissue Type`, tissueDisplay]);
        woundRows.push([`Wound #${i} — Drainage`, drainDisplay]);
      }
    }
    if (woundRows.length) drawTable("WOUND TISSUE & DRAINAGE", woundRows);
  }

  // 4. Additional Clinical Data — bordered table with boolean / object handling
  const SKIP = new Set([
    "vitals", "checklist", "checklistComments", "icd10", "diagnoses", "_omissions",
    "photoPaths", "woundPhotoPath", "attachedDocuments", "woundMarkers",
    "primaryDx", "secondaryDx", "woundDiagnoses",
  ]);
  const SKIP_PREFIX = /^(woundTissue|woundDrainageAmt|woundDrainageType)_\d+$/;

  const additional = [];
  if (!isShiftNote) Object.entries(formData).forEach(([key, value]) => {
    if (SKIP.has(key)) return;
    if (SKIP_PREFIX.test(key)) return;
    if (value == null || value === "") return;
    if (typeof value === "object" && !Array.isArray(value) &&
        Object.keys(value).length === 0) return;

    const isOmitted = Object.prototype.hasOwnProperty.call(omissionMap, key);
    let display;
    if (isOmitted) {
      display = `[Not Applicable: ${omissionMap[key]}]`;
    } else if (typeof value === "boolean") {
      display = value ? "Yes" : "No";
    } else if (Array.isArray(value)) {
      display = value
        .map((item) => (item && typeof item === "object" ? renderObjectAsList(item) : String(item)))
        .filter((s) => s !== "")
        .join(", ");
    } else if (typeof value === "object") {
      display = renderObjectAsList(value);
    } else {
      display = String(value);
    }
    additional.push({ label: camelToTitle(key), display, isOmitted });
  });

  if (additional.length) {
    needPage(40);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("ADDITIONAL CLINICAL DATA", 40, y);
    y += 6;

    const tableX = 40;
    const tableW = W - 80;
    const labelColW = 180;
    const valueColW = tableW - labelColW;
    const headerH = 16;
    const lineH = 11;
    const padY = 6;

    pdf.setFillColor(...PURPLE);
    pdf.setDrawColor(...PURPLE);
    pdf.setLineWidth(0.6);
    pdf.rect(tableX, y, tableW, headerH, "FD");
    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(255, 255, 255);
    pdf.text("FIELD", tableX + 8, y + 11);
    pdf.text("VALUE", tableX + labelColW + 8, y + 11);
    y += headerH;

    additional.forEach((row, idx) => {
      const wrapped = pdf.splitTextToSize(row.display, valueColW - 16);
      const rowH = Math.max(16, wrapped.length * lineH + padY);
      needPage(rowH);

      if (idx % 2 === 0) {
        pdf.setFillColor(...PURPLE_TINT);
        pdf.rect(tableX, y, tableW, rowH, "F");
      }
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.3);
      pdf.rect(tableX, y, tableW, rowH, "S");
      pdf.line(tableX + labelColW, y, tableX + labelColW, y + rowH);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(7.5);
      pdf.setTextColor(...MED_GRAY);
      pdf.text(row.label, tableX + 8, y + 11);

      pdf.setFont("helvetica", row.isOmitted ? "italic" : "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(0, 0, 0);
      let ty = y + 11;
      wrapped.forEach((line) => {
        pdf.text(line, tableX + labelColW + 8, ty);
        ty += lineH;
      });

      y += rowH;
    });

    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    y += 8;
  }

  // Wound photo attachment notice (Fix 4)
  if (formData.woundPhotoPath) {
    needPage(28);
    pdf.setFillColor(245, 240, 250);
    pdf.rect(40, y, W - 80, 20, 'F');
    pdf.setFontSize(8);
    pdf.setTextColor(...PURPLE);
    pdf.setFont("helvetica", "normal");
    pdf.text('Wound photo on file - securely stored in NNHC clinical system',
      50, y + 13);
    y += 28;
    pdf.setTextColor(0, 0, 0);
  }

  // Clinical photos attachment notice
  if (Array.isArray(formData.photoPaths) && formData.photoPaths.length) {
    needPage(28);
    pdf.setFillColor(245, 240, 250);
    pdf.rect(40, y, W - 80, 20, 'F');
    pdf.setFontSize(8);
    pdf.setTextColor(...PURPLE);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      `📎 ${formData.photoPaths.length} clinical photo(s) on file — securely stored in NNHC clinical system.`,
      50, y + 13
    );
    y += 28;
    pdf.setTextColor(0, 0, 0);
  }

  // Attached documents — structured table (category | filename | size | uploaded)
  if (Array.isArray(formData.attachedDocuments) && formData.attachedDocuments.length) {
    const fmtBytes = (b) => {
      if (!b) return "—";
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
      return `${(b / 1024 / 1024).toFixed(1)} MB`;
    };
    const fmtUploaded = (iso) => {
      if (!iso) return "—";
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-US");
    };

    needPage(40);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("ATTACHED SUPPORTING DOCUMENTS", 40, y);
    y += 6;

    const tableX = 40;
    const tableW = W - 80;
    const colCategory = 110;
    const colSize = 60;
    const colDate = 70;
    const colFilename = tableW - colCategory - colSize - colDate;
    const headerH = 14;
    const lineH = 11;

    pdf.setFillColor(...PURPLE);
    pdf.setDrawColor(...PURPLE);
    pdf.setLineWidth(0.6);
    pdf.rect(tableX, y, tableW, headerH, "FD");
    pdf.setFontSize(7.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(255, 255, 255);
    pdf.text("CATEGORY", tableX + 6, y + 9);
    pdf.text("FILENAME", tableX + colCategory + 6, y + 9);
    pdf.text("SIZE", tableX + colCategory + colFilename + 6, y + 9);
    pdf.text("UPLOADED", tableX + colCategory + colFilename + colSize + 6, y + 9);
    y += headerH;

    formData.attachedDocuments.forEach((d, idx) => {
      const cat = d.label || d.category || "Document";
      const filename = d.filename || "—";
      const wrapped = pdf.splitTextToSize(filename, colFilename - 12);
      const rowH = Math.max(14, wrapped.length * lineH + 4);
      needPage(rowH);
      if (idx % 2 === 0) {
        pdf.setFillColor(245, 240, 250);
        pdf.rect(tableX, y, tableW, rowH, "F");
      }
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.3);
      pdf.rect(tableX, y, tableW, rowH, "S");
      pdf.line(tableX + colCategory, y, tableX + colCategory, y + rowH);
      pdf.line(tableX + colCategory + colFilename, y, tableX + colCategory + colFilename, y + rowH);
      pdf.line(tableX + colCategory + colFilename + colSize, y,
               tableX + colCategory + colFilename + colSize, y + rowH);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(0, 0, 0);
      pdf.text(cat, tableX + 6, y + 10);
      let ty = y + 10;
      wrapped.forEach((line) => {
        pdf.text(line, tableX + colCategory + 6, ty);
        ty += lineH;
      });
      pdf.text(fmtBytes(d.sizeBytes), tableX + colCategory + colFilename + 6, y + 10);
      pdf.text(fmtUploaded(d.uploadedAt),
        tableX + colCategory + colFilename + colSize + 6, y + 10);
      y += rowH;
    });

    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(7);
    pdf.setTextColor(102, 102, 102);
    pdf.text("Files stored securely. Available in NNHC Clinical Portal.",
      tableX, y + 10);
    y += 18;
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
  }

  // 5. Render any omissions whose field key was not in formData
  const omissionsArray = formData._omissions || [];
  const leftover = omissionsArray.filter(o => !(o.field in formData));
  if (leftover.length) {
    needPage(20 + leftover.length * 12);
    pdf.setFontSize(8.5);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(...PURPLE);
    pdf.text("FIELDS OMITTED WITH REASON", 40, y);
    y += 12;
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8);
    pdf.setTextColor(0, 0, 0);
    leftover.forEach(o => {
      needPage(14);
      const line = `${camelToTitle(o.field)}: [Not Applicable: ${o.reason}]`;
      const wrapped = pdf.splitTextToSize(line, W - 80);
      wrapped.forEach(l => {
        needPage(12);
        pdf.text(l, 40, y);
        y += 11;
      });
    });
    pdf.setFont("helvetica", "normal");
  }

  // CONSENT BOX
  y += 8;
  if (y > H - 180) { pdf.addPage(); y = 50; }
  pdf.setDrawColor(...PURPLE);
  pdf.setFillColor(...PURPLE_TINT);
  pdf.rect(40, y, W - 80, 44, "FD");
  pdf.setFontSize(7.5);
  pdf.setFont("helvetica", "italic");
  pdf.setTextColor(0, 0, 0);
  const CONSENT = {
    RN_SUPERVISORY: "I acknowledge and confirm the NNHC RN performed supervisory note and evaluation as part of my care. This process is done every 90 days. I recognize and agree that I have the right to refuse treatment or terminate services at any time by notifying Nurturing Nurses HomeCare, Inc.",
    WOUND_CARE: "I acknowledge and consent to wound care assessment and treatment provided by the NNHC RN during this visit. I understand the wound care plan, dressing changes, and any physician-ordered treatments performed. I have the right to refuse treatment at any time by notifying Nurturing Nurses HomeCare, Inc.",
    SKILLED_NURSING: "I acknowledge and confirm the NNHC RN performed a skilled nursing assessment during this visit. I have been informed of the clinical findings and any changes to my plan of care. I have the right to refuse treatment or terminate services at any time by notifying Nurturing Nurses HomeCare, Inc.",
    SEIZURE_VNS: "I acknowledge that the NNHC RN documented the seizure event and post-ictal assessment in this record. The information provided is accurate to the best of my knowledge. I have the right to request amendments by contacting Nurturing Nurses HomeCare, Inc.",
    IV_INFUSION: "I acknowledge and consent to IV infusion therapy administered by the NNHC RN during this visit. I have been informed of the medication, dosage, rate, and any observed reactions. I have the right to refuse treatment at any time by notifying Nurturing Nurses HomeCare, Inc.",
    VENT_TRACH: "I acknowledge that the NNHC RN performed ventilator monitoring and tracheostomy care during this visit. I have been informed of current clinical status and any equipment concerns. I have the right to refuse treatment at any time by notifying Nurturing Nurses HomeCare, Inc.",
    PLAN_OF_CARE: "I acknowledge receipt and review of this Plan of Care. I understand the goals, visit frequency, physician orders, and my rights regarding this plan. I have the right to request changes by contacting Nurturing Nurses HomeCare, Inc.",
    SHIFT_NOTE: "I acknowledge that the NNHC nurse provided skilled nursing services during this shift and documented my assessment, vitals, and care provided. I have the right to refuse treatment or request changes by notifying Nurturing Nurses HomeCare, Inc.",
  };
  const consentText = CONSENT[doc.doc_type] || CONSENT.RN_SUPERVISORY;
  pdf.text(pdf.splitTextToSize(consentText, W - 100), 50, y + 12);

  // SIGNATURES — embed actual signature images (base64 PNG data URLs from
  // the rn_signature / client_signature / md_signature columns). When a
  // signature is missing, render "Not signed" in red instead of silently
  // omitting the slot.
  y += 16;
  if (y > H - 160) { pdf.addPage(); y = 50; }

  const rnRole = (doc.clinician_role || "").toUpperCase();
  const rnLabel = (rnRole.includes("DON") || rnRole.includes("DIRECTOR"))
    ? "Visiting RN / DON" : "Visiting RN";
  const submittedAt = new Date(doc.created_at || Date.now())
    .toLocaleString("en-US");

  const drawSigBlock = (x, w, sigDataUrl, name, role) => {
    const blockH = 88;
    needPage(blockH + 10);
    const imgH = 36;
    const imgW = Math.min(w - 8, 200);
    if (sigDataUrl && typeof sigDataUrl === "string" && sigDataUrl.startsWith("data:")) {
      try {
        pdf.addImage(sigDataUrl, "PNG", x + 4, y, imgW, imgH);
      } catch (e) {
        console.warn("Signature image embed failed:", e);
      }
    } else {
      pdf.setFont("helvetica", "italic");
      pdf.setFontSize(11);
      pdf.setTextColor(220, 38, 38);
      pdf.text("Not signed", x + 4, y + 22);
    }
    // Underline
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.7);
    pdf.line(x, y + imgH + 4, x + w - 4, y + imgH + 4);
    // Name + role + timestamp
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    pdf.setTextColor(0, 0, 0);
    pdf.text(name || "—", x + 4, y + imgH + 16);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(102, 102, 102);
    pdf.text(role, x + 4, y + imgH + 26);
    pdf.text(`Signed: ${submittedAt}`, x + 4, y + imgH + 36);
  };

  const hasMd = !!doc.md_signature;
  const slots = hasMd ? 3 : 2;
  const gap = 12;
  const sigW = (W - 80 - gap * (slots - 1)) / slots;

  drawSigBlock(40, sigW,
    doc.rn_signature,
    `${doc.clinician_name || ""}${doc.clinician_creds ? ", " + doc.clinician_creds : ""}`,
    rnLabel);
  drawSigBlock(40 + (sigW + gap), sigW,
    doc.client_signature,
    doc.patient_name || "—",
    "Patient / Client Signature");
  if (hasMd) {
    drawSigBlock(40 + 2 * (sigW + gap), sigW,
      doc.md_signature,
      doc.form_data?.physicianName || "Ordering Physician",
      "Physician Signature");
  }
  y += 88 + 12;

  // ADDENDA / AMENDMENTS — Sprint 5b
  const allAmendments = Array.isArray(doc.amendments) ? doc.amendments : [];
  const addenda = allAmendments.filter(a => a && a.type === "addendum" && a.status === "applied");
  const appliedAmendments = allAmendments.filter(a => a && a.type === "amendment" && a.status === "approved");

  const drawSectionHeader = (title) => {
    needPage(40);
    pdf.setDrawColor(...PURPLE);
    pdf.setLineWidth(0.6);
    pdf.line(40, y, W - 40, y);
    y += 12;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.setTextColor(...PURPLE);
    pdf.setCharSpace(1);
    pdf.text(title, 40, y);
    pdf.setCharSpace(0);
    y += 6;
    pdf.line(40, y, W - 40, y);
    y += 12;
  };

  const drawWrapped = (label, value, indent = 50) => {
    if (value == null || value === "") return;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.setTextColor(...MED_GRAY);
    pdf.text(label, indent, y);
    pdf.setFont("helvetica", "normal");
    pdf.setTextColor(0, 0, 0);
    const valStr = String(value);
    const lines = pdf.splitTextToSize(valStr, W - indent - 40 - 70);
    lines.forEach((ln, idx) => {
      needPage(12);
      pdf.text(ln, indent + 70, y);
      if (idx < lines.length - 1) y += 10;
    });
    y += 12;
  };

  if (addenda.length > 0) {
    drawSectionHeader("ADDENDA");
    addenda.forEach((a, i) => {
      needPage(60);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(...PURPLE);
      const ts = a.created_at ? new Date(a.created_at).toLocaleString("en-US") : "—";
      const author = `${a.author_name || ""}${a.author_credentials ? ", " + a.author_credentials : ""}`;
      pdf.text(`[${i + 1}] ${ts} — ${author}`, 40, y);
      y += 14;
      drawWrapped("Reason:", a.reason);
      drawWrapped("Content:", a.content);
      y += 6;
    });
  }

  if (appliedAmendments.length > 0) {
    drawSectionHeader("AMENDMENTS");
    appliedAmendments.forEach((a, i) => {
      needPage(80);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      pdf.setTextColor(...PURPLE);
      const ts = a.reviewed_at ? new Date(a.reviewed_at).toLocaleString("en-US") : "—";
      pdf.text(`[${i + 1}] Applied ${ts}`, 40, y);
      y += 14;
      drawWrapped("Field:", a.field_path);
      drawWrapped("Original:", a.original_value);
      drawWrapped("Amended to:", a.content);
      drawWrapped("Reason:", a.reason);
      const proposedBy = `${a.author_name || ""}${a.author_credentials ? ", " + a.author_credentials : ""}`;
      drawWrapped("Proposed by:", proposedBy);
      const approvedAt = a.reviewed_at ? new Date(a.reviewed_at).toLocaleString("en-US") : "—";
      drawWrapped("Approved by:", `${a.reviewed_by_name || "—"} on ${approvedAt}`);
      drawWrapped("Approval notes:", a.review_notes);
      y += 6;
    });
  }

  // FOOTER
  y += 50;
  if (y > H - 60) { pdf.addPage(); y = H - 80; }
  pdf.setDrawColor(...GOLD);
  pdf.setLineWidth(1);
  pdf.line(40, y, W - 40, y);
  y += 10;
  pdf.setFontSize(6.5);
  pdf.setTextColor(...PURPLE);
  pdf.setFont("helvetica", "bold");
  pdf.text("Document ID", 40, y);
  pdf.setTextColor(51, 51, 51);
  pdf.setFont("helvetica", "normal");
  pdf.text(" • " + (doc.id || "pending"), 80, y);

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.rect(W - 90, y - 7, 50, 10);
  pdf.setFontSize(6);
  pdf.setFont("helvetica", "bold");
  pdf.setTextColor(0, 0, 0);
  pdf.text("LOCKED", W - 65, y, { align: "center" });

  y += 10;
  pdf.setFont("courier", "normal");
  pdf.setFontSize(6);
  pdf.setTextColor(...PURPLE);
  pdf.text("SHA-256:", 40, y);
  pdf.setTextColor(0, 0, 0);
  const hashText = (hash || "").replace("SHA256:", "");
  pdf.text(pdf.splitTextToSize(hashText, W - 100), 75, y);

  y += 12;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(6);
  pdf.setTextColor(85, 85, 85);
  pdf.text("Cryptographically locked • ESIGN Act 15 U.S.C. §7001 • NC UETA G.S. 66-317 • HIPAA 45 CFR 164.312(b)", W / 2, y, { align: "center" });

  const safe = (doc.patient_name || "patient").replace(/\s+/g, "_");
  const fileName = "NNHC_" + (doc.doc_type || "RN_SUPERVISORY") + "_" + safe + "_" + Date.now() + ".pdf";
  // Open the generated PDF in a new browser tab on every viewport size rather
  // than embedding it in an <iframe>. iOS Safari renders inline PDF iframes
  // unreliably, and an embedded preview can show a stale (previous patient's)
  // PDF from cache — a HIPAA exposure risk. A new tab is more defensible.
  // Falls back to a direct download if the browser blocks the popup.
  try {
    const url = URL.createObjectURL(pdf.output("blob"));
    const win = window.open(url, "_blank");
    if (!win) pdf.save(fileName);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch {
    pdf.save(fileName);
  }
}

function CareVoyantUploadModal({ doc, user, onClose, onConfirmed }) {
  const [cvFileName, setCvFileName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const confirm = async () => {
    setSubmitting(true);
    setErr(null);
    const { error } = await supabase.from("clinical_docs")
      .update({
        status: "uploaded_to_cv",
        updated_at: new Date().toISOString(),
        form_data: { ...(doc.form_data || doc.data || {}), cv_upload_note: cvFileName },
      })
      .eq("id", doc.id);
    if (error) {
      setSubmitting(false);
      setErr(error.message || "Could not update document.");
      return;
    }
    await supabase.from("audit_logs").insert({
      user_id: user.id,
      username: user.username,
      action: "UPLOADED_TO_CAREVOYANT",
      details: {
        doc_id: doc.id,
        cv_reference: cvFileName,
        patient_name: doc.patient_name || doc.patient?.name || null,
      },
      ip_address: "browser",
    });
    setSubmitting(false);
    onConfirmed(cvFileName);
  };

  return (
    <ModalShell title="Confirm CareVoyant Upload" onClose={onClose} maxWidth={480}>
      <p style={{ fontSize: 13, color: BRAND.gray800, lineHeight: 1.5, margin: "0 0 14px" }}>
        Confirm you have uploaded the PDF to the patient's chart in CareVoyant
        and printed a copy for the paper chart.
      </p>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600,
        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
      }}>
        CareVoyant document/visit ID or filename (optional)
      </label>
      <input
        type="text"
        value={cvFileName}
        onChange={(e) => setCvFileName(e.target.value)}
        placeholder="e.g. visit#12345 or NNHC_RN_SUP_DOE.pdf"
        style={textInput}
      />
      {err && (
        <div style={{
          marginTop: 12, padding: "10px 12px",
          background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
          color: BRAND.error, fontSize: 13, borderRadius: 8,
        }}>{err}</div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
        <button onClick={onClose} style={btnSecondary} disabled={submitting}>Cancel</button>
        <button onClick={confirm} style={btnPrimary} disabled={submitting}>
          {submitting ? "Saving…" : "Confirm Upload"}
        </button>
      </div>
    </ModalShell>
  );
}

function VoidDocumentModal({ doc, user, onClose, onVoided }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const tooShort = reason.trim().length < 20;

  const confirm = async () => {
    if (tooShort) {
      setErr("Reason must be at least 20 characters.");
      return;
    }
    setSubmitting(true);
    setErr(null);
    const before = {
      status: doc.status || "submitted",
      voided_at: doc.voided_at || null,
      voided_by: doc.voided_by || null,
      void_reason: doc.void_reason || null,
    };
    const after = {
      status: "voided",
      voided_at: new Date().toISOString(),
      voided_by: user.username,
      void_reason: reason.trim(),
    };
    const { error } = await supabase.from("clinical_docs")
      .update(after)
      .eq("id", doc.id);
    if (error) {
      setSubmitting(false);
      setErr(error.message || "Could not void document.");
      return;
    }
    await supabase.from("clinical_audit_log").insert({
      doc_id: doc.id,
      user_id: user.id,
      user_name: user.name,
      action: "DOCUMENT_VOIDED",
      device_info: navigator.userAgent,
      changed_fields: {
        before,
        after,
        patient_name: doc.patient_name || doc.patient?.name || null,
      },
    });
    setSubmitting(false);
    onVoided(after);
  };

  return (
    <ModalShell title="Void This Document" onClose={onClose} maxWidth={520}>
      <div style={{
        padding: "10px 12px", background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
        color: BRAND.error, fontSize: 13, borderRadius: 8, marginBottom: 14, lineHeight: 1.5,
      }}>
        Voiding is permanent for the visible record. The document will display a
        red VOIDED watermark on re-download and will be hidden from the patient's
        Recent Visits panel.
      </div>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600,
        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
      }}>
        Void Reason (required, minimum 20 characters)
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={4}
        style={{ ...textInput, minHeight: 90, resize: "vertical" }}
        placeholder="Explain why this document is being voided…"
      />
      <div style={{ fontSize: 11, color: tooShort ? BRAND.error : BRAND.gray600, marginTop: 4 }}>
        {reason.trim().length} / 20 characters
      </div>
      {err && (
        <div style={{
          marginTop: 12, padding: "10px 12px",
          background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
          color: BRAND.error, fontSize: 13, borderRadius: 8,
        }}>{err}</div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
        <button onClick={onClose} style={btnSecondary} disabled={submitting}>Cancel</button>
        <button
          onClick={confirm}
          disabled={submitting || tooShort}
          style={{
            ...btnPrimary, background: BRAND.error, boxShadow: "none",
            opacity: tooShort ? 0.55 : 1,
          }}
        >
          {submitting ? "Voiding…" : "Void Document"}
        </button>
      </div>
    </ModalShell>
  );
}

function ReassignPatientModal({ doc, user, onClose, onReassigned }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const tooShort = reason.trim().length < 20;

  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    if (!q) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from("patients")
        .select("id, name, dob, policy_number")
        .or(`name.ilike.%${q}%,policy_number.ilike.%${q}%`)
        .eq("status", "active")
        .order("name")
        .limit(20);
      if (!cancelled) {
        setResults(data || []);
        setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const confirm = async () => {
    if (!selected) { setErr("Select a patient first."); return; }
    if (tooShort) { setErr("Reason must be at least 20 characters."); return; }
    setSubmitting(true);
    setErr(null);
    const before = {
      patient_id: doc.patient_id || doc.patient?.id || null,
      patient_name: doc.patient_name || doc.patient?.name || null,
      patient_dob: doc.patient_dob || doc.patient?.dob || null,
      patient_mid: doc.patient_mid || doc.patient?.mid || null,
      status: doc.status || "submitted",
    };
    const after = {
      patient_id: selected.id,
      patient_name: selected.name,
      patient_dob: selected.dob,
      patient_mid: selected.policy_number,
      original_patient_id: before.patient_id,
      original_patient_name: before.patient_name,
      reassigned_at: new Date().toISOString(),
      reassigned_by: user.username,
      reassignment_reason: reason.trim(),
      status: "corrected",
    };
    const { error } = await supabase.from("clinical_docs")
      .update(after)
      .eq("id", doc.id);
    if (error) {
      setSubmitting(false);
      setErr(error.message || "Could not reassign document.");
      return;
    }
    await supabase.from("clinical_audit_log").insert({
      doc_id: doc.id,
      user_id: user.id,
      user_name: user.name,
      action: "DOCUMENT_REASSIGNED",
      device_info: navigator.userAgent,
      changed_fields: {
        before,
        after,
        from_patient_name: before.patient_name,
        to_patient_name: selected.name,
      },
    });
    setSubmitting(false);
    onReassigned(after);
  };

  return (
    <ModalShell title="Reassign to Different Patient" onClose={onClose} maxWidth={560}>
      <div style={{
        padding: "10px 12px", background: BRAND.offWhite, border: `1px solid ${BRAND.gray200}`,
        color: BRAND.gray800, fontSize: 13, borderRadius: 8, marginBottom: 14,
      }}>
        Currently assigned to: <strong>{doc.patient_name || doc.patient?.name}</strong>
      </div>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600,
        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
      }}>
        Search patients by name or MID
      </label>
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
        placeholder="Start typing a name or MID…"
        style={textInput}
      />
      <div style={{
        marginTop: 10, maxHeight: 180, overflowY: "auto",
        border: `1px solid ${BRAND.gray200}`, borderRadius: 8,
      }}>
        {searching && (
          <div style={{ padding: 10, fontSize: 12, color: BRAND.gray600 }}>Searching…</div>
        )}
        {!searching && results.length === 0 && query.trim() && (
          <div style={{ padding: 10, fontSize: 12, color: BRAND.gray600 }}>No matches.</div>
        )}
        {!searching && results.map((p) => {
          const isSel = selected?.id === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 12px", background: isSel ? `${BRAND.purple}10` : BRAND.white,
                border: "none", borderBottom: `1px solid ${BRAND.gray100}`,
                fontFamily: "DM Sans, sans-serif", cursor: "pointer", fontSize: 13,
                color: BRAND.gray800,
              }}
            >
              <strong>{p.name}</strong>
              <div style={{ fontSize: 11, color: BRAND.gray600 }}>
                DOB: {p.dob} · MID: {p.policy_number}
              </div>
            </button>
          );
        })}
      </div>

      <label style={{
        display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600,
        textTransform: "uppercase", letterSpacing: "0.06em", margin: "16px 0 6px",
      }}>
        Reassignment Reason (required, minimum 20 characters)
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        style={{ ...textInput, minHeight: 70, resize: "vertical" }}
        placeholder="Explain why this document is being reassigned…"
      />
      <div style={{ fontSize: 11, color: tooShort ? BRAND.error : BRAND.gray600, marginTop: 4 }}>
        {reason.trim().length} / 20 characters
      </div>

      {err && (
        <div style={{
          marginTop: 12, padding: "10px 12px",
          background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
          color: BRAND.error, fontSize: 13, borderRadius: 8,
        }}>{err}</div>
      )}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
        <button onClick={onClose} style={btnSecondary} disabled={submitting}>Cancel</button>
        <button
          onClick={confirm}
          disabled={submitting || !selected || tooShort}
          style={{
            ...btnPrimary, background: BRAND.warning, boxShadow: "none",
            opacity: (!selected || tooShort) ? 0.55 : 1,
          }}
        >
          {submitting ? "Reassigning…" : "Reassign Document"}
        </button>
      </div>
    </ModalShell>
  );
}

function SubmittedScreen({ doc, onNew, onDashboard, user }) {
  const docId = doc.id || `NNHC-${(doc.doc_type || doc.type || "DOC").slice(0, 3)}-${Date.now().toString(36).toUpperCase()}`;
  // Hash is authoritative from the server-side insert (includes a timestamp in
  // the canonical payload). Never recompute here — a recomputation would use a
  // different timestamp and produce a different digest, breaking tamper-evidence.
  const hash = doc.doc_hash || doc.hash || "SHA256:unavailable";

  const [liveDoc, setLiveDoc] = useState(doc);
  const [showCvModal, setShowCvModal] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [reassignSuccess, setReassignSuccess] = useState(false);

  const isAdmin = (user?.role || "").toLowerCase().includes("administrator");
  const cvUploaded = liveDoc.status === "uploaded_to_cv";
  const isVoided = liveDoc.status === "voided";

  const patientName = liveDoc.patient_name || liveDoc.patient?.name || doc.patient?.name;
  const docType = (liveDoc.doc_type || doc.type || "").replace(/_/g, " ");
  const statusLabel = isVoided
    ? "🚫 VOIDED"
    : liveDoc.status === "corrected"
    ? "↔ REASSIGNED"
    : cvUploaded
    ? "✅ Uploaded to CareVoyant"
    : "🔒 LOCKED — Tamper-Evident";

  return (
    <div style={{ textAlign: "center", padding: "40px 20px", fontFamily: "DM Sans, sans-serif" }}>
      <div style={{
        width: 80, height: 80, borderRadius: "50%", background: `linear-gradient(135deg, ${BRAND.success}, #34D399)`,
        display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
        fontSize: 36, boxShadow: "0 4px 20px rgba(5,150,105,0.3)"
      }}>✓</div>
      <h2 style={{ color: BRAND.gray800, marginBottom: 8, fontFamily: "Comfortaa, sans-serif" }}>Document Submitted</h2>
      <p style={{ color: BRAND.gray600, marginBottom: 30, fontSize: 15 }}>
        Successfully signed, locked, and queued for PDF generation
      </p>

      <div style={{
        background: BRAND.offWhite, border: `1.5px solid ${BRAND.gray200}`, borderRadius: 12,
        padding: 24, maxWidth: 520, margin: "0 auto 30px", textAlign: "left"
      }}>
        {[
          ["Document ID", docId],
          ["Document Type", docType],
          ["Patient", patientName],
          ["Submitted", new Date().toLocaleString()],
          ["Integrity Hash", hash],
          ["Status", statusLabel],
          ["QA Queue", "Pending DON Review"],
        ].map(([k, v]) => {
          const isHash = k === "Integrity Hash";
          return (
            <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0",
              borderBottom: `1px solid ${BRAND.gray200}`, fontSize: 13, gap: 16 }}>
              <span style={isHash
                ? { fontWeight: 600, color: BRAND.gray600, whiteSpace: "nowrap", verticalAlign: "top", paddingRight: 16 }
                : { fontWeight: 600, color: BRAND.gray600 }}>{k}</span>
              <span style={isHash
                ? { color: BRAND.gray800, fontFamily: "monospace", fontSize: 10, wordBreak: "break-all", textAlign: "right" }
                : { color: BRAND.gray800, fontFamily: typeof v === "string" && v.startsWith("SHA") ? "monospace" : undefined,
                    fontSize: typeof v === "string" && v.startsWith("SHA") ? 11 : 13 }}>{v}</span>
            </div>
          );
        })}
      </div>

      {reassignSuccess && (
        <div style={{
          maxWidth: 520, margin: "0 auto 20px", padding: "12px 16px",
          background: "#FEF3C7", border: `1px solid ${BRAND.warning}`,
          color: BRAND.gray800, fontSize: 13, borderRadius: 8, textAlign: "left", lineHeight: 1.5,
        }}>
          This document has been reassigned. An addendum has been recorded in the
          audit log. Please update CareVoyant accordingly.
        </div>
      )}

      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <button style={btnSecondary} onClick={onNew}>+ New Document</button>
        <button style={btnSecondary} onClick={() => window.print()}>🖨 Print Copy</button>
        <button style={btnGold} onClick={() => generatePDF(liveDoc, hash, user)}>
          ⬇ Download PDF
        </button>
        <button style={btnPrimary} onClick={onDashboard}>← Dashboard</button>
      </div>

      <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
        {cvUploaded ? (
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            color: BRAND.success, background: "#F0FDF4", border: `1.5px solid ${BRAND.success}`,
          }}>
            ✓ Uploaded to CareVoyant
          </span>
        ) : (
          <button
            onClick={() => setShowCvModal(true)}
            disabled={isVoided}
            style={{ ...btnSecondary, opacity: isVoided ? 0.5 : 1 }}
          >
            📤 Mark as Uploaded to CareVoyant
          </button>
        )}
      </div>

      {isAdmin && (
        <div style={{
          maxWidth: 520, margin: "32px auto 0", border: `1px solid ${BRAND.error}40`,
          borderRadius: 10, background: "#FEF2F2",
        }}>
          <button
            onClick={() => setAdminOpen((v) => !v)}
            style={{
              width: "100%", padding: "12px 16px", background: "transparent",
              border: "none", display: "flex", justifyContent: "space-between",
              alignItems: "center", cursor: "pointer", color: BRAND.error,
              fontWeight: 700, fontSize: 13, fontFamily: "Comfortaa, sans-serif",
            }}
          >
            <span>⚠ Admin Actions</span>
            <span style={{ color: BRAND.error, fontSize: 16 }}>
              {adminOpen ? "▾" : "▸"}
            </span>
          </button>
          {adminOpen && (
            <div style={{
              padding: "0 16px 16px", display: "flex", flexDirection: "column",
              gap: 10, alignItems: "stretch",
            }}>
              <button
                onClick={() => setShowVoidModal(true)}
                disabled={isVoided}
                style={{
                  ...btnSecondary, color: BRAND.error, borderColor: BRAND.error,
                  opacity: isVoided ? 0.5 : 1, textAlign: "left",
                }}
              >
                ⚠ Void This Document
              </button>
              <button
                onClick={() => setShowReassignModal(true)}
                disabled={isVoided}
                style={{
                  ...btnSecondary, color: BRAND.warning, borderColor: BRAND.warning,
                  opacity: isVoided ? 0.5 : 1, textAlign: "left",
                }}
              >
                ↔ Reassign to Different Patient
              </button>
            </div>
          )}
        </div>
      )}

      {showCvModal && (
        <CareVoyantUploadModal
          doc={liveDoc}
          user={user}
          onClose={() => setShowCvModal(false)}
          onConfirmed={(note) => {
            setLiveDoc((d) => ({
              ...d,
              status: "uploaded_to_cv",
              form_data: { ...(d.form_data || d.data || {}), cv_upload_note: note },
            }));
            setShowCvModal(false);
          }}
        />
      )}
      {showVoidModal && (
        <VoidDocumentModal
          doc={liveDoc}
          user={user}
          onClose={() => setShowVoidModal(false)}
          onVoided={(after) => {
            setLiveDoc((d) => ({ ...d, ...after }));
            setShowVoidModal(false);
          }}
        />
      )}
      {showReassignModal && (
        <ReassignPatientModal
          doc={liveDoc}
          user={user}
          onClose={() => setShowReassignModal(false)}
          onReassigned={(after) => {
            setLiveDoc((d) => ({ ...d, ...after }));
            setShowReassignModal(false);
            setReassignSuccess(true);
          }}
        />
      )}
    </div>
  );
}

// ─── Add New Patient Modal ───────────────────────────────────────────────────
function AddPatientModal({ user, onClose, onCreated }) {
  const isNarrow = useIsNarrow();
  const [form, setForm] = useState({
    name: "",
    dob: "",
    policy_number: "",
    phone: "",
    gender: "",
    language: "English",
    county: "MECKLENBURG",
    address: "",
    emergency_contact: "",
    emergency_phone: "",
    payer: "NC Medicaid",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const NNHC_NPI = "1457693301";

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const requiredMissing = !form.name.trim() || !form.dob || !form.policy_number.trim() || !form.phone.trim();

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (requiredMissing) {
      setErr("Please fill all required fields (marked with *).");
      return;
    }
    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      dob: form.dob,
      policy_number: form.policy_number.trim(),
      phone: form.phone.trim(),
      gender: form.gender || null,
      language: form.language,
      county: form.county,
      address: form.address.trim() || null,
      emergency_contact: form.emergency_contact.trim() || null,
      emergency_phone: form.emergency_phone.trim() || null,
      payer: form.payer,
      provider_id: NNHC_NPI,
      notes: form.notes.trim() || null,
      status: "active",
    };
    const { data: newPatient, error } = await supabase
      .from("patients")
      .insert(payload)
      .select()
      .single();
    if (error) {
      setSubmitting(false);
      setErr(error.message || "Could not save patient.");
      return;
    }

    await supabase.from("audit_logs").insert({
      user_id: user.id,
      username: user.username,
      action: "PATIENT_ADDED",
      details: {
        patient_id: newPatient.id,
        patient_name: newPatient.name,
        mid: newPatient.policy_number,
      },
      ip_address: "browser",
    });

    setSubmitting(false);
    onCreated(newPatient);
  };

  const reqStar = <span style={{ color: BRAND.error, marginLeft: 3 }}>*</span>;
  const lbl = {
    display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600,
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={modalOverlayStyle(isNarrow)}
    >
      <form onSubmit={submit} className={"form-rgrid-scope" + (isNarrow ? " min-h-screen-dvh" : "")} style={modalCardStyle(isNarrow, 680)}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontFamily: "Comfortaa, sans-serif", color: BRAND.purple, fontWeight: 700 }}>
            Add New Patient
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            background: "transparent", border: "none", fontSize: 22, cursor: "pointer", color: BRAND.gray600,
            width: 44, height: 44, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1,
          }}>×</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>Patient Name {reqStar}</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => set("name", e.target.value.toUpperCase())}
              placeholder="LASTNAME, FIRSTNAME"
              style={textInput}
            />
          </div>
          <div>
            <label style={lbl}>Date of Birth {reqStar}</label>
            <input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} style={textInput} />
          </div>
          <div>
            <label style={lbl}>MID# / Policy Number {reqStar}</label>
            <input type="text" value={form.policy_number} onChange={(e) => set("policy_number", e.target.value)} style={textInput} />
          </div>
          <div>
            <label style={lbl}>Phone {reqStar}</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone", formatPhone(e.target.value))}
              placeholder="(XXX) XXX-XXXX"
              style={textInput}
            />
          </div>
          <div>
            <label style={lbl}>Gender</label>
            <select value={form.gender} onChange={(e) => set("gender", e.target.value)} style={textInput}>
              <option value="">Select…</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
              <option>Prefer not to say</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Language</label>
            <select value={form.language} onChange={(e) => set("language", e.target.value)} style={textInput}>
              <option>English</option>
              <option>Spanish</option>
              <option>Vietnamese</option>
              <option>Haitian Creole</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label style={lbl}>County</label>
            <select value={form.county} onChange={(e) => set("county", e.target.value)} style={textInput}>
              {NC_COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>Address</label>
            <textarea
              value={form.address}
              onChange={(e) => set("address", e.target.value)}
              style={{ ...textInput, minHeight: 60, resize: "vertical" }}
            />
          </div>
          <div>
            <label style={lbl}>Emergency Contact</label>
            <input type="text" value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} style={textInput} />
          </div>
          <div>
            <label style={lbl}>Emergency Phone</label>
            <input
              type="tel"
              value={form.emergency_phone}
              onChange={(e) => set("emergency_phone", formatPhone(e.target.value))}
              placeholder="(XXX) XXX-XXXX"
              style={textInput}
            />
          </div>
          <div>
            <label style={lbl}>Payer</label>
            <select value={form.payer} onChange={(e) => set("payer", e.target.value)} style={textInput}>
              <option>NC Medicaid</option>
              <option>SC Medicaid</option>
              <option>VA Community Care Network</option>
              <option>Private Pay</option>
              <option>Other</option>
            </select>
          </div>
          <div>
            <label style={lbl}>Provider NPI</label>
            <input type="text" value={NNHC_NPI} readOnly style={{ ...textInput, background: BRAND.gray100, cursor: "not-allowed" }} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={lbl}>Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Include ICD-10 codes, allergies, special instructions"
              style={{ ...textInput, minHeight: 80, resize: "vertical" }}
            />
          </div>
        </div>

        {err && (
          <div style={{
            marginTop: 14, padding: "10px 12px",
            background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
            color: BRAND.error, fontSize: 13, borderRadius: 8,
          }}>
            {err}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={submitting}>Cancel</button>
          <button type="submit" style={btnGold} disabled={submitting}>
            {submitting ? "Saving…" : "Save Patient"}
          </button>
        </div>
      </form>
    </div>
  );
}


// ─── Patient helpers ─────────────────────────────────────────────────────────
// Derive ICD-10 codes from free-text notes so existing form UI keeps working.
const ICD10_RE = /\b[A-TV-Z]\d{2}(?:\.[A-Z0-9]{1,4})?\b/g;
function mapPatient(p) {
  if (!p) return p;
  const codes = Array.from(new Set((p.notes || "").match(ICD10_RE) || [])).slice(0, 6);
  return { ...p, mid: p.policy_number || p.mid || "", diagnoses: codes };
}

// NC 100 counties — priority counties (NNHC service area) first, then alphabetical.
const NC_PRIORITY = ["MECKLENBURG", "ANSON", "CABARRUS", "CASWELL", "EDGECOMBE", "HALIFAX", "RICHMOND", "ROBESON", "UNION"];
const NC_OTHER = [
  "ALAMANCE","ALEXANDER","ALLEGHANY","ASHE","AVERY","BEAUFORT","BERTIE","BLADEN","BRUNSWICK","BUNCOMBE",
  "BURKE","CALDWELL","CAMDEN","CARTERET","CATAWBA","CHATHAM","CHEROKEE","CHOWAN","CLAY","CLEVELAND",
  "COLUMBUS","CRAVEN","CUMBERLAND","CURRITUCK","DARE","DAVIDSON","DAVIE","DUPLIN","DURHAM","FORSYTH",
  "FRANKLIN","GASTON","GATES","GRAHAM","GRANVILLE","GREENE","GUILFORD","HARNETT","HAYWOOD","HENDERSON",
  "HERTFORD","HOKE","HYDE","IREDELL","JACKSON","JOHNSTON","JONES","LEE","LENOIR","LINCOLN",
  "MACON","MADISON","MARTIN","MCDOWELL","MITCHELL","MONTGOMERY","MOORE","NASH","NEW HANOVER","NORTHAMPTON",
  "ONSLOW","ORANGE","PAMLICO","PASQUOTANK","PENDER","PERQUIMANS","PERSON","PITT","POLK","RANDOLPH",
  "ROCKINGHAM","ROWAN","RUTHERFORD","SAMPSON","SCOTLAND","STANLY","STOKES","SURRY","SWAIN","TRANSYLVANIA",
  "TYRRELL","VANCE","WAKE","WARREN","WASHINGTON","WATAUGA","WAYNE","WILKES","WILSON","YADKIN","YANCEY"
];
const NC_COUNTIES = [...NC_PRIORITY, ...NC_OTHER];

function formatPhone(raw) {
  const d = (raw || "").replace(/\D/g, "").slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ─── KPI Modals (Fix 1) ──────────────────────────────────────────────────────

function ActivePatientsModal({ onClose }) {
  const isNarrow = useIsNarrow();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data: pts, error } = await supabase
        .from("patients").select("*").eq("status", "active").order("name");
      if (error || !pts) { if (!cancelled) { setRows([]); setLoading(false); } return; }

      // For each patient, look up their most recent doc in parallel.
      const withDocs = await Promise.all(pts.map(async (p) => {
        const { data: docs } = await supabase
          .from("clinical_docs")
          .select("doc_type, status, clinician_name, created_at")
          .eq("patient_id", p.id)
          .order("created_at", { ascending: false })
          .limit(1);
        return { ...p, lastDoc: (docs || [])[0] || null };
      }));
      if (!cancelled) { setRows(withDocs); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const Chip = ({ status }) => {
    const chip = stageChip[status] || { bg: BRAND.gray100, fg: BRAND.gray600, label: status || "—" };
    return (
      <span style={{
        display: "inline-block", padding: "3px 10px", borderRadius: 12,
        background: chip.bg, color: chip.fg, fontSize: 11, fontWeight: 700,
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>{chip.label}</span>
    );
  };

  return (
    <ModalShell title="Active Patients" onClose={onClose} maxWidth={900}>
      {loading && <p style={{ color: BRAND.gray600, fontSize: 13 }}>Loading…</p>}
      {!loading && rows.length === 0 && (
        <div style={{
          padding: "30px 20px", textAlign: "center", background: BRAND.offWhite,
          border: `1.5px dashed ${BRAND.gray200}`, borderRadius: 10,
          color: BRAND.gray600, fontSize: 13,
        }}>
          No active patients. Click <strong>+ Add New Patient</strong> on the dashboard.
        </div>
      )}
      {!loading && rows.length > 0 && isNarrow && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map(r => {
            const d = r.lastDoc;
            const lastVisitDate = d ? new Date(d.created_at).toLocaleDateString() : "—";
            return (
              <div key={r.id} style={phiCardStyle}>
                <div style={{ fontWeight: 700, fontSize: 15, color: BRAND.gray800 }}>{r.name}</div>
                <FieldPair label="DOB" value={r.dob || "—"} />
                <FieldPair label="MID #" value={r.policy_number || "—"} mono />
                <FieldPair label="Last Visit" value={lastVisitDate} />
                <FieldPair label="Assigned Clinician" value={d?.clinician_name || "—"} />
                <div style={{ marginTop: 4 }}>{d ? <Chip status={d.status} /> : <Chip status="draft" />}</div>
              </div>
            );
          })}
        </div>
      )}
      {!loading && rows.length > 0 && !isNarrow && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: BRAND.purple, color: BRAND.white }}>
                {["Name", "DOB", "MID #", "Last Visit", "Assigned Clinician", "Process Stage"].map(h => (
                  <th key={h} style={{
                    padding: "8px 10px", textAlign: "left", fontSize: 11,
                    textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const d = r.lastDoc;
                const lastVisitDate = d ? new Date(d.created_at).toLocaleDateString() : "—";
                return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${BRAND.gray100}` }}>
                    <td style={{ padding: "10px", fontWeight: 600, color: BRAND.gray800 }}>{r.name}</td>
                    <td style={{ padding: "10px", color: BRAND.gray600 }}>{r.dob || "—"}</td>
                    <td style={{ padding: "10px", color: BRAND.gray600, fontFamily: "monospace" }}>{r.policy_number || "—"}</td>
                    <td style={{ padding: "10px", color: BRAND.gray600 }}>{lastVisitDate}</td>
                    <td style={{ padding: "10px", color: BRAND.gray600 }}>{d?.clinician_name || "—"}</td>
                    <td style={{ padding: "10px" }}>{d ? <Chip status={d.status} /> : <Chip status="draft" />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  );
}

function PatientVisitsModal({ patient, onClose }) {
  const isNarrow = useIsNarrow();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patient?.id) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("clinical_docs")
        .select("id, doc_type, clinician_name, status, doc_hash, created_at")
        .eq("patient_id", patient.id)
        .order("created_at", { ascending: false })
        .limit(20);
      if (!cancelled) { setRows(data || []); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [patient]);

  return (
    <ModalShell title={patient ? `${patient.name}'s Visits` : "Patient Visits"} onClose={onClose} maxWidth={820}>
      {!patient && (
        <div style={{
          padding: "30px 20px", textAlign: "center", background: BRAND.offWhite,
          border: `1.5px dashed ${BRAND.gray200}`, borderRadius: 10,
          color: BRAND.gray600, fontSize: 13,
        }}>
          Select a patient from the dashboard to see their visits.
        </div>
      )}
      {patient && loading && <p style={{ color: BRAND.gray600, fontSize: 13 }}>Loading…</p>}
      {patient && !loading && rows.length === 0 && (
        <p style={{ color: BRAND.gray600, fontSize: 13 }}>No documents recorded for this patient yet.</p>
      )}
      {patient && !loading && rows.length > 0 && isNarrow && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map(d => (
            <div key={d.id} style={phiCardStyle}>
              <div style={{ fontWeight: 700, fontSize: 14, color: BRAND.gray800 }}>{(d.doc_type || "").replace(/_/g, " ")}</div>
              <FieldPair label="Date" value={new Date(d.created_at).toLocaleString()} />
              <FieldPair label="Clinician" value={d.clinician_name || "—"} />
              <FieldPair label="Status" value={d.status || "—"} />
              <FieldPair label="Hash" value={`${(d.doc_hash || "").replace("SHA256:", "").slice(0, 12)}…`} mono />
            </div>
          ))}
        </div>
      )}
      {patient && !loading && rows.length > 0 && !isNarrow && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: BRAND.purple, color: BRAND.white }}>
                {["Date", "Doc Type", "Clinician", "Status", "Hash"].map(h => (
                  <th key={h} style={{
                    padding: "8px 10px", textAlign: "left", fontSize: 11,
                    textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700,
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(d => (
                <tr key={d.id} style={{ borderBottom: `1px solid ${BRAND.gray100}` }}>
                  <td style={{ padding: "8px 10px", color: BRAND.gray800 }}>
                    {new Date(d.created_at).toLocaleString()}
                  </td>
                  <td style={{ padding: "8px 10px", color: BRAND.gray800 }}>
                    {(d.doc_type || "").replace(/_/g, " ")}
                  </td>
                  <td style={{ padding: "8px 10px", color: BRAND.gray600 }}>{d.clinician_name || "—"}</td>
                  <td style={{ padding: "8px 10px", color: BRAND.gray600 }}>{d.status || "—"}</td>
                  <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 10, color: BRAND.gray600 }}>
                    {(d.doc_hash || "").replace("SHA256:", "").slice(0, 12)}…
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ModalShell>
  );
}

function ProfileModal({ user, onLogout, onClose }) {
  let sessionExpires = "—";
  try {
    const raw = localStorage.getItem("nnhc_clinical_session");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.expires) sessionExpires = new Date(parsed.expires).toLocaleString();
    }
  } catch { /* ignore */ }

  const roleUpper = (user.role || "").toUpperCase();
  const isRN = roleUpper.includes("RN") || roleUpper.includes("NURSING");
  const licenseLine = user.license_number
    ? `Verified: #${user.license_number}`
    : "Pending NC BON verification";

  const Row = ({ label, value }) => (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0",
      borderBottom: `1px solid ${BRAND.gray100}`, fontSize: 13 }}>
      <span style={{ color: BRAND.gray600, fontWeight: 600 }}>{label}</span>
      <span style={{ color: BRAND.gray800, textAlign: "right" }}>{value || "—"}</span>
    </div>
  );

  return (
    <ModalShell title="Profile" onClose={onClose} maxWidth={480}>
      <Row label="Name" value={user.name} />
      <Row label="Role" value={user.role} />
      <Row label="Email" value={user.email} />
      <Row label="Last Login" value={user.last_login ? new Date(user.last_login).toLocaleString() : new Date().toLocaleString()} />
      <Row label="Session Expires" value={sessionExpires} />
      {isRN && <Row label="License Status" value={licenseLine} />}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
        <button onClick={onClose} style={btnSecondary}>Close</button>
        <button onClick={onLogout} style={{ ...btnPrimary, background: BRAND.error, boxShadow: "none" }}>
          Sign Out
        </button>
      </div>
    </ModalShell>
  );
}

function QAQueueModal({ onClose }) {
  return (
    <ModalShell title="QA Queue" onClose={onClose} maxWidth={520}>
      <div style={{
        background: BRAND.offWhite, border: `1.5px solid ${BRAND.gray200}`,
        borderRadius: 10, padding: 20, fontSize: 13, color: BRAND.gray800, lineHeight: 1.6,
      }}>
        QA review workflow coming in Sprint 4. For now, you can review documents
        by opening each patient's history.
      </div>
    </ModalShell>
  );
}

// ─── Hamburger NNHC-Apps Drawer (Fix 2) ──────────────────────────────────────

function AppsDrawer({ user, onClose, onLogout }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const Section = ({ children }) => (
    <div style={{ padding: "6px 0", borderBottom: `1px solid ${BRAND.gray100}` }}>{children}</div>
  );

  const Link = ({ icon, label, href, current, action }) => {
    const rowStyle = {
      display: "flex", alignItems: "center", gap: 10,
      padding: "10px 18px", fontSize: 14, color: current ? BRAND.purple : BRAND.gray800,
      background: current ? `${BRAND.purple}0E` : "transparent",
      fontWeight: current ? 700 : 500, cursor: current ? "default" : "pointer",
      borderLeft: current ? `4px solid ${BRAND.purple}` : "4px solid transparent",
      textDecoration: "none", fontFamily: "DM Sans, sans-serif",
    };
    if (action) {
      return (
        <button onClick={() => { action(); onClose(); }} style={{
          ...rowStyle, border: "none", width: "100%", textAlign: "left",
        }}>
          <span style={{ fontSize: 16 }}>{icon}</span> {label}
        </button>
      );
    }
    return (
      <a href={href} target={current ? undefined : "_blank"} rel="noopener noreferrer"
        onClick={() => { if (!current) onClose(); }} style={rowStyle}>
        <span style={{ fontSize: 16 }}>{icon}</span> {label}
      </a>
    );
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 8, 30, 0.35)",
        zIndex: 1100, display: "flex",
      }}
    >
      <div style={{
        width: 290, maxWidth: "85vw", height: "100%", background: BRAND.white,
        boxShadow: "0 24px 80px rgba(0,0,0,0.35)", overflowY: "auto",
        fontFamily: "DM Sans, sans-serif", animation: "slideInLeft 0.2s ease-out",
      }}>
        <div style={{
          padding: "16px 18px", background: `linear-gradient(135deg, ${BRAND.purpleDark}, ${BRAND.purple})`,
          color: BRAND.white, display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span style={{ fontFamily: "Comfortaa, sans-serif", fontWeight: 700, fontSize: 15 }}>
            NNHC Apps
          </span>
          <button onClick={onClose} aria-label="Close menu" style={{
            background: "transparent", border: "none", color: BRAND.white,
            fontSize: 22, cursor: "pointer", padding: 0, lineHeight: 1,
          }}>×</button>
        </div>

        <Section>
          <Link icon="🩺" label="Clinical Portal" current />
          <Link icon="🏢" label="Command Center" href="https://app.nurturingnurses.net" />
          <Link icon="📥" label="Intake Portal" href="https://intake.nurturingnurses.net" />
          <Link icon="✅" label="Quality Assurance" href="https://qa.nurturingnurses.net" />
          <Link icon="📚" label="Inservices" href="https://inservices.nurturingnurses.net" />
        </Section>

        <Section>
          <Link icon="🌐" label="Public Website" href="https://nurturingnurseshc.com" />
          {user?.email && (
            <Link icon="📧" label="Email (Outlook)" href="https://outlook.office.com" />
          )}
          {user?.paychex_employee_id && (
            <Link icon="💼" label="Paychex" href="https://paychex.com" />
          )}
        </Section>

        <Section>
          <Link icon="🚪" label="Sign Out" action={onLogout} />
        </Section>

        <div style={{
          padding: "14px 18px", fontSize: 11, color: BRAND.gray600, lineHeight: 1.5,
          background: BRAND.offWhite, marginTop: 8,
        }}>
          Single Sign-On across subdomains is coming in Sprint 3. For now, each app
          requires a separate login.
        </div>
      </div>
    </div>
  );
}

// ─── Idle auto-logout (Fix 9) — HIPAA 164.312(a)(2)(iii) ─────────────────────

const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const IDLE_WARN_MS    = 14 * 60 * 1000;

function IdleWarningModal({ secondsLeft, onStay, onLogoutNow }) {
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(15, 8, 30, 0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 2000, fontFamily: "DM Sans, sans-serif",
    }}>
      <div style={{
        background: BRAND.white, borderRadius: 16, padding: 28, maxWidth: 420,
        width: "92%", boxShadow: "0 24px 80px rgba(0,0,0,0.35)", textAlign: "center",
      }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>⏱️</div>
        <h2 style={{
          margin: "0 0 8px", fontSize: 18, fontFamily: "Comfortaa, sans-serif",
          color: BRAND.purple, fontWeight: 700,
        }}>Session expiring</h2>
        <p style={{ margin: "0 0 16px", color: BRAND.gray600, fontSize: 13, lineHeight: 1.5 }}>
          You'll be signed out in <strong>{secondsLeft}</strong> seconds due to
          inactivity. HIPAA 45 CFR 164.312(a)(2)(iii) requires automatic session
          termination.
        </p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button onClick={onLogoutNow} style={btnSecondary}>Sign Out Now</button>
          <button onClick={onStay} style={btnPrimary}>Stay Signed In</button>
        </div>
      </div>
    </div>
  );
}

// Lookup table for doc-type icons + display labels. Used by MyDraftsPanel and
// the Recent Visits panel under each patient card. Kept in sync with FORM_TYPES.
const DOC_TYPE_META = {
  RN_SUPERVISORY: { icon: "👁️", label: "RN Supervisory Visit" },
  SHIFT_NOTE:     { icon: "🗒️", label: "Nurse's Shift Note" },
  PLAN_OF_CARE:   { icon: "📋", label: "Plan of Care" },
  TUBE_FEEDING:   { icon: "🍶", label: "Tube Feeding Order Packet" },
  WOUND_CARE:     { icon: "🩹", label: "Wound Care Note" },
  SEIZURE_VNS:    { icon: "⚡", label: "Seizure / VNS Log" },
  IV_INFUSION:    { icon: "💉", label: "IV Infusion Record" },
  VENT_TRACH:     { icon: "🫁", label: "Vent / Trach Care" },
  SKILLED_NURSING:{ icon: "🩺", label: "Skilled Nursing Assessment" },
};

function relativeTime(ts) {
  if (!ts) return "—";
  const diff = Date.now() - new Date(ts).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
  return new Date(ts).toLocaleDateString();
}

// ─── My Drafts Panel ─────────────────────────────────────────────────────────
// Lists in-progress drafts for the current clinician with Resume/Discard.
// Resume hands the full draft row back to the parent via onResume so the
// parent can hydrate the right form. Discard hard-deletes the row.
function MyDraftsPanel({ user, onResume, refreshKey, onChanged }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase
      .from("clinical_docs")
      .select("id, doc_type, patient_name, patient_id, patient_dob, patient_mid, updated_at, form_data")
      .eq("clinician_id", user.id)
      .eq("status", "draft")
      .order("updated_at", { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error) setDrafts(data || []);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user.id, refreshKey]);

  const handleDiscard = async (id) => {
    setBusyId(id);
    const { error } = await supabase.from("clinical_docs").delete().eq("id", id);
    setBusyId(null);
    if (error) {
      alert("Failed to discard draft: " + error.message);
      return;
    }
    setDrafts(d => d.filter(x => x.id !== id));
    setConfirmId(null);
    if (typeof onChanged === "function") onChanged();
  };

  const confirmRow = drafts.find(x => x.id === confirmId);

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{
        fontSize: 15, fontWeight: 700, color: BRAND.gray800,
        marginBottom: 14, fontFamily: "Comfortaa, sans-serif",
      }}>
        📋 My Drafts (in progress)
      </h2>

      <div style={{
        background: BRAND.white, border: `1.5px solid ${BRAND.gray200}`,
        borderRadius: 12, overflow: "hidden",
        boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
      }}>
        {loading ? (
          <p style={{ padding: 24, textAlign: "center", color: BRAND.gray400, fontSize: 13, margin: 0 }}>
            Loading…
          </p>
        ) : drafts.length === 0 ? (
          <p style={{ padding: 24, textAlign: "center", color: BRAND.gray400, fontSize: 13, margin: 0 }}>
            No drafts yet. Use 💾 Save &amp; Continue Later from any form to save your progress.
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {drafts.map(d => {
              const meta = DOC_TYPE_META[d.doc_type] || { icon: "📄", label: (d.doc_type || "").replace(/_/g, " ") };
              return (
                <li key={d.id} style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 18px", borderBottom: `1px solid ${BRAND.gray200}`,
                }}>
                  <span style={{
                    fontSize: 22, width: 40, height: 40, borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: `${BRAND.purple}10`, flexShrink: 0,
                  }}>{meta.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: BRAND.gray800 }}>
                      {d.patient_name || "—"}
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 600, color: BRAND.purple,
                        background: `${BRAND.purple}12`, padding: "2px 7px", borderRadius: 6,
                        textTransform: "uppercase", letterSpacing: "0.04em",
                      }}>{meta.label}</span>
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: BRAND.gray400 }}>
                      Last saved {relativeTime(d.updated_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => onResume(d)}
                    style={{
                      background: `${BRAND.purple}10`, border: `1px solid ${BRAND.purple}30`,
                      color: BRAND.purple, borderRadius: 7, padding: "6px 12px",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "DM Sans, sans-serif", whiteSpace: "nowrap",
                    }}
                  >▶ Resume</button>
                  <button
                    onClick={() => setConfirmId(d.id)}
                    disabled={busyId === d.id}
                    style={{
                      background: BRAND.white, border: `1px solid ${BRAND.error}40`,
                      color: BRAND.error, borderRadius: 7, padding: "6px 12px",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "DM Sans, sans-serif", whiteSpace: "nowrap",
                      opacity: busyId === d.id ? 0.45 : 1,
                    }}
                  >🗑 Discard</button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {confirmRow && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center",
          padding: 20,
        }}>
          <div style={{
            background: BRAND.white, borderRadius: 14, padding: 24,
            maxWidth: 380, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            fontFamily: "DM Sans, sans-serif",
          }}>
            <h3 style={{
              margin: "0 0 8px", fontSize: 16, fontWeight: 700, color: BRAND.gray800,
              fontFamily: "Comfortaa, sans-serif",
            }}>Discard draft?</h3>
            <p style={{ margin: "0 0 18px", fontSize: 13, color: BRAND.gray600, lineHeight: 1.5 }}>
              Discard this draft? This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmId(null)}
                style={{
                  background: BRAND.white, border: `1.5px solid ${BRAND.gray200}`,
                  color: BRAND.gray800, borderRadius: 8, padding: "8px 16px",
                  fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif",
                }}
              >Cancel</button>
              <button
                onClick={() => handleDiscard(confirmRow.id)}
                disabled={busyId === confirmRow.id}
                style={{
                  background: BRAND.error, border: "none", color: BRAND.white,
                  borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600,
                  cursor: "pointer", fontFamily: "DM Sans, sans-serif",
                  opacity: busyId === confirmRow.id ? 0.6 : 1,
                }}
              >Discard</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sprint 5b: Addendum / Amendment helpers ─────────────────────────────────
// Top-level scalar fields from form_data — drives the amendment field picker.
function listScalarFormDataFields(formData) {
  if (!formData || typeof formData !== "object") return [];
  return Object.entries(formData)
    .filter(([k, v]) => {
      if (k.startsWith("_")) return false;
      if (k === "lastStep") return false;
      if (v === null || v === undefined) return true;
      const t = typeof v;
      return t === "string" || t === "number" || t === "boolean";
    })
    .map(([k]) => k);
}

// ─── Sprint 5b: Addendum Modal ────────────────────────────────────────────────
function AddendumModal({ doc, user, onClose, onSaved }) {
  const [content, setContent] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const canSubmit = content.trim().length > 0 && !submitting;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);

    if (!content || content.trim().length < 10) {
      setErr("Addendum content must be at least 10 characters.");
      setSubmitting(false);
      return;
    }

    const reauth = await promptPasswordReauth(supabase, user.username);
    if (!reauth.ok) {
      if (reauth.error !== "Password required") setErr(reauth.error);
      setSubmitting(false);
      return;
    }

    const result = await signAddendum(supabase, {
      docId: doc.id,
      content: content.trim(),
      reauthVerifiedAt: reauth.reauthVerifiedAt,
    });

    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error || "Could not save addendum.");
      return;
    }
    onSaved();
  };

  const meta = DOC_TYPE_META[doc.doc_type] || { label: (doc.doc_type || "").replace(/_/g, " ") };

  return (
    <ModalShell
      title={`Add Addendum to: ${meta.label} for ${doc.patient_name || "—"}`}
      onClose={onClose}
      maxWidth={600}
    >
      <div style={{
        background: BRAND.gray50, border: `1px solid ${BRAND.gray200}`,
        borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: BRAND.gray800,
      }}>
        <div><strong>Original Note</strong></div>
        <div>Patient: {doc.patient_name || "—"}</div>
        <div>Type: {meta.label}</div>
        <div>Clinician: {doc.clinician_name || "—"}</div>
        <div>Submitted: {doc.created_at ? new Date(doc.created_at).toLocaleString() : "—"}</div>
      </div>

      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        Addendum content (additive only — original is preserved) *
      </label>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="Enter additional information to append to this note…"
        style={{ ...textInput, minHeight: 110, resize: "vertical", fontFamily: "DM Sans, sans-serif" }}
      />

      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", letterSpacing: "0.06em", margin: "14px 0 6px" }}>
        Reason (optional)
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Why is this addendum being added?"
        style={{ ...textInput, minHeight: 60, resize: "vertical", fontFamily: "DM Sans, sans-serif" }}
      />

      {err && (
        <div style={{
          marginTop: 12, padding: "10px 12px",
          background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
          color: BRAND.error, fontSize: 13, borderRadius: 8,
        }}>{err}</div>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
        <button onClick={onClose} style={btnSecondary} disabled={submitting}>Cancel</button>
        <button
          onClick={handleSave}
          style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? "pointer" : "not-allowed" }}
          disabled={!canSubmit}
        >
          {submitting ? "Saving…" : "Save Addendum"}
        </button>
      </div>
    </ModalShell>
  );
}

// ─── Sprint 5b: Propose Amendment Modal ───────────────────────────────────────
function ProposeAmendmentModal({ doc, user, onClose, onSaved }) {
  const [fullDoc, setFullDoc] = useState(null);
  const [field, setField] = useState("");
  const [newValue, setNewValue] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from("clinical_docs")
      .select("id, doc_type, patient_name, clinician_name, created_at, form_data, amendments")
      .eq("id", doc.id)
      .single()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setErr(error?.message || "Could not load document.");
          return;
        }
        setFullDoc(data);
      });
    return () => { cancelled = true; };
  }, [doc.id]);

  const fields = useMemo(
    () => listScalarFormDataFields(fullDoc?.form_data),
    [fullDoc]
  );
  const currentValue = field && fullDoc ? fullDoc.form_data?.[field] : null;

  const canSubmit =
    !!fullDoc &&
    !!field &&
    newValue.trim().length > 0 &&
    reason.trim().length > 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setErr(null);

    if (!field) {
      setErr("Please select a field to amend.");
      setSubmitting(false);
      return;
    }
    if (!reason || reason.trim().length < 10) {
      setErr("Amendment reason must be at least 10 characters.");
      setSubmitting(false);
      return;
    }

    const reauth = await promptPasswordReauth(supabase, user.username);
    if (!reauth.ok) {
      if (reauth.error !== "Password required") setErr(reauth.error);
      setSubmitting(false);
      return;
    }

    const result = await proposeAmendment(supabase, {
      docId: doc.id,
      fieldPath: field,
      oldValue: currentValue == null ? null : String(currentValue),
      newValue,
      reason: reason.trim(),
      reauthVerifiedAt: reauth.reauthVerifiedAt,
    });

    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error || "Could not submit amendment.");
      return;
    }
    onSaved();
  };

  const meta = DOC_TYPE_META[doc.doc_type] || { label: (doc.doc_type || "").replace(/_/g, " ") };

  return (
    <ModalShell
      title={`Propose Amendment to: ${meta.label} for ${doc.patient_name || "—"}`}
      onClose={onClose}
      maxWidth={620}
    >
      {!fullDoc && !err ? (
        <p style={{ padding: 16, color: BRAND.gray400, fontSize: 13, margin: 0 }}>Loading…</p>
      ) : (
        <>
          <div style={{
            background: "#FFFBEB", border: `1px solid ${BRAND.warning}40`,
            borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: BRAND.gray800,
          }}>
            Amendments require Director of Nursing approval. The original value is preserved until approved.
          </div>

          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Field to amend *
          </label>
          <select
            value={field}
            onChange={(e) => { setField(e.target.value); setNewValue(""); }}
            style={{ ...textInput, cursor: "pointer" }}
          >
            <option value="">— Select a field —</option>
            {fields.map(f => (
              <option key={f} value={f}>{f}</option>
            ))}
          </select>
          {fields.length === 0 && fullDoc && (
            <p style={{ fontSize: 12, color: BRAND.gray400, margin: "8px 0 0" }}>
              No top-level scalar fields available on this document.
            </p>
          )}

          {field && (
            <>
              <div style={{
                marginTop: 14, background: BRAND.gray50, border: `1px solid ${BRAND.gray200}`,
                borderRadius: 8, padding: "10px 12px", fontSize: 12, color: BRAND.gray800,
              }}>
                <strong style={{ color: BRAND.gray600 }}>Current value: </strong>
                <span style={{ wordBreak: "break-word" }}>
                  {currentValue == null || currentValue === "" ? "(empty)" : String(currentValue)}
                </span>
              </div>

              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", letterSpacing: "0.06em", margin: "14px 0 6px" }}>
                Proposed new value *
              </label>
              <textarea
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                placeholder="Enter the proposed new value…"
                style={{ ...textInput, minHeight: 80, resize: "vertical", fontFamily: "DM Sans, sans-serif" }}
              />
            </>
          )}

          <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", letterSpacing: "0.06em", margin: "14px 0 6px" }}>
            Reason for amendment (required) *
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this amendment being proposed?"
            style={{ ...textInput, minHeight: 70, resize: "vertical", fontFamily: "DM Sans, sans-serif" }}
          />

          {err && (
            <div style={{
              marginTop: 12, padding: "10px 12px",
              background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
              color: BRAND.error, fontSize: 13, borderRadius: 8,
            }}>{err}</div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
            <button onClick={onClose} style={btnSecondary} disabled={submitting}>Cancel</button>
            <button
              onClick={handleSubmit}
              style={{ ...btnPrimary, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? "pointer" : "not-allowed" }}
              disabled={!canSubmit}
            >
              {submitting ? "Submitting…" : "Submit for Approval"}
            </button>
          </div>
        </>
      )}
    </ModalShell>
  );
}

// ─── Sprint 5b: Pending Amendments Panel (DON / Admin only) ───────────────────
function PendingAmendmentsPanel({ user, refreshKey, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewTarget, setReviewTarget] = useState(null); // { doc, amendment }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase
      .from("clinical_docs")
      .select("id, doc_type, patient_name, amendments, form_data, voided_at")
      .filter("amendments", "cs", '[{"status":"pending"}]')
      .is("voided_at", null)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setRows(data);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const flat = [];
  rows.forEach(d => {
    (d.amendments || []).forEach(a => {
      if (a && a.type === "amendment" && a.status === "pending") {
        flat.push({ doc: d, amendment: a });
      }
    });
  });
  flat.sort((a, b) => {
    const ta = new Date(a.amendment.created_at || 0).getTime();
    const tb = new Date(b.amendment.created_at || 0).getTime();
    return tb - ta;
  });

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{
        fontSize: 15, fontWeight: 700, color: BRAND.gray800,
        marginBottom: 14, fontFamily: "Comfortaa, sans-serif",
      }}>
        🔄 Pending Amendments
      </h2>

      <div style={{
        background: BRAND.white, border: `1.5px solid ${BRAND.gray200}`,
        borderRadius: 12, overflow: "hidden",
        boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
      }}>
        {loading ? (
          <p style={{ padding: 24, textAlign: "center", color: BRAND.gray400, fontSize: 13, margin: 0 }}>Loading…</p>
        ) : flat.length === 0 ? (
          <p style={{ padding: 24, textAlign: "center", color: BRAND.gray400, fontSize: 13, margin: 0 }}>
            No pending amendments.
          </p>
        ) : (
          <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
            {flat.map(({ doc, amendment }) => {
              const meta = DOC_TYPE_META[doc.doc_type] || { icon: "📄", label: (doc.doc_type || "").replace(/_/g, " ") };
              return (
                <li key={amendment.id} style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 18px", borderBottom: `1px solid ${BRAND.gray200}`,
                }}>
                  <span style={{
                    fontSize: 22, width: 40, height: 40, borderRadius: 10,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    background: `${BRAND.warning}15`, flexShrink: 0,
                  }}>{meta.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: BRAND.gray800 }}>
                      {doc.patient_name || "—"}
                      <span style={{
                        marginLeft: 8, fontSize: 11, fontWeight: 600, color: BRAND.purple,
                        background: `${BRAND.purple}12`, padding: "2px 7px", borderRadius: 6,
                        textTransform: "uppercase", letterSpacing: "0.04em",
                      }}>{meta.label}</span>
                    </p>
                    <p style={{ margin: "4px 0 0", fontSize: 12, color: BRAND.gray400 }}>
                      Proposed by {amendment.author_name || "—"}
                      {amendment.author_credentials ? `, ${amendment.author_credentials}` : ""}
                      {" — "}{relativeTime(amendment.created_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => setReviewTarget({ doc, amendment })}
                    style={{
                      background: `${BRAND.warning}15`, border: `1px solid ${BRAND.warning}50`,
                      color: BRAND.warning, borderRadius: 7, padding: "6px 12px",
                      fontSize: 12, fontWeight: 600, cursor: "pointer",
                      fontFamily: "DM Sans, sans-serif", whiteSpace: "nowrap",
                    }}
                  >Review →</button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {reviewTarget && (
        <ReviewAmendmentModal
          doc={reviewTarget.doc}
          amendment={reviewTarget.amendment}
          user={user}
          onClose={() => setReviewTarget(null)}
          onDecided={() => {
            setReviewTarget(null);
            if (typeof onChanged === "function") onChanged();
          }}
        />
      )}
    </div>
  );
}

// ─── Sprint 5b: Review Amendment Modal (DON / Admin) ──────────────────────────
function ReviewAmendmentModal({ doc, amendment, user, onClose, onDecided }) {
  const [reviewNotes, setReviewNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  const canDecide = reviewNotes.trim().length > 0 && !submitting;

  const decide = async (decision) => {
    if (!canDecide) {
      setErr("Review notes are required.");
      return;
    }
    setSubmitting(true);
    setErr(null);

    if (!reviewNotes || reviewNotes.trim().length < 10) {
      setErr(
        decision === "approve"
          ? "Review notes are required (minimum 10 characters)."
          : "Rejection notes are required (minimum 10 characters)."
      );
      setSubmitting(false);
      return;
    }

    const reauth = await promptPasswordReauth(supabase, user.username);
    if (!reauth.ok) {
      if (reauth.error !== "Password required") setErr(reauth.error);
      setSubmitting(false);
      return;
    }

    const fn = decision === "approve" ? approveAmendment : rejectAmendment;
    const result = await fn(supabase, {
      docId: doc.id,
      amendmentId: amendment.id,
      reviewNotes: reviewNotes.trim(),
      reauthVerifiedAt: reauth.reauthVerifiedAt,
    });

    setSubmitting(false);
    if (!result.ok) {
      setErr(result.error || "Could not save decision.");
      return;
    }
    onDecided();
  };

  const meta = DOC_TYPE_META[doc.doc_type] || { label: (doc.doc_type || "").replace(/_/g, " ") };
  const origVal = amendment.original_value;
  const newVal = amendment.content;

  const sideStyle = {
    background: BRAND.gray50, border: `1px solid ${BRAND.gray200}`,
    borderRadius: 8, padding: "10px 12px",
    fontSize: 13, color: BRAND.gray800,
    minHeight: 80, wordBreak: "break-word", whiteSpace: "pre-wrap",
  };
  const sideLabel = {
    fontSize: 11, fontWeight: 700, color: BRAND.gray600,
    textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
  };

  return (
    <ModalShell
      title={`Review Amendment — ${meta.label} for ${doc.patient_name || "—"}`}
      onClose={onClose}
      maxWidth={720}
    >
      <div style={{ fontSize: 12, color: BRAND.gray600, marginBottom: 6 }}>
        Field: <strong style={{ color: BRAND.gray800 }}>{amendment.field_path || "—"}</strong>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 4 }}>
        <div>
          <div style={sideLabel}>Original value</div>
          <div style={sideStyle}>{origVal == null || origVal === "" ? "(empty)" : String(origVal)}</div>
        </div>
        <div>
          <div style={sideLabel}>Proposed value</div>
          <div style={{ ...sideStyle, background: "#FFFBEB", borderColor: `${BRAND.warning}40` }}>
            {newVal == null || newVal === "" ? "(empty)" : String(newVal)}
          </div>
        </div>
      </div>

      <div style={{
        marginTop: 14, background: BRAND.gray50, border: `1px solid ${BRAND.gray200}`,
        borderRadius: 8, padding: "10px 12px", fontSize: 12, color: BRAND.gray800,
      }}>
        <div><strong>Proposed by:</strong> {amendment.author_name || "—"}{amendment.author_credentials ? `, ${amendment.author_credentials}` : ""}</div>
        <div><strong>When:</strong> {amendment.created_at ? new Date(amendment.created_at).toLocaleString() : "—"}</div>
        <div style={{ marginTop: 6 }}><strong>Reason:</strong> {amendment.reason || "—"}</div>
      </div>

      <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: BRAND.gray600, textTransform: "uppercase", letterSpacing: "0.06em", margin: "14px 0 6px" }}>
        Review notes (required) *
      </label>
      <textarea
        value={reviewNotes}
        onChange={(e) => setReviewNotes(e.target.value)}
        placeholder="Add a note explaining your approval or rejection…"
        style={{ ...textInput, minHeight: 80, resize: "vertical", fontFamily: "DM Sans, sans-serif" }}
      />

      {err && (
        <div style={{
          marginTop: 12, padding: "10px 12px",
          background: "#FEF2F2", border: `1px solid ${BRAND.error}`,
          color: BRAND.error, fontSize: 13, borderRadius: 8,
        }}>{err}</div>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 }}>
        <button onClick={onClose} style={btnSecondary} disabled={submitting}>Cancel</button>
        <button
          onClick={() => decide("reject")}
          disabled={!canDecide}
          style={{
            background: BRAND.white, color: BRAND.error,
            border: `1.5px solid ${BRAND.error}`, borderRadius: 8,
            padding: "9px 18px", fontSize: 13, fontWeight: 600,
            cursor: canDecide ? "pointer" : "not-allowed",
            fontFamily: "Comfortaa, sans-serif",
            opacity: canDecide ? 1 : 0.55,
          }}
        >Reject</button>
        <button
          onClick={() => decide("approve")}
          disabled={!canDecide}
          style={{
            ...btnPrimary,
            opacity: canDecide ? 1 : 0.55,
            cursor: canDecide ? "pointer" : "not-allowed",
          }}
        >Approve &amp; Apply</button>
      </div>
    </ModalShell>
  );
}

// ─── All Completed Notes Panel ────────────────────────────────────────────────
function AllCompletedNotesPanel({ user, refreshKey, onChanged }) {
  const isNarrow = useIsNarrow();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [filterDocType, setFilterDocType] = useState("");
  const [filterClinician, setFilterClinician] = useState("");
  const [filterPatient, setFilterPatient] = useState("");
  const [filterDateRange, setFilterDateRange] = useState("all");
  const [addendumDoc, setAddendumDoc] = useState(null);
  const [amendmentDoc, setAmendmentDoc] = useState(null);
  const [localRefresh, setLocalRefresh] = useState(0);

  // FIX 4 — Role-based visibility: supervisors (DON, Administrator/RN, or admin
  // flag) see all completed notes; other clinicians see only their own.
  // Uses userIsSupervisor() to match the SQL fn_user_is_supervisor() helper.
  const canSeeAllNotes = userIsSupervisor(user);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    let query = supabase
      .from("clinical_docs")
      .select("id, doc_type, patient_name, clinician_name, clinician_id, created_at, status")
      .in("status", ["submitted", "uploaded_to_cv"])
      .is("voided_at", null);
    if (!canSeeAllNotes && user?.id) {
      query = query.eq("clinician_id", user.id);
    }
    query
      .order("created_at", { ascending: false })
      .limit(limit)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data) setDocs(data);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [limit, refreshKey, localRefresh, canSeeAllNotes, user?.id]);

  const refreshAll = () => {
    setLocalRefresh(k => k + 1);
    if (typeof onChanged === "function") onChanged();
  };

  const distinctDocTypes = [...new Set(docs.map(d => d.doc_type).filter(Boolean))].sort();
  const distinctClinicians = [...new Set(docs.map(d => d.clinician_name).filter(Boolean))].sort();

  const now = Date.now();
  const filtered = docs.filter(d => {
    if (filterDocType && d.doc_type !== filterDocType) return false;
    if (filterClinician && d.clinician_name !== filterClinician) return false;
    if (filterPatient && !(d.patient_name || "").toLowerCase().includes(filterPatient.toLowerCase())) return false;
    if (filterDateRange === "7d" && new Date(d.created_at).getTime() < now - 7 * 86400000) return false;
    if (filterDateRange === "30d" && new Date(d.created_at).getTime() < now - 30 * 86400000) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortField] ?? "";
    let bv = b[sortField] ?? "";
    if (sortField === "created_at") {
      av = new Date(av).getTime();
      bv = new Date(bv).getTime();
    } else {
      av = String(av).toLowerCase();
      bv = String(bv).toLowerCase();
    }
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  function toggleSort(field) {
    if (sortField === field) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  }

  function sortArrow(field) {
    if (sortField !== field) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  }

  function statusLabel(s) {
    if (s === "uploaded_to_cv") return "Uploaded to CV";
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : "—";
  }

  async function handleViewPDF(row) {
    const { data } = await supabase
      .from("clinical_docs")
      .select("*")
      .eq("id", row.id)
      .single();
    if (data) generatePDF(data, data.doc_hash || null, user);
  }

  const thBase = {
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 700,
    color: BRAND.gray600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    textAlign: "left",
    borderBottom: `2px solid ${BRAND.gray200}`,
    background: BRAND.gray50,
    whiteSpace: "nowrap",
  };

  const tdStyle = {
    padding: "10px 12px",
    fontSize: 13,
    color: BRAND.gray800,
    borderBottom: `1px solid ${BRAND.gray200}`,
    verticalAlign: "middle",
  };

  const selectStyle = {
    padding: "9px 10px",
    minHeight: 40,
    fontSize: 14,
    border: `1px solid ${BRAND.gray200}`,
    borderRadius: 7,
    background: BRAND.white,
    color: BRAND.gray800,
    fontFamily: "DM Sans, sans-serif",
    cursor: "pointer",
  };

  const columns = [
    { label: "Date", field: "created_at" },
    { label: "Type", field: "doc_type" },
    { label: "Patient", field: "patient_name" },
    { label: "Clinician", field: "clinician_name" },
    { label: "Status", field: "status" },
  ];

  const statusPill = (d) => (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 8,
      background: d.status === "uploaded_to_cv" ? `${BRAND.success}15` : `${BRAND.warning}15`,
      color: d.status === "uploaded_to_cv" ? BRAND.success : BRAND.warning,
    }}>{statusLabel(d.status)}</span>
  );

  const actionBtn = (extra) => ({
    background: BRAND.white, border: `1px solid ${BRAND.gray200}`, color: BRAND.gray600,
    borderRadius: 7, padding: "8px 12px", minHeight: 40, fontSize: 12, fontWeight: 600,
    cursor: "pointer", fontFamily: "DM Sans, sans-serif", whiteSpace: "nowrap", ...extra,
  });
  const renderActions = (d) => (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      <button onClick={() => handleViewPDF(d)} style={actionBtn({ background: `${BRAND.purple}10`, border: `1px solid ${BRAND.purple}30`, color: BRAND.purple })}>👁 View PDF</button>
      <button onClick={() => setAddendumDoc(d)} style={actionBtn({ background: `${BRAND.success}10`, border: `1px solid ${BRAND.success}40`, color: BRAND.success })}>+ Addendum</button>
      <button onClick={() => setAmendmentDoc(d)} style={actionBtn()}>✏ Propose Amendment</button>
    </div>
  );

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{
        fontSize: 15, fontWeight: 700, color: BRAND.gray800,
        marginBottom: 14, fontFamily: "Comfortaa, sans-serif",
      }}>
        📂 All Completed Notes
      </h2>

      {isNarrow && (
        <div style={{
          background: `${BRAND.purple}0D`, border: `1px solid ${BRAND.purple}33`,
          borderRadius: 10, padding: "10px 14px", fontSize: 12.5, color: BRAND.gray800,
          marginBottom: 12, lineHeight: 1.5,
        }}>
          ℹ️ This audit view is best on a larger screen. Every record below is shown
          as a card with all of its details — no columns are hidden.
        </div>
      )}

      <div style={{
        display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 14,
        background: BRAND.white, border: `1.5px solid ${BRAND.gray200}`,
        borderRadius: 12, padding: "14px 16px",
      }}>
        <select value={filterDocType} onChange={e => setFilterDocType(e.target.value)} style={selectStyle}>
          <option value="">All Types</option>
          {distinctDocTypes.map(t => (
            <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
          ))}
        </select>

        <select value={filterClinician} onChange={e => setFilterClinician(e.target.value)} style={selectStyle}>
          <option value="">All Clinicians</option>
          {distinctClinicians.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <input
          type="search"
          placeholder="Search patient…"
          value={filterPatient}
          onChange={e => setFilterPatient(e.target.value)}
          style={{ ...selectStyle, minWidth: 160 }}
        />

        <select value={filterDateRange} onChange={e => setFilterDateRange(e.target.value)} style={selectStyle}>
          <option value="all">All Time</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
        </select>
      </div>

      <div style={{
        background: BRAND.white, border: `1.5px solid ${BRAND.gray200}`,
        borderRadius: 12, overflow: "hidden",
        boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
      }}>
        {loading ? (
          <p style={{ padding: "24px", textAlign: "center", color: BRAND.gray400, fontSize: 13, margin: 0 }}>
            Loading…
          </p>
        ) : sorted.length === 0 ? (
          <p style={{ padding: "24px", textAlign: "center", color: BRAND.gray400, fontSize: 13, margin: 0 }}>
            No completed notes found.
          </p>
        ) : isNarrow ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12 }}>
            {sorted.map(d => (
              <div key={d.id} style={phiCardStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 14, color: BRAND.gray800 }}>{(d.doc_type || "").replace(/_/g, " ")}</span>
                  {statusPill(d)}
                </div>
                <FieldPair label="Date" value={new Date(d.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} />
                <FieldPair label="Patient" value={d.patient_name || "—"} />
                <FieldPair label="Clinician" value={d.clinician_name || "—"} />
                <div style={{ marginTop: 6 }}>{renderActions(d)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  {columns.map(col => (
                    <th
                      key={col.field}
                      style={{ ...thBase, cursor: "pointer", userSelect: "none" }}
                      onClick={() => toggleSort(col.field)}
                    >
                      {col.label}{sortArrow(col.field)}
                    </th>
                  ))}
                  <th style={{ ...thBase, cursor: "default" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(d => (
                  <tr
                    key={d.id}
                    style={{ transition: "background 0.1s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = BRAND.gray50; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                  >
                    <td style={tdStyle}>
                      {new Date(d.created_at).toLocaleDateString("en-US", {
                        month: "short", day: "numeric", year: "numeric",
                      })}
                    </td>
                    <td style={tdStyle}>{(d.doc_type || "").replace(/_/g, " ")}</td>
                    <td style={tdStyle}>{d.patient_name || "—"}</td>
                    <td style={tdStyle}>{d.clinician_name || "—"}</td>
                    <td style={tdStyle}>{statusPill(d)}</td>
                    <td style={tdStyle}>{renderActions(d)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && docs.length >= limit && (
          <div style={{ padding: "14px 16px", borderTop: `1px solid ${BRAND.gray200}`, textAlign: "center" }}>
            <button
              onClick={() => setLimit(l => l + 100)}
              style={{
                background: BRAND.white,
                border: `1.5px solid ${BRAND.purple}`,
                color: BRAND.purple,
                borderRadius: 8,
                padding: "8px 20px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              Load more
            </button>
          </div>
        )}
      </div>

      {addendumDoc && (
        <AddendumModal
          doc={addendumDoc}
          user={user}
          onClose={() => setAddendumDoc(null)}
          onSaved={() => { setAddendumDoc(null); refreshAll(); }}
        />
      )}
      {amendmentDoc && (
        <ProposeAmendmentModal
          doc={amendmentDoc}
          user={user}
          onClose={() => setAmendmentDoc(null)}
          onSaved={() => { setAmendmentDoc(null); refreshAll(); }}
        />
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function ClinicalPortal() {
  const isNarrow = useIsNarrow();
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [screen, setScreen] = useState("dashboard");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [completedDoc, setCompletedDoc] = useState(null);

  // Live patient list + search
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState("");
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [showAddPatient, setShowAddPatient] = useState(false);
  const [toast, setToast] = useState(null);

  // Sprint 2.5 UI state
  const [kpiModal, setKpiModal] = useState(null); // "active" | "visits" | "profile" | "qa"
  const [menuOpen, setMenuOpen] = useState(false);
  const [idleWarnSeconds, setIdleWarnSeconds] = useState(null); // null = not warning

  // Recent visits for the selected patient
  const [patientDocs, setPatientDocs] = useState([]);

  // Draft resume — when the user clicks Resume on a draft (from My Drafts or
  // Recent Visits) we hand the full DB row to the form so it can hydrate state,
  // step, and draftIdRef. dashboardRefreshKey forces panels to re-fetch after
  // a discard or a successful save.
  const [resumeDraft, setResumeDraft] = useState(null);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
  const refreshDashboard = useCallback(() => setDashboardRefreshKey(k => k + 1), []);

  // ── Session bootstrap ──
  // Authentication is gated by AuthGuard (Command Center SSO). If a local
  // session blob is present we use it; otherwise we hydrate from the active
  // Supabase session that AuthGuard has already restored.
  useEffect(() => {
    let cancelled = false;
    // [clinical-diag] Temporary diagnostics — see PR.
    console.log("[clinical-diag] ClinicalPortal: session bootstrap start");
    (async () => {
      try {
        const raw = localStorage.getItem("nnhc_clinical_session");
        console.log("[clinical-diag] ClinicalPortal: localStorage 'nnhc_clinical_session' —", raw ? "present" : "absent");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.expires > Date.now()) {
            console.log("[clinical-diag] ClinicalPortal: restored user from localStorage blob");
            if (!cancelled) {
              setUser(parsed.user);
              setAuthChecked(true);
            }
            return;
          }
          console.log("[clinical-diag] ClinicalPortal: localStorage session blob expired — clearing");
          localStorage.removeItem("nnhc_clinical_session");
        }
      } catch (e) {
        console.warn("[clinical-diag] ClinicalPortal: localStorage session blob unreadable —", e);
        localStorage.removeItem("nnhc_clinical_session");
      }

      console.log("[clinical-diag] ClinicalPortal: calling supabase.auth.getSession()");
      const { data: sessionData } = await supabase.auth.getSession();
      const authUser = sessionData?.session?.user;
      console.log("[clinical-diag] ClinicalPortal: getSession() resolved —", authUser ? `auth user ${authUser.id}` : "no active Supabase session");
      if (!authUser) {
        // NOTE: at this point user stays null forever. ClinicalPortal expects an
        // <AuthGuard> ancestor to have established a session or to render a login
        // form — but in the CRA build (src/index.js) there is no AuthGuard, so
        // the next render shows the permanent "Loading…" screen.
        console.warn("[clinical-diag] ClinicalPortal: no auth user — setAuthChecked(true), user stays null. If there is no AuthGuard wrapper in this build, the app is now stuck on 'Loading…'.");
        if (!cancelled) setAuthChecked(true);
        return;
      }
      const { data: profile } = await supabase
        .from("profiles")
        .select("id, full_name, role, credentials, npi, active")
        .eq("id", authUser.id)
        .maybeSingle();
      const hydrated = {
        id: authUser.id,
        email: authUser.email,
        username: authUser.email,
        full_name: profile?.full_name || authUser.email,
        role: profile?.role || "",
        credentials: profile?.credentials || "",
        npi: profile?.npi || null,
        app_access: { clinical: true },
      };
      const session = {
        user: hydrated,
        expires: Date.now() + 8 * 60 * 60 * 1000,
      };
      try {
        localStorage.setItem("nnhc_clinical_session", JSON.stringify(session));
      } catch {
        // ignore quota / privacy errors
      }
      if (!cancelled) {
        setUser(hydrated);
        setAuthChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // [clinical-diag] Temporary diagnostics — see PR. Fires whenever the auth-gate
  // inputs change so we can see, in the browser console, exactly which branch of
  // the render gate below is active.
  useEffect(() => {
    console.log(
      "[clinical-diag] ClinicalPortal auth-gate state: authChecked =", authChecked,
      "| user =", user ? `{ id: ${user.id}, role: ${JSON.stringify(user.role)} }` : null
    );
    if (authChecked && !user) {
      console.warn("[clinical-diag] ClinicalPortal: rendering the permanent 'Loading…' screen. There is no session and no AuthGuard/login form in this build, so this state will never resolve on its own.");
    }
  }, [authChecked, user]);

  // ── Live patients (debounced search) ──
  const loadPatients = async () => {
    setLoadingPatients(true);
    let q = supabase.from("patients").select("*").eq("status", "active").order("name");
    const s = search.trim();
    if (s) {
      const esc = s.replace(/[%,]/g, "");
      q = q.or(`name.ilike.%${esc}%,policy_number.ilike.%${esc}%`);
    }
    const { data, error } = await q;
    if (!error) setPatients((data || []).map(mapPatient));
    setLoadingPatients(false);
  };

  useEffect(() => {
    if (!user) return;
    const t = setTimeout(loadPatients, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, user]);

  // ── Recent visits for selected patient ──
  useEffect(() => {
    if (!selectedPatient?.id) { setPatientDocs([]); return; }
    let cancelled = false;
    supabase.from("clinical_docs")
      .select("id, doc_type, status, qa_status, created_at")
      .eq("patient_id", selectedPatient.id)
      .neq("status", "voided")
      .order("created_at", { ascending: false })
      .limit(5)
      .then(({ data }) => { if (!cancelled) setPatientDocs(data || []); });
    return () => { cancelled = true; };
  }, [selectedPatient, dashboardRefreshKey]);

  // ── Resume a draft — fetch the full row + patient, hydrate, switch screen ──
  const resumeDraftRow = async (draftLike) => {
    let row = draftLike;
    if (!row?.form_data) {
      const { data, error } = await supabase
        .from("clinical_docs")
        .select("id, doc_type, patient_id, patient_name, patient_dob, patient_mid, form_data, status")
        .eq("id", draftLike.id)
        .single();
      if (error || !data) {
        alert("Could not load draft: " + (error?.message || "not found"));
        return;
      }
      row = data;
    }
    if (row.status && row.status !== "draft") {
      alert("This document is no longer a draft and cannot be resumed.");
      return;
    }
    // Reuse already-loaded patient if it matches; otherwise fetch fresh so the
    // form has diagnoses + full record for the patient banner.
    let patient = selectedPatient && selectedPatient.id === row.patient_id ? selectedPatient : null;
    if (!patient) {
      const { data: p } = await supabase.from("patients").select("*").eq("id", row.patient_id).single();
      if (p) patient = mapPatient(p);
      else patient = {
        id: row.patient_id,
        name: row.patient_name || "—",
        dob: row.patient_dob || "",
        mid: row.patient_mid || "",
        diagnoses: [],
      };
    }
    setSelectedPatient(patient);
    setResumeDraft(row);
    setSelectedForm(row.doc_type);
    setScreen("form");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── View a submitted note's PDF (Recent Visits click) ──
  const viewSubmittedPDF = async (id) => {
    const { data, error } = await supabase
      .from("clinical_docs").select("*").eq("id", id).single();
    if (error || !data) {
      alert("Could not load document: " + (error?.message || "not found"));
      return;
    }
    generatePDF(data, data.doc_hash || null, user);
  };

  // ── Auth handlers ──
  const showToast = (msg, kind = "info") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3800);
  };

  const handleLogout = async () => {
    if (user) {
      await supabase.from("audit_logs").insert({
        user_id: user.id,
        username: user.username,
        action: "CLINICAL_PORTAL_LOGOUT",
        details: {},
        ip_address: "browser",
      });
    }
    await supabase.auth.signOut();
    localStorage.removeItem("nnhc_clinical_session");
    setUser(null);
    setSelectedPatient(null);
    setSelectedForm(null);
    setResumeDraft(null);
    setCompletedDoc(null);
    setScreen("dashboard");
    setIdleWarnSeconds(null);
    setMenuOpen(false);
    setKpiModal(null);
  };

  // ── Idle auto-logout (Fix 9) — HIPAA 164.312(a)(2)(iii) ──
  // idleResetRef exposes a resetIdle function that can be called both from
  // inside the effect (event listeners) and from outside (Stay Signed In).
  const idleResetRef = useRef(null);
  useEffect(() => {
    if (!user) { idleResetRef.current = null; return; }
    let logoutTimer = null;
    let warnTimer = null;
    let countdownTimer = null;
    let countdownLeft = 60;
    let warning = false;

    const stopTimers = () => {
      if (logoutTimer) clearTimeout(logoutTimer);
      if (warnTimer) clearTimeout(warnTimer);
      if (countdownTimer) clearInterval(countdownTimer);
      logoutTimer = warnTimer = countdownTimer = null;
    };

    const forceLogout = async () => {
      stopTimers();
      warning = false;
      try {
        await supabase.from("audit_logs").insert({
          user_id: user.id,
          username: user.username,
          action: "SESSION_TIMEOUT",
          details: { reason: "Idle 15 minutes" },
          ip_address: "browser",
        });
      } catch { /* best-effort */ }
      try { await supabase.auth.signOut(); } catch { /* best-effort */ }
      localStorage.removeItem("nnhc_clinical_session");
      setUser(null);
      setSelectedPatient(null);
      setSelectedForm(null);
      setResumeDraft(null);
      setCompletedDoc(null);
      setScreen("dashboard");
      setIdleWarnSeconds(null);
      // eslint-disable-next-line no-alert
      alert("Session expired due to inactivity. Please sign in again.");
    };

    const resetIdle = () => {
      stopTimers();
      warning = false;
      setIdleWarnSeconds(null);
      warnTimer = setTimeout(() => {
        warning = true;
        countdownLeft = 60;
        setIdleWarnSeconds(countdownLeft);
        countdownTimer = setInterval(() => {
          countdownLeft -= 1;
          setIdleWarnSeconds(countdownLeft > 0 ? countdownLeft : 0);
        }, 1000);
      }, IDLE_WARN_MS);
      logoutTimer = setTimeout(forceLogout, IDLE_TIMEOUT_MS);
    };
    idleResetRef.current = resetIdle;

    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    // Only natural activity (not during warning) resets the timers; during
    // warning the user must click "Stay Signed In" — otherwise moving the
    // mouse to the modal would silently extend the session.
    const onActivity = () => { if (!warning) resetIdle(); };
    events.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    resetIdle();
    return () => {
      events.forEach(e => window.removeEventListener(e, onActivity));
      stopTimers();
      idleResetRef.current = null;
    };
  }, [user]);

  const stayActive = () => {
    if (idleResetRef.current) idleResetRef.current();
    else setIdleWarnSeconds(null);
  };

  const FORM_TYPES = [
    { key: "RN_SUPERVISORY", label: "RN Supervisory Visit", icon: "👁️", freq: "Every 90 days", roles: ["DON", "field_rn"], color: BRAND.purple },
    { key: "SHIFT_NOTE", label: "Nurse's Shift Note", icon: "🗒️", freq: "Per visit", roles: ["DON", "field_rn", "field_lpn"], color: BRAND.purpleDark },
    { key: "PLAN_OF_CARE", label: "Plan of Care", icon: "📋", freq: "Per cert period", roles: ["DON", "field_rn"], color: BRAND.gold },
    { key: "TUBE_FEEDING", label: "Tube Feeding Order Packet", icon: "🍶", freq: "Per order change", roles: ["DON", "field_rn"], color: "#0891B2" },
    { key: "WOUND_CARE", label: "Wound Care Note", icon: "🩹", freq: "Per visit", roles: ["DON", "field_rn", "field_lpn"], color: "#D97706" },
    { key: "SEIZURE_VNS", label: "Seizure / VNS Log", icon: "⚡", freq: "Per event", roles: ["DON", "field_rn", "field_lpn"], color: "#7C3AED" },
    { key: "IV_INFUSION", label: "IV Infusion Record", icon: "💉", freq: "Per infusion", roles: ["DON", "field_rn"], color: "#BE123C" },
    { key: "VENT_TRACH", label: "Vent / Trach Care", icon: "🫁", freq: "Per visit", roles: ["DON", "field_rn"], color: "#065F46" },
    { key: "SKILLED_NURSING", label: "Skilled Nursing Assessment", icon: "🩺", freq: "Initial / PRN", roles: ["DON", "field_rn", "field_lpn"], color: "#92400E" },
    { key: "SUPPLY_LOG", label: "Supply Delivery Log", icon: "📦", freq: "Monthly", roles: ["DON", "field_rn", "field_lpn"], color: "#374151" },
  ];

  // ── Auth gate ──
  // Authentication is handled by AuthGuard (Command Center SSO).
  if (!authChecked) return null;
  if (!user) {
    return (
      <div className="min-h-screen-dvh" style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        background: BRAND.offWhite, fontFamily: "DM Sans, sans-serif", color: BRAND.purpleDark,
        fontSize: 16,
      }}>
        Loading…
      </div>
    );
  }

  // ── Dashboard ──
  if (screen === "dashboard") {
    const userCreds = user?.credentials || user?.role || "";
    return (
      <div className="min-h-screen-dvh" style={{ background: BRAND.offWhite, fontFamily: "DM Sans, sans-serif" }}>
        <PHIWatermark user={user} />
        {/* Header — unified header with hamburger top-left; wraps to two rows on phones */}
        <div style={{
          background: `linear-gradient(135deg, ${BRAND.purpleDark}, ${BRAND.purple})`,
          padding: isNarrow ? "8px 12px" : "0 24px",
          minHeight: 64, height: isNarrow ? "auto" : 64,
          display: "flex", alignItems: "center",
          justifyContent: "space-between", flexWrap: isNarrow ? "wrap" : "nowrap",
          rowGap: 8, columnGap: 12,
          boxShadow: `0 2px 16px ${BRAND.purple}40`,
          position: "relative", zIndex: 2,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: isNarrow ? 10 : 14, minWidth: 0 }}>
            <button
              onClick={() => setMenuOpen(true)}
              aria-label="Open app menu"
              style={{
                background: `${BRAND.white}18`, border: "none", color: BRAND.white,
                borderRadius: 8, width: 44, height: 44, fontSize: 18, cursor: "pointer", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >☰</button>
            <span style={{ fontSize: 24 }}>🩺</span>
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: isNarrow ? 15 : 17, fontWeight: 700, color: BRAND.white, fontFamily: "Comfortaa, sans-serif", whiteSpace: "nowrap" }}>
                NNHC Clinical Portal
              </h1>
              <p style={{ margin: 0, fontSize: 11, color: `${BRAND.white}90`, whiteSpace: "nowrap" }}>clinical.nurturingnurses.net</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: isNarrow ? 8 : 16, marginLeft: "auto" }}>
            <div style={{ textAlign: "right" }}>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: BRAND.white }}>
                {user.name}{userCreds ? `, ${userCreds}` : ""}
              </p>
              <p style={{ margin: 0, fontSize: 11, color: `${BRAND.white}80` }}>
                {user.role} · {new Date().toLocaleDateString()}
              </p>
            </div>
            <button onClick={handleLogout} style={{
              background: `${BRAND.white}20`, color: BRAND.white, border: "none",
              borderRadius: 8, padding: "8px 14px", fontSize: 12, cursor: "pointer", fontFamily: "Comfortaa, sans-serif",
              flexShrink: 0, minHeight: 40,
            }}>Sign Out</button>
          </div>
        </div>

        <div style={{ padding: isNarrow ? "16px 12px" : "24px", maxWidth: 1100, margin: "0 auto" }}>
          {/* Stats — Sprint 2.5: each card opens a detail modal on click */}
          <div style={{ display: "grid", gridTemplateColumns: isNarrow ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: isNarrow ? 10 : 16, marginBottom: isNarrow ? 18 : 28, position: "relative", zIndex: 2 }}>
            {[
              { key: "active",  label: "Active Patients",         value: patients.length,       icon: "👥", color: BRAND.purple },
              { key: "visits",  label: "Selected Patient Visits", value: patientDocs.length,    icon: "📄", color: BRAND.warning },
              { key: "profile", label: "Clinician",               value: userCreds || "—",      icon: "✅", color: BRAND.success },
              { key: "qa",      label: "QA Flags",                value: 0,                     icon: "🔍", color: BRAND.error },
            ].map(s => (
              <button
                key={s.label}
                onClick={() => setKpiModal(s.key)}
                style={{
                  background: BRAND.white, borderRadius: 14, padding: "20px 22px",
                  border: `1.5px solid ${BRAND.gray200}`,
                  boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
                  display: "flex", alignItems: "center", gap: 14,
                  cursor: "pointer", textAlign: "left", fontFamily: "DM Sans, sans-serif",
                  transition: "transform 0.1s, box-shadow 0.15s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 6px rgba(0,0,0,0.05)"; }}
              >
                <div style={{
                  width: 48, height: 48, borderRadius: 12,
                  background: `${s.color}15`, display: "flex", alignItems: "center",
                  justifyContent: "center", fontSize: 22, flexShrink: 0
                }}>{s.icon}</div>
                <div>
                  <p style={{ margin: 0, fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</p>
                  <p style={{ margin: "4px 0 0", fontSize: 12, color: BRAND.gray400 }}>{s.label}</p>
                </div>
              </button>
            ))}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isNarrow ? "1fr" : "1fr 1.6fr", gap: isNarrow ? 20 : 24 }}>
            {/* Patient List */}
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: BRAND.gray800, marginBottom: 12, fontFamily: "Comfortaa, sans-serif" }}>
                My Patients
              </h2>

              <button
                onClick={() => setShowAddPatient(true)}
                style={{
                  width: "100%",
                  background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldLight})`,
                  color: BRAND.white, border: "none", borderRadius: 10,
                  padding: "11px 14px", fontSize: 14, fontWeight: 700,
                  cursor: "pointer", fontFamily: "Comfortaa, sans-serif",
                  letterSpacing: "0.03em", boxShadow: `0 3px 10px ${BRAND.gold}50`,
                  marginBottom: 10,
                }}
              >
                + Add New Patient
              </button>

              <input
                type="search"
                placeholder="Search by name or MID#"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ padding: 10, width: "100%", border: "1px solid #ccc",
                         borderRadius: 8, fontSize: 16, fontFamily: "DM Sans, sans-serif",
                         boxSizing: "border-box", marginBottom: 12 }}
              />

              {loadingPatients && (
                <p style={{ fontSize: 12, color: BRAND.gray400, margin: "4px 0 10px" }}>Loading…</p>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {patients.length === 0 && !loadingPatients && (
                  <div style={{
                    background: BRAND.white, border: `1.5px dashed ${BRAND.gray200}`,
                    borderRadius: 12, padding: "22px 18px", textAlign: "center",
                    color: BRAND.gray600, fontSize: 13, lineHeight: 1.5
                  }}>
                    {search.trim()
                      ? `No patients match "${search.trim()}"`
                      : "No active patients yet. Click + Add New Patient below to get started."}
                  </div>
                )}
                {patients.map(p => {
                  const isSelected = selectedPatient?.id === p.id;
                  return (
                    <div key={p.id}>
                      <div
                        onClick={() => setSelectedPatient(isSelected ? null : p)}
                        style={{
                          background: isSelected ? `${BRAND.purple}08` : BRAND.white,
                          border: `1.5px solid ${isSelected ? BRAND.purple : BRAND.gray200}`,
                          borderRadius: 12, padding: "16px 18px", cursor: "pointer",
                          transition: "all 0.15s", boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div>
                            <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 15, color: BRAND.gray800 }}>{p.name}</p>
                            <p style={{ margin: "0 0 8px", fontSize: 12, color: BRAND.gray400 }}>
                              DOB: {p.dob || "—"} · MID: {p.mid || "—"}
                            </p>
                            {p.diagnoses.length > 0 && (
                              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                {p.diagnoses.map(d => (
                                  <span key={d} style={{
                                    fontSize: 10, fontWeight: 600, padding: "2px 7px",
                                    background: `${BRAND.purple}12`, color: BRAND.purple,
                                    borderRadius: 4, border: `1px solid ${BRAND.purple}30`
                                  }}>{d}</span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {isSelected && (
                        <div style={{
                          marginTop: 8,
                          background: `${BRAND.purple}06`,
                          border: `1px solid ${BRAND.purple}25`,
                          borderRadius: 10, padding: "12px 14px",
                        }}>
                          <p style={{
                            margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: BRAND.purple,
                            textTransform: "uppercase", letterSpacing: "0.08em"
                          }}>Recent Visits</p>
                          {patientDocs.length === 0 ? (
                            <p style={{ margin: 0, fontSize: 12, color: BRAND.gray600 }}>No visits yet</p>
                          ) : (
                            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
                              {patientDocs.map(d => {
                                const dt = new Date(d.created_at);
                                const dateStr = dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                                const typeLabel = (d.doc_type || "").replace(/_/g, " ");
                                const isDraft = d.status === "draft";
                                const isSubmitted = d.status === "submitted" || d.status === "uploaded_to_cv" || d.status === "amended";
                                const clickable = isDraft || isSubmitted;
                                const handleClick = (ev) => {
                                  ev.stopPropagation();
                                  if (isDraft) resumeDraftRow({ id: d.id });
                                  else if (isSubmitted) viewSubmittedPDF(d.id);
                                };
                                return (
                                  <li
                                    key={d.id}
                                    onClick={clickable ? handleClick : undefined}
                                    title={isDraft ? "Resume draft" : isSubmitted ? "View PDF" : undefined}
                                    style={{
                                      fontSize: 12, color: BRAND.gray800, lineHeight: 1.5,
                                      cursor: clickable ? "pointer" : "default",
                                      fontStyle: isDraft ? "italic" : "normal",
                                      padding: "2px 0",
                                      transition: "color 0.1s",
                                    }}
                                    onMouseEnter={ev => { if (clickable) ev.currentTarget.style.color = BRAND.purple; }}
                                    onMouseLeave={ev => { if (clickable) ev.currentTarget.style.color = BRAND.gray800; }}
                                  >
                                    {isSubmitted && <span style={{ marginRight: 4 }}>👁</span>}
                                    {dateStr} · {typeLabel} · {isSubmitted && d.status === "uploaded_to_cv" ? "UPLOADED" : isSubmitted ? "SUBMITTED" : (d.status || "").toUpperCase()}
                                    {isDraft && (
                                      <span style={{ marginLeft: 8, color: BRAND.purple, fontWeight: 600, fontStyle: "normal" }}>
                                        Resume →
                                      </span>
                                    )}
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Form Types */}
            <div>
              <h2 style={{ fontSize: 15, fontWeight: 700, color: BRAND.gray800, marginBottom: 16, fontFamily: "Comfortaa, sans-serif" }}>
                Document Types
              </h2>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {FORM_TYPES.map(f => {
                  const canUse = true; // gated by app_access.clinical at login
                  const available = !f.coming;
                  return (
                    <div key={f.key}
                      onClick={() => {
                        if (!available || !canUse) return;
                        if (!selectedPatient) { alert("Please select a patient first."); return; }
                        setResumeDraft(null);
                        setSelectedForm(f.key);
                        setScreen("form");
                      }}
                      style={{
                        background: BRAND.white,
                        border: `1.5px solid ${available && canUse ? f.color + "40" : BRAND.gray200}`,
                        borderRadius: 12, padding: "14px 16px",
                        cursor: available && canUse ? "pointer" : "not-allowed",
                        opacity: canUse ? 1 : 0.5,
                        transition: "all 0.15s",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
                      }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{
                          fontSize: 20, width: 36, height: 36, borderRadius: 8,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          background: `${f.color}15`
                        }}>{f.icon}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: available ? f.color : BRAND.gray400, lineHeight: 1.2 }}>
                          {f.label}
                        </span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 11, color: BRAND.gray400 }}>{f.freq}</span>
                        {f.coming ? (
                          <span style={{ fontSize: 10, background: BRAND.gray100, color: BRAND.gray400, padding: "2px 7px", borderRadius: 10, fontWeight: 600 }}>Coming Soon</span>
                        ) : (
                          <span style={{ fontSize: 10, background: `${BRAND.success}15`, color: BRAND.success, padding: "2px 7px", borderRadius: 10, fontWeight: 600 }}>Ready</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {(user.role === "Director of Nursing" || user.role === "Administrator") && (
            <PendingAmendmentsPanel
              user={user}
              refreshKey={dashboardRefreshKey}
              onChanged={refreshDashboard}
            />
          )}

          <MyDraftsPanel
            user={user}
            onResume={resumeDraftRow}
            refreshKey={dashboardRefreshKey}
            onChanged={refreshDashboard}
          />

          <AllCompletedNotesPanel
            user={user}
            refreshKey={dashboardRefreshKey}
            onChanged={refreshDashboard}
          />
        </div>

        {showAddPatient && (
          <AddPatientModal
            user={user}
            onClose={() => setShowAddPatient(false)}
            onCreated={(newPatient) => {
              setShowAddPatient(false);
              showToast(`Patient "${newPatient.name}" added.`, "success");
              loadPatients();
            }}
          />
        )}

        {toast && (
          <div style={{
            position: "fixed", bottom: 24, right: 24, zIndex: 1100,
            background: toast.kind === "success" ? BRAND.success : BRAND.gray800,
            color: BRAND.white, padding: "12px 18px", borderRadius: 10,
            fontSize: 13, fontWeight: 600, boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            fontFamily: "DM Sans, sans-serif",
          }}>
            {toast.msg}
          </div>
        )}

        {/* Sprint 2.5 — KPI modals, apps drawer, idle warning */}
        {kpiModal === "active"  && <ActivePatientsModal onClose={() => setKpiModal(null)} />}
        {kpiModal === "visits"  && <PatientVisitsModal patient={selectedPatient} onClose={() => setKpiModal(null)} />}
        {kpiModal === "profile" && <ProfileModal user={user} onLogout={handleLogout} onClose={() => setKpiModal(null)} />}
        {kpiModal === "qa"      && <QAQueueModal onClose={() => setKpiModal(null)} />}
        {menuOpen && <AppsDrawer user={user} onClose={() => setMenuOpen(false)} onLogout={handleLogout} />}
        {idleWarnSeconds != null && (
          <IdleWarningModal
            secondsLeft={idleWarnSeconds}
            onStay={stayActive}
            onLogoutNow={handleLogout}
          />
        )}
      </div>
    );
  }

  // ── Form Screen ──
  if (screen === "form") {
    const formMeta = FORM_TYPES.find(f => f.key === selectedForm);

    // Save & Continue Later — upsert a draft row, toast, return to dashboard.
    // draftIdRef lets a single form session reuse its draft row across saves.
    const saveDraft = async ({ type, data, lastStep, draftIdRef }) => {
      const clinicianCreds = user?.role === "Director of Nursing"
        ? "RN-BSN"
        : (user?.credentials || "RN");
      const formData = { ...(data || {}), lastStep };
      const payload = {
        doc_type: type,
        patient_id: selectedPatient.id,
        patient_name: selectedPatient.name,
        patient_dob: selectedPatient.dob,
        patient_mid: selectedPatient.mid,
        clinician_id: user.id,
        clinician_name: user.name,
        clinician_creds: clinicianCreds,
        clinician_role: user.role,
        form_data: formData,
        rn_signature: null,
        client_signature: null,
        md_signature: null,
        doc_hash: null,
        status: "draft",
      };

      let result;
      if (draftIdRef && draftIdRef.current) {
        result = await supabase
          .from("clinical_docs")
          .update(payload)
          .eq("id", draftIdRef.current)
          .select()
          .single();
      } else {
        result = await supabase
          .from("clinical_docs")
          .insert(payload)
          .select()
          .single();
      }

      if (result.error) {
        alert("Save failed: " + result.error.message);
        console.error("Draft save error:", result.error);
        return;
      }
      if (draftIdRef && !draftIdRef.current && result.data) {
        draftIdRef.current = result.data.id;
      }

      showToast("Draft saved. Resume from your dashboard.", "success");
      setSelectedForm(null);
      setResumeDraft(null);
      refreshDashboard();
      setScreen("dashboard");
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleComplete = async (doc) => {
      // FIX 6 — Build a canonical hash payload from the FULL form state
      // (not a stale/empty reference) and include an explicit timestamp so two
      // identical submissions still produce distinct SHA-256 digests.
      const clinicianCreds = user?.role === "Director of Nursing"
        ? "RN-BSN"
        : (user?.credentials || "RN");

      const hashPayload = {
        doc_type: doc.type,
        patient: {
          id: doc.patient?.id || null,
          name: doc.patient?.name || null,
          dob: doc.patient?.dob || null,
          mid: doc.patient?.mid || doc.patient?.policy_number || null,
        },
        clinician: {
          id: user.id,
          name: user.name,
          creds: clinicianCreds,
          role: user.role,
        },
        form_data: doc.data || {},
        signatures: {
          rn: doc.rnSig || null,
          client: doc.clientSig || null,
          md: doc.mdSig || null,
        },
        timestamp: new Date().toISOString(),
      };
      const realHash = await computeDocHash(hashPayload);

      // If this submission was previously drafted via Save & Continue Later,
      // promote that draft row to submitted instead of inserting a duplicate.
      const draftId = doc.draftIdRef?.current || doc.draftId || null;
      const submitRow = {
        doc_type: doc.type,
        patient_id: doc.patient.id,
        patient_name: doc.patient.name,
        patient_dob: doc.patient.dob,
        patient_mid: doc.patient.mid,
        clinician_id: user.id,
        clinician_name: user.name,
        clinician_creds: clinicianCreds,
        clinician_role: user.role,
        form_data: doc.data,
        // Signature pads emit { dataUrl|url, timestamp|ts, label }.
        // The DB columns are TEXT and expect a base64 PNG data URL —
        // not the wrapper object. Extract the data URL or coerce to null.
        rn_signature: extractSignatureDataUrl(doc.rnSig),
        client_signature: extractSignatureDataUrl(doc.clientSig),
        md_signature: extractSignatureDataUrl(doc.mdSig),
        doc_hash: realHash,
        status: "submitted",
        qa_status: "pending_review",
        locked_at: new Date().toISOString()
      };

      const { data: saved, error } = draftId
        ? await supabase.from("clinical_docs").update(submitRow).eq("id", draftId).select().single()
        : await supabase.from("clinical_docs").insert(submitRow).select().single();

      if (error) {
        // Fix 8 — keep the local draft on network/submit failure so data
        // isn't lost when the clinician clicks Submit again.
        alert(
          "SUBMIT FAILED: " + error.message +
          "\n\nDraft saved locally. Try submitting again when connection returns."
        );
        console.error("Supabase insert error:", error);
        return;
      }

      // Successful submit — clear the local draft.
      if (typeof doc.clearDraft === "function") {
        try { doc.clearDraft(); } catch { /* best-effort */ }
      }

      // Wound photo upload (Fix 4) — store in 'wound-photos' bucket and
      // back-reference the path in form_data.woundPhotoPath.
      let woundPhotoPath = null;
      if (doc.woundPhoto && saved?.id) {
        const file = doc.woundPhoto;
        const ext = file.name.split('.').pop();
        const path = `${saved.id}/${Date.now()}.${ext}`;
        const { error: photoErr } = await supabase.storage
          .from('wound-photos')
          .upload(path, file, { contentType: file.type, upsert: false });
        if (photoErr) {
          console.error('Photo upload failed:', photoErr);
        } else {
          woundPhotoPath = path;
          await supabase.from('clinical_docs')
            .update({ form_data: { ...(doc.data || {}), woundPhotoPath: path } })
            .eq('id', saved.id);
        }
      }

      // Audit log (HIPAA 45 CFR 164.312(b))
      await supabase.from("clinical_audit_log").insert({
        doc_id: saved.id,
        user_id: user.id,
        user_name: user.name,
        action: "DOCUMENT_SUBMITTED",
        device_info: navigator.userAgent,
        changed_fields: { doc_type: doc.type, hash: realHash, status: "submitted" }
      });

      const mergedFormData = woundPhotoPath
        ? { ...(doc.data || {}), woundPhotoPath }
        : doc.data;
      setCompletedDoc({
        ...saved,
        ...doc,
        form_data: mergedFormData,
        data: mergedFormData,
        doc_hash: realHash,
        hash: realHash,
      });
      setResumeDraft(null);
      refreshDashboard();
      setScreen("submitted");
      window.scrollTo({ top: 0, behavior: "smooth" });
    };
    return (
      <div className="min-h-screen-dvh" style={{ background: BRAND.offWhite, fontFamily: "DM Sans, sans-serif" }}>
        <PHIWatermark user={user} />
        {/* Header — unified header with hamburger top-left; wraps on phones */}
        <div style={{
          background: `linear-gradient(135deg, ${BRAND.purpleDark}, ${BRAND.purple})`,
          padding: isNarrow ? "8px 12px" : "0 24px",
          minHeight: 64, height: isNarrow ? "auto" : 64,
          display: "flex", alignItems: "center", gap: isNarrow ? 8 : 16,
          flexWrap: isNarrow ? "wrap" : "nowrap", rowGap: 8,
          position: "relative", zIndex: 2,
        }}>
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Open app menu"
            style={{
              background: `${BRAND.white}18`, border: "none", color: BRAND.white,
              borderRadius: 8, width: 44, height: 44, fontSize: 18, cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >☰</button>
          <button onClick={() => { setResumeDraft(null); setScreen("dashboard"); }} style={{
            background: `${BRAND.white}20`, color: BRAND.white, border: "none",
            borderRadius: 8, padding: "8px 14px", fontSize: 13, cursor: "pointer", flexShrink: 0, minHeight: 40,
          }}>← Back</button>
          <div style={{ minWidth: 0, flex: isNarrow ? "1 1 100%" : "0 1 auto", order: isNarrow ? 3 : 0 }}>
            <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: BRAND.white, fontFamily: "Comfortaa, sans-serif" }}>
              {formMeta?.icon} {formMeta?.label}
            </h1>
            <p style={{ margin: 0, fontSize: 12, color: `${BRAND.white}80` }}>
              {selectedPatient.name} · DOB: {selectedPatient.dob} · MID: {selectedPatient.mid}
            </p>
          </div>
          <div style={{ marginLeft: "auto", textAlign: "right" }}>
            <p style={{ margin: 0, fontSize: 12, color: `${BRAND.white}80` }}>{user?.name}, {user?.credentials}</p>
            <p style={{ margin: 0, fontSize: 11, color: `${BRAND.white}60` }}>{new Date().toLocaleString()}</p>
          </div>
        </div>

        {/* Patient Banner */}
        <div style={{
          background: BRAND.white, borderBottom: `1px solid ${BRAND.gray200}`,
          padding: isNarrow ? "10px 12px" : "10px 24px",
          display: "flex", gap: isNarrow ? 12 : 24,
          alignItems: isNarrow ? "flex-start" : "center", flexWrap: "wrap",
        }}>
          {[
            ["Patient", selectedPatient.name],
            ["DOB", selectedPatient.dob],
            ["Medicaid ID", selectedPatient.mid],
            ["Diagnoses", selectedPatient.diagnoses.join(", ")],
          ].map(([k, v]) => (
            <div key={k}>
              <span style={{ fontSize: 10, fontWeight: 700, color: BRAND.gray400, textTransform: "uppercase" }}>{k}</span>
              <p style={{ margin: "2px 0 0", fontSize: 13, fontWeight: 600, color: BRAND.gray800 }}>{v}</p>
            </div>
          ))}
          <div style={{ marginLeft: isNarrow ? 0 : "auto" }}>
            <span style={{
              background: "#FEF3C7", color: BRAND.warning, fontSize: 11, fontWeight: 700,
              padding: "4px 12px", borderRadius: 20, border: `1px solid ${BRAND.warning}40`,
              display: "inline-block", whiteSpace: "nowrap",
            }}>⚠️ PHI — HIPAA Protected</span>
          </div>
        </div>

        <div style={{ padding: isNarrow ? "12px" : "24px", maxWidth: 860, margin: "0 auto" }}>
          <div className="form-rgrid-scope" style={{
            background: BRAND.white, borderRadius: 16, padding: isNarrow ? "16px" : "32px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: `1px solid ${BRAND.gray200}`
          }}>
            {selectedForm === "RN_SUPERVISORY" && (
              <RNSupervisoryForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} onBack={() => { setResumeDraft(null); setScreen("dashboard"); }} />
            )}
            {selectedForm === "PLAN_OF_CARE" && (
              <PlanOfCareForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} onBack={() => { setResumeDraft(null); setScreen("dashboard"); }} />
            )}
            {selectedForm === "TUBE_FEEDING" && (
              <TubeFeedingForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} onBack={() => { setResumeDraft(null); setScreen("dashboard"); }} />
            )}
            {selectedForm === "WOUND_CARE" && (
              <WoundCareForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} />
            )}
            {selectedForm === "SEIZURE_VNS" && (
              <SeizureVNSForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} />
            )}
            {selectedForm === "IV_INFUSION" && (
              <IVInfusionForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} />
            )}
            {selectedForm === "VENT_TRACH" && (
              <VentTrachForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} />
            )}
            {selectedForm === "SKILLED_NURSING" && (
              <SkillNursingAssessment key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} />
            )}
            {selectedForm === "SHIFT_NOTE" && (
              <NurseShiftNoteForm key={resumeDraft?.id || "new"} initialDraft={resumeDraft} patient={selectedPatient} onComplete={handleComplete} onSaveDraft={saveDraft} showToast={showToast} />
            )}
          </div>
        </div>
        {menuOpen && <AppsDrawer user={user} onClose={() => setMenuOpen(false)} onLogout={handleLogout} />}
        {idleWarnSeconds != null && (
          <IdleWarningModal secondsLeft={idleWarnSeconds} onStay={stayActive} onLogoutNow={handleLogout} />
        )}
      </div>
    );
  }

  // ── Submitted Screen ──
  if (screen === "submitted" && completedDoc) {
    return (
      <div className="min-h-screen-dvh" style={{ background: BRAND.offWhite, fontFamily: "DM Sans, sans-serif" }}>
        <PHIWatermark user={user} />
        <div style={{
          background: `linear-gradient(135deg, ${BRAND.purpleDark}, ${BRAND.purple})`,
          padding: isNarrow ? "8px 12px" : "0 24px",
          minHeight: 64, height: isNarrow ? "auto" : 64,
          display: "flex", alignItems: "center", gap: isNarrow ? 8 : 16,
          position: "relative", zIndex: 2,
        }}>
          <button
            onClick={() => setMenuOpen(true)}
            aria-label="Open app menu"
            style={{
              background: `${BRAND.white}18`, border: "none", color: BRAND.white,
              borderRadius: 8, width: 44, height: 44, fontSize: 18, cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >☰</button>
          <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: BRAND.white, fontFamily: "Comfortaa, sans-serif" }}>
            NNHC Clinical Portal
          </h1>
        </div>
        <div style={{ maxWidth: 640, margin: isNarrow ? "16px auto" : "40px auto", padding: isNarrow ? "0 12px" : "0 24px", position: "relative", zIndex: 2 }}>
          <div style={{ background: BRAND.white, borderRadius: 16, padding: isNarrow ? 16 : 32, boxShadow: "0 2px 16px rgba(0,0,0,0.06)" }}>
            <SubmittedScreen doc={completedDoc}
              user={user}
              onNew={() => { setSelectedForm(null); setScreen("dashboard"); }}
              onDashboard={() => setScreen("dashboard")} />
          </div>
        </div>
        {menuOpen && <AppsDrawer user={user} onClose={() => setMenuOpen(false)} onLogout={handleLogout} />}
        {idleWarnSeconds != null && (
          <IdleWarningModal secondsLeft={idleWarnSeconds} onStay={stayActive} onLogoutNow={handleLogout} />
        )}
      </div>
    );
  }

  return null;
}
